import * as React from "react"
import * as ClientOAuth2 from "client-oauth2";
import * as localforage from "localforage";

import { App } from "./app";
import { TreeViewGW } from "./TreeViewGW";
import { TreeItemGW } from "./TreeItemGW";
import { ArticleViewGW } from "./ArticleViewGW";
import { ArticleItemGW } from "./ArticleItemGW";
import { SelectionViewGW } from "./SelectionViewGW";
import { SelectionItemGW } from "./SelectionItemGW";
import { ArticleGW } from "./ArticleGW";
import { TotalDisplayGW } from "./TotalDisplayGW";
import { QReportTitleGW } from "./QReportTitleGW";
import { ExtInfoViewGW } from "./ExtInfoViewGW";
import { ArticleGroupGW } from "./ArticleGroupGW";
import { ExtDataGW } from "./ExtDataGW";
import { LoadBarGW } from "./LoadBarGW";
import { BuildPartGW } from "./BuildPartGW";
import { SelectionSetManagerGW } from "./SelectionSetManagerGW";
import { ProjectView } from "./ProjectView";
import { ProjectItem } from "./ProjectItem"
import { ProjectSaveView } from "./ProjectSaveView";
import { SelectionImportObject } from "./SelectionImportObject";
import { PriceBook } from "./PriceBook";
import { BuildPartView } from "./BuildPartView";
import { BuildPartSaveView } from "./BuildPartSaveView";
import { SelectionSetGW } from "./SelectionSetGW";
import { BuildPartItem } from "./BuildPartItem";
import { BuildPartQRItem } from "./BuildPartQRItem";
import { QReportTabGW } from "./QReportTabGW";
import { BuildPartSet } from "./BuildPartSet";
import { SummaryView } from "./SummaryView";
import { SearchField } from "./SearchField";
import { SearchSettingsView } from "./SearchSettingsView";
import { PriceSettingsView } from "./PriceSettingsView";
import { NewProjectView } from "./NewProjectView"; 
import { HelpView } from "./HelpView";
import { NobblessGroup } from "./PriceBook";
import { Supplier } from "./PriceBook";
import { LoadingView } from "./LoadingView";
import { FavoritesDeleteView } from "./FavoritesDeleteView";
import { LogView } from "./LogView";
import { SearchCall } from "./SearchCall";

import { Assert } from "./PADebug";
import { PADebug } from "./PADebug";
import { Log } from "./PADebug";

// GetFromUrl - loads a string from an url
function GetFromUrl(url: string) {
    var Httpreq = new XMLHttpRequest(); // a new request
    Httpreq.open("GET", url, false);

    try {
        Httpreq.send(null);
    }

    catch (e) {
        Assert( false, "GetFromUrl() - error: Httprequest failed!  Requested url was: \"" + url + "\"")
    }
    
    return Httpreq.responseText;
}

// LoadFromUrl - also loads a string, though it won't wait for the result but takes an event handler instead
function LoadFromUrl(url: string, onloaded: (s: string) => void, onloadError: (s: string) => void, token?: ClientOAuth2.Token) {
    var Httpreq = new XMLHttpRequest(); // a new request
    Httpreq.open("GET", url, true);
    if (token != null)
        Httpreq.setRequestHeader("Authorization", "Bearer " + token.accessToken);
    Httpreq.setRequestHeader("Content-Type", "application/json");
    Httpreq.addEventListener("load", () => onloaded(Httpreq.responseText));
    Httpreq.addEventListener("error", () => onloadError(Httpreq.responseText));
    Httpreq.send(null);
}

// Debug state:
const debugRenderDisplay = false // If true and in debug mode, some debug info will be shown in the view

// Defaults
const defaultBuildpartsAccessed = false // If true, build parts can be accessed and saved per default for all users, or per default not if this is set to false

const oldExtInfoViewDisplayed = false /* A switch to show the old version of Ext info view where suppliers and package types could be chosen and browsed for more information, this
                                       * switch is made available for debug reasons only, and active use of the older ext info view might cause some undesired effects in the updated system. */

const Max_Shown_Decimal_Amount = 5 // Change this to increase or reduce the maximum amount of decimals visibly describing a value within numerical controls. This won't affect the actual values. 
const spaceBetweenArticles = "14px"

const norwegianMVA: number = 0.25 // this controls how large the MVA percentage should be

export const PAVersion = "0101" // Changing PAVersion here sets the new version of Prisassistenten. It will (in time) trigger updates and it's displayed up at "Prisassistenten"'s title bar.

const decimalSeparatorSign = "," // This character separates a number from its decimal numbers wherever a numerical value is displayed
const thousandSeparatorSign = "." // This character is added between the 0-999 numericals and thousands, millions, billions and trillions wherever a numerical value is displayed

const localTimeDifference = 2 // Norwegian time (+2)

const usingDeviceLocalStorage = true
const deviceLocalDataMaxAgeInSeconds = 2592000  // 86400 equals one day, 3600 is one hour (2592000 here is 30 days), after that time, data will be renewed if it was cashed earlier

const searchDelayBeats = 5 // each beat represents searchDelayLength milliseconds of delay after the user has stopped typing into the search field and until a search will be started.
const searchDelayLength = 10 // one delay beat represents this many milliseconds

// MakeNational takes a numeric string as with no thousand separator signs and an english dot as
// decimal separator, and returns the string using signs specified in BDKalkCore(at the top of the file)
export function MakeNational(numericString: string) {
    if (numericString == null) {
        return "0"
    }

    if (numericString == "") {
        return "0"
    }

    // Locating the decimal separator position if there is one
    var commaPosition = -1
    for (var i = numericString.length - 1; i >= 0; i--) {
        if ((numericString.charAt(i) == ".") || (numericString.charAt(i) == ",")) {
            commaPosition = i
            break
        }
    }

    var passedComma = false
    var potenseNumbers = 0
    var result = ""

    for (var i = numericString.length - 1; i >= 0; i--) {
        // Thousands potense separator is added (if not still dealing with decimals)
        if ((passedComma == true) || (commaPosition == -1)) {
            potenseNumbers++

            if (potenseNumbers == 4) {
                potenseNumbers = 1
                result = thousandSeparatorSign + result
            }
        }

        // Decimal separator sign is added
        if ((numericString.charAt(i) == ".") || (numericString.charAt(i) == ",")) {
            passedComma = true
            result = decimalSeparatorSign + result
        }
        else {
            result = numericString.charAt(i) + result
        }
    }

    return result
}

/*  << BDKalkCore >>
     Within this class there is access to all the controls of the application, as well as all the
     data required for calculations. This is where the values are calculated before they are
     displayed in the various controls, and also where communication with the back end takes place. */

/**
    ------------------   BDKalkCore methods overview:   ------------------------------------

    void ConnectQReportTitle(qrt: QReportTitleGW)                                           // Initial referencing to the titlebar of Qreport
    void ConnectTotalDisplay(display: TotalDisplayGW)                                       // Initial referencing to the titlebar of the BDKalk page
    void LoadFullDataFile()                                                                 // LoadFullDataFile loads the main data file into the client
    void HandleSearchInput(input: string, searchedData: Array<ArticleGW>)                   // ..is called whenever the user types enter while the search field has focus.

    void InactivateInputs()                                                                 // This is called when the user leaves a 'page' of displayed articles, it resets any active amount inputs
    Array<ArticleGW> SearchWithin(scoop: Array<ArticleGW>, word:string)                     // searches for a one word string within an amount of articles and returns an array of the matches.
    boolean Match(article: ArticleGW, textCheck: string)                                    // Match determines if a string can be found within an article.
    void AddParentGroup(title:string, identifier:string)                                    // This is a help method used within GenerateGroups()
    void GenerateGroups()                                                                   // Processing the data recieved by LoadFullDataFile() and makes it available through various article groups

    <node> RenderAlphabeticalTree(tree:TreeViewGW, itemWidth:string, itemHeight:string)     // Renders an alphabetical tree of nobb groups for the tree view
    <node> RenderHierarchyTree(tree: TreeViewGW, itemWidth: string, itemHeight: string)     // Renders a hierarchial nobb groups tree for the tree view
    void SetFocusOnItemInput()                                                              // Locates an input component with ID:"currentInput" within the page and gives it focus
    void HandleButton(buttonID: string)                                                     // General handler for a variety of button events, identified by a provided string
    void HandleOriButton(button: OrientationButton)                                         // Handler for orientation within a search/group selection result, among pages of matches

    void RedrawTotalSum()                                                                   // Specifically updates the displayed total sum of articles prizes
    void HandleItemClick(article: ArticleItemGW, clickPos: string)                          // HandleItemClick is the general handler for all clicks within an article Item (phase one)
    void HandleItemClickPhaseTwo()                                                          // Phase two of HandleItemClick (a delayed phase is performed because some actions will kill their own thread)
    void HandleQRItemClick(article: SelectionItemGW, clickPos: string)                      // HandleQRItemClick is the general handler for all clicks within a selection Item (phase one)
    void HandleQRItemClickPhaseTwo()                                                        // Phase two of HandleQRItemClick (a delayed phase is performed because some actions will kill their own thread)

    void HandleKeyArticleItem(key: string, article: ArticleItemGW)                          // Handler for keyboard inputs while an article is selected
    void RunKeyEffect()                                                                     // A slightly delayed sub method for the (above) HandleKeyArticleItem method
    App GetApp()                                                                            // Access to the app object
    string GetNordic(text: string)                                                          // Performs a translation from certain character combines within a string into nordic letters
    boolean InsertSelectArticle(newArticle: ArticleGW)                                      // InsertSelectArticle adds an article into the list of chosen articles, and returns true if a change was made

    boolean RemoveSelectArticle(articleID: string)                                          // RemoveSelectArticle removes an article from the list of chosen articles, and returns true if a change was made
    void ExpandGroupInTreeView(group: string)                                               // Updates the tree view with a new group ID to determine what items should be expanded
    boolean SelectQreportArticle(newArticle: ArticleGW, newItem: SelectionItemGW)           // SelectQreportArticle selects what selection item should be highlighted, returns true if a change was made
    boolean SelectDisplayArticle(newArticle: ArticleGW, newItem: ArticleItemGW)             // SelectDisplayArticle selects what article item should be highlighted, returns true if a change was made
    boolean SelectedFind(articleID: string)                                                 // SelectedFind returns true if an article with the given ID can be found within the list of chosen articles

    ArticleGW GetSelectedArticle(articleID: string)                                         // GetSelectedArticle returns an article if it exists within the list of chosen articles, or null if it doesn't
    ArticleGW GetFavorite(articleID: string)                                                // GetSelectedArticle returns an article if it exists within the list of favorites, or null if it doesn't
    ArticleGW GetDisplayedArticle(articleID: string)                                        // GetSelectedArticle returns an article if it is currently shown within the articles view, or null if it isn't
    TreeViewGW GetTreeView()                                                                // Accessing the currently active treeview object (this reference will be updated on each render)
    ArticleViewGW GetArticleView()                                                          // Accessing the currently active article view object (this reference will be updated on each render)

    SelectionViewGW GetSelectionView                                                        // Accessing the currently active selection view object (this reference will be updated on each render)
    boolean CheckForSelectionContents(groupID: string, hierarchyLevel: number)              // Returns true if a given nobb group is represented among the selected items corresponding groups
    void DisplayFavorites()                                                                 // All items within the list of favorites are shown within the articles view
    ConnectApp(app: App)                                                                    // Initial referencing to the app object (entire page)
    ConnectTreeView(treeView: TreeViewGW)                                                   // Initial referencing to the tree view (left hand side)

    ConnectArticleView(ArticleView: ArticleViewGW)                                          // Initial referencing to the articles view (right hand side)
    ConnectSelectionView(SelectionView: SelectionViewGW)                                    // Initial referencing to the selection view (right hand side when quick report is active)
    Array<ArticleGW> GetArticlesByUrl(url: string)                                          // GetArticlesByUrl reads in a set of articles to be shown within the articles view
    Array<ArticleGW> GetArticlesByGroup(nobbGroup: string)                                  // Access to an array of articles limited by a given nobbGroup string
    <node> RenderFavorites()                                                                // Reads the list of favorites and returns it as a renderable object

    <node> RenderArticles(art: Array<ArticleGW>)                                            // Reads the list of currently displayed articles and returns it as a renderable object
    <node> RenderQreport(art: Array<ArticleGW>)                                             // Reads the list of chosen articles and returns it as a renderable object (for the quick report)
    <node> RenderArticle(art: ArticleGW)                                                    // Reads a single article and converts it into a renderable object for the articles display view
    <node> RenderSelection(art: ArticleGW)                                                  // Reads a single article and converts it into a renderable object for the selection (qreport) view
    Array<ArticleGW> GetDisplayedArticles()                                                 // Accessing the displayed articles

    Array<ArticleGW> GetSelectedArticles()                                                  // Accessing the articles which can be found in the selection view
    number GetFavoritesAmount()                                                             // Accessing the amount of articles marked as favorites
    boolean FavoritesFind(checkArticleID: string)                                           // Returns true if articleID is represented within the favorites
    boolean FavoritesInsert(newFavorite: ArticleGW)                                         // Inserts a copy of the chosen article into the favorites list, returns true if a change was made
    boolean FavoritesRemove(articleID: string)                                              // FavoritesRemove removes an article from the favorites list and returns true if an article was removed

    number TotalSum()                                                                       // Returns the total prize for all of the chosen articles
    number GetArticlePrize(article: ArticleGW)                                              // Here the prize of an article is calculated as it should be displayed on various items and affect the total sum
    ArticleItemGW GetInputItemArt()                                                         // Accessing what article is being typed an amount into within the Article view
    ArticleItemGW GetInputItemSel()                                                         // Accessing what article is being typed an amount into within the Selection view
    void SetInputItemArt(item: ArticleItemGW)                                               // Setting what article is being typed an amount into within the Article view

    void SetInputItemSel(item: SelectionItemGW)                                             // Setting what article is being typed an amount into within the Selection view
    void SetFocusOnSearchInput()                                                            // Causing the search input to gain focus
    void EndOldSelection()                                                                  // This will trigger some delayed methods that will properly clean up a previous selection.
    void FinishOngoingInput()                                                               // This will interrupt any ongoing inputs and also complete them if they are valid new amounts
    boolean isInitiated()                                                                   // Returns true if data has been loaded properly, otherwise makes sure it will be loaded soon - while displaying a waiting screen.

    void init()                                                                             // Init is called with a delay when isInitiated has returned false (above)
    void ConnectSelectedArticleItem(selectedItem: ArticleItemGW)                            // Help method for changing the selected item with the use of arrow keys
    <node> RenderToolbarButton( imageNr:number, buttonID: string, tooltipText: string )     // Renders and returns a toolbar button based on the given parameters
    string GetLoggedInName()                                                                // Returns the name of who is currently logged in
    string GetLoggedInCompanyName()                                                         // Returns the company name of who is currently logged in

    void LogOut()                                                                           // Sets the logged in person to nobody
    void LogIn(name: String, company: String)                                               // Sets the logged in person to the given name and company name (will be displayed up to the right of the toolbar buttons)
    ExtDataGW GetExtData()                                                                  // Returns the extended info data set for the currently selected article (while cashing the currently watched data & displayed product images)
    ArticleItemGW GetSelectedItem()                                                         // Returns the currently selected article's component
    HandleKeyDown(key: string)                                                              // Handler for general keydown events on the entire page (App)

    HandleKeyUp(key: string)                                                                // Handler for general keyup events on the entire page (App)
    RenderDebugDisplay()                                                                    // RenderDebugDisplay renders some stats below the tree view for debug uses
    Assert(condition: boolean, errorDescriptionAndLocation: string)                         // Debug assertion method, use it to ensure whatever needs to be ensured, and get an alert otherwise if in debug mode
    IsDebuging()                                                                            // Returns true if debug mode is on
    GetExtInfoView()                                                                        // Returns a handle to the current ExtInfoView

    DemandGroupDisplayed()                                                                  // Sets a certain unloaded group into priority until it is loaded, when it is, its contents will become available and the views will update
    FinalizeGroups()                                                                        // When all article cathegories has been loaded and everything is ready to use, this function will be run to complete the job and to inform the user
    UpdateCathegories()                                                                     // Whenever a group of articles has been loaded, this function will be called to update the affected views with the new loaded data
    isGroupLoaded(nobbGroup: string)                                                        // Returns true if a specified nobb groups is loaded and ready to use
    InitLoadBarHandle(handle: LoadBarGW)                                                    // Initiates the load bar for visible updates of the percentage

    SetLoadBarMounted(monted: boolean)                                                      // Sets the flag telling if the load bar for search field is currently mounted and can recieve update requests (without warnings)
    GetContentStringForPrislister()                                                         // Returns the loaded contents for the prislister dropdown menu or a replacement if not yet loaded (will trigger the loading of it)
    ClearAllQRArticles()                                                                    // Clears the quick report list of selected articles and returns all amounts to zero for those articles
    GetHighestPrizebookIndex()                                                              // Returns the highest possible index of the prizebooks applied into the system
*/

export class BDKalkCore {

    constructor(serverUrl: string, idServerUrl: string) {
        this.m_serverUrl = serverUrl
        this.m_idServerUrl = idServerUrl
        this.m_selectedArticles = new Array<ArticleGW>()
        this.SetDisplayedArticles(new Array<ArticleGW>())
        this.m_favorites = new Array<ArticleGW>()
        this.m_lastSelectedNobbGroup = ""
        this.m_functionalKeyPressed = ""
        this.m_arrowKeyInputFlag = ""
        this.m_selectionRollIn = 0
        this.m_loadingStatus = {
            beatStarted: false,
            loadInProgress: false,
            priceBookGenerationInProgress: false,
            priceBookGenerationAtIndex: -1
        }
        this.m_loadBarIsMounted = false
        this.m_qreportInsertionQueue = new Array<ArticleGW>()
        this.m_qreportInsertionPulseOn = false
        this.m_qreportInsertionPreviousIncomplete = false
        this.m_selectionSetManager = new SelectionSetManagerGW(this.m_serverUrl)
        this.m_selectionSetManager.InitCore(this)
        this.m_userIsWarnedAboutSupplierPackageChangeEffects = false
        this.m_selectedPriceBookID = null // Default
        this.m_priceBooks = new Array<PriceBook>()
        this.m_initStage = 0
        this.m_favoritesUpdateStage = 0
        this.m_buildParts = new Array<BuildPartGW>()
        this.m_projectSummaries = new Array<BuildPartGW>()
        this.m_selectedBuildParts = new Array<BuildPartGW>()
        this.m_buildPartLoadingSets = null
        this.m_projectLoadingSets = null
        this.m_inputItemBuildPartQRView = null
        this.m_userRole = 0
        this.m_inputUnitExtInfo = -1
        this.m_searchInFocus = false

        this.m_userBuildPartOn = true
        this.m_indexForUniqueKeys = 0
        this.m_printableArticleSource = null
        this.m_printableArticlesReady = false
        this.m_printableArticlesLoadIndex = 0
        this.m_searchField = null
        this.m_searchSettingsView = null

        this.m_countOfLoadingBeats = 0
        this.m_countOfGroupLoadInits = 0
        this.m_countOfGroupLoadCompletes = 0
        this.m_countOfPriceBookInitiations = 0
        this.m_countOfPriceBookGenerations = 0

        this.m_countOfFinishedHierarchialTopGroups = 0
        this.m_countOfFinishedHierarchialMidGroups = 0
        this.m_countOfFinishedHierarchialLowGroups = 0

        this.m_filterFlags = (1 | 2 | 4 | 8 | 16 | 32 | 64) // default - all checks are set

        this.m_priceFlags = (4) // default
        this.m_priceRadioPos = 2 // default

        this.m_groupBeingLoadedIsFromPricebookID = -11 // (unused value which means undefined)

        this.m_allPriceBooksHierarchialGroups = undefined
        this.m_allPriceBooksAlphabeticalGroups = undefined
        this.m_allPriceBooksNobblessGroups = undefined
        this.m_allPriceBookHierarchyNeedRefresh = true
        this.m_allPriceBookHierarchyIsValid = false

        this.m_hasCalledForLogin == false

        this.m_beatStage = 0


        this.m_priceSettingsView = null
        this.m_newProjectView = null
        this.m_loadingView = null
        this.m_logView = null
        this.m_helpView = null
        this.m_favoritesDeleteView = null
        this.m_allPriceBooksFullData = new Array<ArticleGW>()

        this.m_defaultPriceBook = undefined

        this.m_nobbUnspecifiedArticlesFromAllPriceBooks = new Array<ArticleGW>()
        this.m_summaryModeRadioIndex = 0

        this.m_countOfProjectLoadCalls = 0
        this.m_countOfBuildPartLoadCalls = 0
        this.m_countOfSelectionSetSaveCalls = 0
        this.m_countOfRequestedExtDataForLoadedArticles = 0

        this.m_whatIsBeingLoaded = "no task"

        this.m_selectedArticleQueuedToReciveExtData = null
        this.m_articleIsQueuedToReciveExtData = false
        this.m_favoritesDeletionArticle = null

        this.m_willAskDeleteFavoritesQuestion = true
        this.m_locallyStoredGroupData = ""
        this.m_locallyStoredGroupDataIsUsed = false
        this.m_loadingGroupUrl = ""
        this.m_loadingGroupDate = ""

        this.m_defaultExtDataForArticlesMissingIt = new ExtDataGW()
        this.m_defaultExtDataForArticlesMissingIt.SetIsDefault(true)
        this.m_handlerForAccountReload = null
        this.m_lastWrittenInputArticleID = null

        this.m_latestSearchCall = 0
        this.m_searchCallChecks = 0

        this.m_activeSearches = new Array<SearchCall>()
    }

    // Returns true if data has been loaded properly and is ready to use, otherwise makes sure it will be loaded soon,
    // while still returning false before that, so that app can show its waiting screen in the meanwhile.
    isInitiated() {
        if (this.m_initStage == 0) {
            this.m_initStage = 1
            setTimeout(this.InitiatePriceBooks.bind(this), 10)
            return false
        }
        else {
            if (this.GetInitStage() > 0) {
                return true
            }

            return false
        }
    }

    // Prepares the pricebooks in use for this session
    public InitiatePriceBooks() {
        LoadFromUrl(this.m_serverUrl + "api/price/", this.OnLoadPriceBooks.bind(this), this.DefaultLoadErrorHandler.bind(this), this.GetApp().state.accessToken)
    }

    // Prepares the pricebooks in use for this session
    public OnLoadPriceBooks(data: string) {
        var obj = JSON.parse(data)
        var priceBookArray = new Array<PriceBook>()

        for (var i = 0; i < obj.length; i++) {
            const pb: PriceBook = new PriceBook()
            pb.SetTitle(obj[i].description)
            pb.SetPriceBookId(obj[i].id)
            priceBookArray.push(pb)
        }

        priceBookArray.sort(function (a, b) {
            var x = a.GetTitle().toLowerCase();
            var y = b.GetTitle().toLowerCase();
            if (x < y) { return -1; }
            if (x > y) { return 1; }
            return 0;
        });

        this.m_priceBooks = priceBookArray

        if (priceBookArray.length > 0) {
            const defaultPBID = this.GetSelectedPriceBookID()
        }
        else {
            Assert(false, "core.OnLoadPriceBooks - no pricebooks were added!")
        }

        this.m_app.forceUpdate()
        this.LoadAccountData(null)
    }

    // DebugAction is performed when the debud action button is clicked (only visible when the program is in debug mode) and
    // can contain just anything to be tried out during development
    DebugAction() {
        scroll(0, 0)
        // this.m_app.forceUpdate()
        // this.DebugOutput("called from debug action")
        // this.m_app.GoToLoginPage()
        // alert("DebugAction - utvalg.length:" + this.m_selectedArticles.length)

        /*
        this.StopLoadingBeat()
        localforage.clear()
        */
    }

    // DebugOutput works like the Debug method, but adds a bit of useful system information to the message
    public DebugOutput(text: string) {
        if (PADebug.IsDebuging() == false) {
            return // This alert should only take place when in debug mode
        }

        var sysInfo = "\nAPP:\n"
        var appDisplayMode = "(unknown)"
        var dm = this.m_app.DMode

        switch (this.m_app.state.DisplayMode) {
            case dm.byggdelLoad: appDisplayMode = "byggdelLoad"; break;
            case dm.byggdelSave: appDisplayMode = "byggdelSave"; break;
            case dm.Favorites: appDisplayMode = "Favorites"; break;
            case dm.FavoritesDelete: appDisplayMode = "FavoritesDelete"; break;
            case dm.help: appDisplayMode = "help"; break;
            case dm.Init: appDisplayMode = "Init"; break;
            case dm.Loading: appDisplayMode = "Loading"; break;
            case dm.newProject: appDisplayMode = "newProject"; break;
            case dm.NobbGroup: appDisplayMode = "NobbGroup"; break;
            case dm.priceSettings: appDisplayMode = "priceSettings"; break;
            case dm.projectLoad: appDisplayMode = "projectLoad"; break;
            case dm.projectSave: appDisplayMode = "projectSave"; break;
            case dm.QReport: appDisplayMode = "QReport"; break;
            case dm.renderOrders: appDisplayMode = "renderOrders"; break;
            case dm.SearchResult: appDisplayMode = "SearchResult"; break;
            case dm.searchSettings: appDisplayMode = "searchSettings"; break;
            case dm.Url: appDisplayMode = "Url"; break;
            default:
        }

        sysInfo += "\nwindow.devicePixelRatio : " + window.devicePixelRatio.toString()
        sysInfo += "\nwindow.innerHeight : " + window.innerHeight.toString()
        sysInfo += "\nwindow.innerWidth : " + window.innerWidth.toString()
        sysInfo += "\nwindow.screenX : " + window.screenX.toString()
        sysInfo += "\nwindow.screenY : " + window.screenY.toString()
        sysInfo += "\nwindow.scrollX : " + window.scrollX.toString()
        sysInfo += "\nwindow.scrollY : " + window.scrollY.toString()
        sysInfo += "\nwindow.name : " + window.name

        sysInfo += "\nDisplayMode : " + appDisplayMode + " \n"

        sysInfo += "\nLOADING BEATS:\n"

        if (this.m_loadingStatus.beatStarted == true) {
            sysInfo += "beatStarted : true \n"
        }
        else {
            sysInfo += "beatStarted : false \n"
        }

        sysInfo += "loading beats called : " + this.m_countOfLoadingBeats.toString() + "\n"

        sysInfo += "\nSELECTION SETS: \n"
        sysInfo += "project calls :" + this.m_countOfProjectLoadCalls + "\n"
        sysInfo += "build part calls :" + this.m_countOfBuildPartLoadCalls + "\n"
        sysInfo += "set save calls :" + this.m_countOfSelectionSetSaveCalls + "\n"

        sysInfo += "\nPRICE BOOKS: \n"
        if (this.m_priceBooks != null) {
            sysInfo += "priceBooks.length : " + this.m_priceBooks.length.toString() + "\n"

            if (this.m_selectedPriceBookID != null) {
                sysInfo += "m_selectedPriceBookID : " + this.m_selectedPriceBookID.toString() + "\n"
            }
            else {
                sysInfo += "m_selectedPriceBookID : null\n"
            }

            const noundef = this.m_defaultPriceBook
            if (noundef != undefined) {
                sysInfo += "m_defaultPriceBook : " + noundef.toString() + "\n"
            }
            else {
                sysInfo += "m_defaultPriceBook : null\n"
            }

            sysInfo += "Pricebook initiations begun : " + this.m_countOfPriceBookInitiations.toString() + "\n"

            if (this.m_loadingStatus.priceBookGenerationInProgress == true) {
                sysInfo += "priceBookGenerationInProgress : true (at index:" + this.m_loadingStatus.priceBookGenerationAtIndex.toString() + ")\n"
            }
            else {
                sysInfo += "priceBookGenerationInProgress : false (at index:" + this.m_loadingStatus.priceBookGenerationAtIndex.toString() + ")\n"
            }

            for (var i = 0; i < this.m_priceBooks.length; i++) {
                const ii = i

                var hier = "null"

                if (this.m_priceBooks[i].GetHierarchialGroups() != null) {
                    hier = this.m_priceBooks[i].GetHierarchialGroups().length.toString()
                }

                var alph = "null"

                if (this.m_priceBooks[i].GetAlphabeticalGroups(false) != null) {
                    var loadeds = 0
                    for (var j = 0; j < this.m_priceBooks[i].GetAlphabeticalGroups(false).length; j++) {
                        if (this.m_priceBooks[i].GetAlphabeticalGroups(false)[j].GetLoadedStatus().status == 2) {
                            loadeds++
                        }
                    }

                    alph = " has loaded: " + loadeds.toString() + " of " + this.m_priceBooks[i].GetAlphabeticalGroups(false).length.toString() + " groups."

                }

                var nobbless = "null"

                if (this.m_priceBooks[i].GetNobbUnspecifiedGroups(false) != null) {
                    var loadeds = 0
                    for (var j = 0; j < this.m_priceBooks[i].GetNobbUnspecifiedGroups(false).length; j++) {
                        if (this.m_priceBooks[i].GetNobbUnspecifiedGroups(false)[j].GetLoadedStatus().status == 2) {
                            loadeds++
                        }
                    }

                    nobbless = " has loaded: " + loadeds.toString() + " of " + this.m_priceBooks[i].GetNobbUnspecifiedGroups(false).length.toString() + " groups."

                }

                var title = this.m_priceBooks[i].GetTitle()

                sysInfo += "\npriceBooks[" + ii + "] : " + title + "\n - initStage: " + this.m_priceBooks[i].GetInitStage().toString() + "\n - hierarchialGroups.length : " + hier + "\n - alphabeticalGroups: " + alph + "\n - nobbUnspecifiedGroups: " + nobbless + "\n - pricebook ID : " + this.m_priceBooks[i].GetPriceBookId().toString() + "\n"
            }
        }

        sysInfo += "\nNOBB GROUPS:\n"
        sysInfo += "Top level hierarchial groups completed : " + this.m_countOfFinishedHierarchialTopGroups.toString() + "\n"
        sysInfo += "Mid level hierarchial groups completed : " + this.m_countOfFinishedHierarchialMidGroups.toString() + "\n"
        sysInfo += "Groups loaded - called:" + this.m_countOfGroupLoadInits.toString() + ", completed:" + this.m_countOfGroupLoadCompletes.toString() + "\n"

        if (this.m_loadingStatus.loadInProgress == true) {
            sysInfo += "loadInProgress : true \n"
        }
        else {
            sysInfo += "loadInProgress : false \n"
        }

        if (this.m_groupBeingLoaded != null) {
            sysInfo += "a group is being loaded:\n - ID:" + this.m_groupBeingLoaded.GetGroupID() + ",\n - description:\"" + this.m_groupBeingLoaded.GetDescription() + "\",\n - pricebookID:" + this.m_groupBeingLoadedIsFromPricebookID + "  \n"
            sysInfo += " .. the loaded group was recently defined for load bar info as: " + this.m_dataLoadGroup + "\n"
        }

        sysInfo += "ExtDataPostCompletions : " + this.m_countOfRequestedExtDataForLoadedArticles + "\n"

        alert("Debug output:\n \"" + text + "\" \n" + sysInfo)
    }

    // 
    public DefaultLoadErrorHandler() {
        // alert("Det ser ut som om kontakten med servern er inaktuell, vennligst logg in på nytt!")
        Assert(false, "core.DefaultLoadErrorHandler - a load was unsuccessful! Turning to login page...")
        alert("Feil: Kontakten med server er i ustand, vennligst logg deg inn på nytt..")
        this.m_app.LogIn()
    }

    // LoadAccountData() is called both after OnLoadPriceBooks (above) but also in SelectionSetManagerGW.ReloadAccountData() to refresh the stored selections
    public LoadAccountData(onLoaded: (() => void) | null) {
        if (onLoaded != null) {
            this.m_handlerForAccountReload = onLoaded
        }

        LoadFromUrl(this.m_serverUrl + "api/selection", this.OnAccountDataLoaded.bind(this), this.DefaultLoadErrorHandler.bind(this), this.GetApp().state.accessToken)
    }

    // Data loaded handler
    public OnAccountDataLoaded(data: string) {
        this.m_selectionSetManager.OnAccountDataLoaded(data)

        if (this.m_handlerForAccountReload != null) {
            const handler = this.m_handlerForAccountReload
            this.m_handlerForAccountReload = null
            handler()
        }

        this.InitLoadingBeat()
    }


    // InitiatePriceBookAtIndex initiates the price book found at a certain index in the array (m_priceBooks) and blocks
    // other pricebook initiations or article loads until the pricebook has been initiated and generated. Don't mix this up
    // with if the pricebook's contents are fully cashed or not, it's just the article groups which are loaded here, all with
    // the status 0 (not loaded but can be loaded). When all pricebooks are initiated, a nobb hierarchy of all groups within
    // all pricebooks can be generated.
    InitiatePriceBookAtIndex(index: number) {

        this.m_loadingStatus.priceBookGenerationAtIndex = index
        this.m_loadingStatus.priceBookGenerationInProgress = true
        this.m_countOfPriceBookInitiations++

        this.m_priceBooks[index].InitFullData()
        // Recieving info about the article groups from the server
        try {
            LoadFromUrl(this.m_serverUrl + "api/price/" + this.m_priceBooks[index].GetPriceBookId().toString(), this.GeneratePriceBookGroups.bind(this), this.DefaultLoadErrorHandler.bind(this), this.GetApp().state.accessToken)
        }

        catch (e) {
            alert("Error - downloading nobb groups from the server has failed - please check your internet connection!")
        }
    }

    // This prepares the groups and starts the loading beat
    GeneratePriceBookGroups(groupDataResponse: string) {

        // logging the pricebook contents
        Log("- Incoming pricebook data:")
        PADebug.LogJson(groupDataResponse, "#4400AA")

        this.m_countOfPriceBookGenerations++
        var load: any

        // Parsing info about the article groups from the server
        try {
            load = JSON.parse(groupDataResponse)
        }

        catch (e) {
            Assert(false, "core.GenerateGroups - parsing from server given data has failed! Unparsable data dump: " + groupDataResponse)
            return
        }

        var groups: Object[] = load.nobbGroups // These groups are at top level (same as L1 in BDKalk)
        var mainGr: Object[] // Main groups are cathegories at the mid level (same as L2 in BDKalk)
        var itemGr: Object[] // Item groups are cathegories at the lowest level, right above the actual articles (same as L3 in BDKalk)

        var nobbless: Array<NobblessGroup> = new Array<NobblessGroup>()

        /* If there is a nobb hierarchy structure within the pricebook, it will be used, 
           otherwise general groups will be shown if there are any, like in some custom price books for example.
           As many price lists are designed, the same articles will occur in both group structures, and to avoid
           doublettes during searches, only one structure will be used at a time, and then preferably the nobb hierarchy. */
        if (groups) {
            if (groups.length == 0) {
                nobbless = load.groups // These groups are separate from nobb
            }
        }
        else {
            nobbless = load.groups // These groups are separate from nobb
        }

        var suppliers: Array<Supplier>

        try {
            suppliers = load.suppliers // These are custom suppliers for this price book
        }

        catch (e) {
            suppliers = []
        }

        var o, j, k, amount = 0
        // Initiating the two main arrays where article groups can be found later
        var pricebook = this.m_priceBooks[this.m_loadingStatus.priceBookGenerationAtIndex]

        if (pricebook != null) {
            pricebook.InitHierarchialGroups()
            pricebook.InitAlphabeticalGroups()
        }

        var hierarchialGroups = pricebook.GetHierarchialGroups();
        var alphabeticalGroups = pricebook.GetAlphabeticalGroups(false);

        if (hierarchialGroups == null) {
            PADebug.Debug("core.GenerateGroups  GetHierarchialGroups() == null")
            return
        }

        if (alphabeticalGroups == null) {
            PADebug.Debug("core.GenerateGroups  GetAlphabeticalGroups() == null")
            return
        }

        pricebook.InitNobbLessGroups(nobbless, this)
        pricebook.InitSuppliers(suppliers)

        var importedCathegories: Array<{ identifier: string, description: string }> = new Array<{ identifier: string, description: string }>()
        var cath: { identifier: string, description: string }

        // Going through the imported group data and generating an array of cathegories from it, following the hierarchial structure of the original data but
        // "flattening" it out in order to aquire a general format for all groups at all levels, sharing the same array. The hierarchy won't be lost here, as it
        // can and will be read again from the id (nobb group number) of each cathegory, in order to generate the hierarchial nobb tree view (seen to the left).
        for (o = 0; o < groups.length; o++) {
            mainGr = load.nobbGroups[o].mainGroups
            cath = { identifier: load.nobbGroups[o].id, description: load.nobbGroups[o].description }
            importedCathegories[amount] = cath
            amount++

            for (j = 0; j < mainGr.length; j++) {
                itemGr = load.nobbGroups[o].mainGroups[j].itemGroups
                cath = { identifier: load.nobbGroups[o].mainGroups[j].id, description: load.nobbGroups[o].mainGroups[j].description }
                importedCathegories[amount] = cath
                amount++

                for (k = 0; k < itemGr.length; k++) {
                    cath = { identifier: load.nobbGroups[o].mainGroups[j].itemGroups[k].id, description: load.nobbGroups[o].mainGroups[j].itemGroups[k].description }
                    importedCathegories[amount] = cath
                    amount++
                }
            }
        }

        // The cathegories at L3 are turned into article groups at hierarchy level 3 within the hierarchy tree, but same groups will instead be used as L1 within the alphabetical tree
        var artH: ArticleGroupGW // holder for hierarchial group
        var artA: ArticleGroupGW // holder for alphabetical group
        var idMin: string
        var idMax: string

        for (var i = 0; i < importedCathegories.length; i++) {
            try {
                if (importedCathegories[i].identifier.length == 7) { // Here the length of the nobb group code is used to determine its position in the hierarchy. 7 numbers means it's a L3 group
                    artH = new ArticleGroupGW(importedCathegories[i].identifier, importedCathegories[i].identifier, importedCathegories[i].identifier + " " + importedCathegories[i].description, 3, importedCathegories[i].description)
                    artA = new ArticleGroupGW(importedCathegories[i].identifier, importedCathegories[i].identifier, importedCathegories[i].identifier + " " + importedCathegories[i].description, 3, importedCathegories[i].description)
                    artA.synchronize(artH)
                    hierarchialGroups.push(artH)
                    alphabeticalGroups.push(artA)
                    continue;
                }
            }
            catch (e) {
                Assert(false, "in BDKalkCore.GenerateGroups() section 1  iteration:" + i.toString()) // (Debug info if this would fail)
            }

            try {
                if (importedCathegories[i].identifier.length == 4) { // Here the length of the nobb group code is used to determine its position in the hierarchy. 4 numbers means it's a L2 group
                    idMin = importedCathegories[i].identifier + "000"
                    idMax = importedCathegories[i].identifier + "999"
                    artH = new ArticleGroupGW(idMin, idMax, importedCathegories[i].identifier + " " + importedCathegories[i].description, 2, importedCathegories[i].description)
                    hierarchialGroups.push(artH)
                    continue;
                }
            }
            catch (e) {
                Assert(false, "in BDKalkCore.GenerateGroups() section 2  iteration:" + i.toString()) // (Debug info if this would fail)
            }

            try {
                if (importedCathegories[i].identifier.length == 2) { // Here the length of the nobb group code is used to determine its position in the hierarchy. 2 numbers means it's a L1 group
                    idMin = importedCathegories[i].identifier + "00000"
                    idMax = importedCathegories[i].identifier + "99999"
                    artH = new ArticleGroupGW(idMin, idMax, importedCathegories[i].identifier + " " + importedCathegories[i].description, 1, importedCathegories[i].description)
                    hierarchialGroups.push(artH)
                    continue;
                }
            }
            catch (e) {
                Assert(false, "in BDKalkCore.GenerateGroups() section 3  iteration:" + i.toString()) // (Debug info if this would fail)
            }

            Assert(false, "core.GenerateGroups() FEL VID KATEGORI IMPORT - FEL ANTAL SIFFROR I GRUPP ID!!!")
        }

        // Now the alphabetical array must ofcourse be sorted alphabetically:
        alphabeticalGroups.sort(function (a, b) {
            var x = a.GetAlphaPos().toLowerCase();
            var y = b.GetAlphaPos().toLowerCase();

            // Some norwegian letters are not in the correct alphabetical order as they appear 
            // in ASCII, so let's give those letters a little touch before comparing the titles...
            var newX: string = ""
            for (let i = 0; i < x.length; i++) {
                if (x.charAt(i) == "æ") {
                    newX += "æ1"
                    continue
                }

                if (x.charAt(i) == "ø") {
                    newX += "æ2"
                    continue
                }

                if (x.charAt(i) == "å") {
                    newX += "æ3"
                    continue
                }

                newX += x.charAt(i)
            }

            var newY: string = ""
            for (let i = 0; i < y.length; i++) {
                if (y.charAt(i) == "æ") {
                    newY += "æ1"
                    continue
                }

                if (y.charAt(i) == "ø") {
                    newY += "æ2"
                    continue
                }

                if (y.charAt(i) == "å") {
                    newY += "æ3"
                    continue
                }

                newY += y.charAt(i)
            }

            if (newX < newY) { return -1; }
            if (newX > newY) { return 1; }
            return 0;
        });
        
        /* We will sort the hierarchial groups by their minimum values ( -just in case, though they should already be in order. ) */
        hierarchialGroups.sort(function (a, b) {
            var x = a.GetIDMin();
            var y = b.GetIDMin();
            if (x < y) { return -1; }
            if (x > y) { return 1; }
            return 0;
        });

        // For graphics, the groups being the, or under the last L2 group should look diffetent at the hierarchy lines to the left,
        // and the following sets a flag of article groups in the hierarchy to true in those cases
        var isLastBorn = true
        for (var i = hierarchialGroups.length - 1; i >= 0; i--) {
            if (hierarchialGroups[i].GetLevel() == 1) {
                isLastBorn = true
            }
            else {
                if (hierarchialGroups[i].GetLevel() == 2) {
                    hierarchialGroups[i].SetLastBorn(isLastBorn, 102)
                    isLastBorn = false
                }
                else {
                    hierarchialGroups[i].SetLastBorn(isLastBorn, 102)
                }
            }
        }

        // We will do the same with the alphabetical groups..
        var isLastBorn = true
        for (var i = alphabeticalGroups.length - 1; i >= 0; i--) {
            if (alphabeticalGroups[i].GetLevel() == 1) {
                isLastBorn = true
            }
            else {
                if (alphabeticalGroups[i].GetLevel() == 2) {
                    alphabeticalGroups[i].SetLastBorn(isLastBorn, 102)
                    isLastBorn = false
                }
                else {
                    alphabeticalGroups[i].SetLastBorn(isLastBorn, 102)
                }
            }
        }

        setTimeout(this.initiationSecond.bind(this), 60) // This will switch over to the main view display, prior to loading completion

        this.m_loadingStatus.priceBookGenerationInProgress = false
        this.m_priceBooks[this.m_loadingStatus.priceBookGenerationAtIndex].SetInitStage(2)

    }

    // This phase opens up the views after groups have been prepared and before they are loaded with article data
    initiationSecond() {
        this.PassOnPreviousNobbGroupSelection()
        this.InitLoadingBeat()
    }

    // This initiation may be called more than once without causing multiple timeouts for the loading beat which is started by it.
    InitLoadingBeat() {
        if (this.m_loadingStatus.beatStarted == false) {
            this.m_loadingStatus.beatStarted = true
            this.LoadingBeat()
        }
    }

    // This method halts the loading process
    StopLoadingBeat() {
        this.m_loadingStatus.beatStarted = false
    }

    // Repeats itself until all data groups have been loaded
    LoadingBeat() {
        this.m_countOfLoadingBeats++

        // Beat stage is only made for animation purposes
        this.m_beatStage++

        if (this.m_beatStage > 100) {
            this.m_beatStage = 0
        }

        // this.UpdateLoadPercentage(this.m_beatStage / 100, "test")

        if (this.m_loadingStatus.beatStarted == false) {
            // Loading beat was stopped using this.StopLoadingBeat()
            return
        }

        setTimeout(this.LoadingBeat.bind(this), 50)

        // Checking if ext data is being prepared for selected articles or else need to be so now.
        if (this.m_articleIsQueuedToReciveExtData == true) {
            return
        }
        else {
            if (this.LoadNextExtDataForSelectedArticles() == true) {
                this.m_countOfRequestedExtDataForLoadedArticles++
                return
            }

            // All selected articles seem to have their ext data loaded already
        }

        // If a price book loading is in progress, this beat will be skipped
        if (this.m_loadingStatus.priceBookGenerationInProgress == true) {
            return
        }
        else {
            if (this.CurrentPrizeBookIsInitiated() == false) {
                this.InitiateNextPriceBook()
                return
            }
        }

        // If an earlier load is allready in progress, this beat (thread) will be interrupted
        if (this.m_loadingStatus.loadInProgress == true) {
            return
        }

        // Loading rights are claimed for this thread or beat sequence only, until done

        if (this.IsShowingAllPriceBooks() == true) {
            var allBooksLoaded = true

            for (var i = 0; i < this.m_priceBooks.length; i++) {
                if (this.m_priceBooks[i].GetInitStage() < 3) {
                    allBooksLoaded = false
                    break
                }
            }

            if (allBooksLoaded == true) {
                PADebug.LogOnce("core.loadingBeat - all groups in all pricebooks have already been loaded!")
            }
            else {
                this.LoadNextGroup()
            }
        }
        else {
            var pricebook = this.GetSelectedPriceBook()
            try {
                if (pricebook && pricebook.GetIsLoadCompleted() == false) {
                    this.LoadNextGroup()
                }
            }
            catch (e) {
                Assert(false, "(in core.LoadingBeat) - Oops, core.LoadNextGroup seem to have crashed!")
            }
        }
    }

    // Checks if there are articles in the selection which has no extended data attatched to them yet, and if so, calls for one to be cashed.
    LoadNextExtDataForSelectedArticles() {

        for (var i = 0; i < this.m_selectedArticles.length; i++) {
            if (this.m_selectedArticles[i].GetIfHasExtData() == null) {
                var nobb = this.m_selectedArticles[i].GetNobb()
                if (nobb) {
                    if (nobb.length > 0) {
                        this.m_selectedArticleQueuedToReciveExtData = this.m_selectedArticles[i]
                        this.m_articleIsQueuedToReciveExtData = true

                        LoadFromUrl(this.m_serverUrl + "api/price/nobb/" + this.m_selectedArticles[i].GetNobb().toString(), this.OnRecieveNextExtDataForSelectedArticles.bind(this), this.DefaultLoadErrorHandler.bind(this), this.GetApp().state.accessToken)
                        return true
                    }
                }

                this.m_selectedArticles[i].SetExtData(this.m_defaultExtDataForArticlesMissingIt)
            }
        }

        return false
    }

    // Recieving data in asynchronous extended data gathering for selected articles (utvalg) 
    OnRecieveNextExtDataForSelectedArticles(data: string) {

        var extD = new ExtDataGW()
        var extDataIsReplaced = false
        var hog: any

        try {
            hog = JSON.parse(data)
            extD.RecieveData(hog)
        }

        catch (e) {
            Assert(false, "core.OnRecieveNextExtDataForSelectedArticles - failed json parse of ext data.")
            extD = this.m_defaultExtDataForArticlesMissingIt
            extDataIsReplaced = true
        }

        var handlerShouldBeCalled: boolean = false
        const targetArticle = this.m_selectedArticleQueuedToReciveExtData
        this.m_selectedArticleQueuedToReciveExtData = null
        var prevValue = 0

        // Control checking that the targeted article matches the requested data
        if (targetArticle && extD.GetNobbNr().toString() == targetArticle.GetNobb()) {
            prevValue = targetArticle.GetAmount()
            extD.SetSelectionLabel(targetArticle.GetSelectionLabel())
            targetArticle.SetExtData(extD)

            // Secondly checks if the current ext data for selected article needs to be updated or if it is already valid
            if (this.m_extDataForSelectedItem != null) {
                if (targetArticle.GetNobb() == this.m_extDataForSelectedItem.GetNobbNr().toString()) {
                    this.m_extDataForSelectedItem = extD
                }
            }
        }
        else {
            Assert(false, "core.OnRecieveNextExtDataForSelectedArticles(..) - it looks like the request for the extended data is not aimed towards the assigned article? ")
        }

        if (targetArticle) {
            extD.DirectSubChoise(targetArticle) // Initiates the default choise of supplier and package type
            targetArticle.SetAmount(prevValue)
        }

        if (handlerShouldBeCalled) {
            var ew = this.GetExtInfoView()
            if (ew) {
                ew.forceUpdate() // (an update will be enough to have the ext data shown as it is available)
            }
        }

        this.m_articleIsQueuedToReciveExtData = false
    }

    // A check if the currently selected pricebook has been generated (having its nobb group structure up and ready, regardless
    // of if its nobbgroups are loaded with data or not)
    CurrentPrizeBookIsInitiated() {
        if (this.IsShowingAllPriceBooks() == true) {
            for (var i = 0; i < this.m_priceBooks.length; i++) {
                if (this.m_priceBooks[i].GetInitStage() < 2) {
                    return false
                }
            }

            if (this.m_allPriceBookHierarchyIsValid == false) {
                this.m_allPriceBookHierarchyIsValid = true
                this.UpdateCrossPriceBookHierarchy()
            }

            return true
        }
        else {
            var book = this.GetSelectedPriceBook()
            if (book != null) {
                if (book.GetInitStage() > 1) {
                    return true
                }
            }
        }

        return false
    }

    // InitiateNextPriceBook will check if all necessary pricebooks are currently initiated and ready to use, if not, one of the
    // pricebooks that misses initiation will be initiated, until this method no longer need to be called by the loading beat.
    InitiateNextPriceBook() {
        if (this.IsShowingAllPriceBooks() == true) {

            for (var i = 0; i < this.m_priceBooks.length; i++) {
                if (this.m_priceBooks[i].GetInitStage() < 2) {
                    // Todo initiate book initiation here
                    const ind = i
                    this.InitiatePriceBookAtIndex(ind)
                    return
                }
            }

            // (All books were initiated already)
            this.m_loadingStatus.priceBookGenerationInProgress = false
            return
        }
        else {
            var book = this.GetSelectedPriceBook()

            if (book != null) {
                if (book.GetInitStage() < 2) {
                    this.m_loadingStatus.priceBookGenerationInProgress = true
                    var curr = -1

                    for (var i = 0; i < this.m_priceBooks.length; i++) {

                        if ((this.m_priceBooks[i].GetInitStage() < 2) && (curr == -1)) {
                            const val = i
                            curr = val
                        }

                        if (this.m_priceBooks[i].GetPriceBookId() == this.GetSelectedPriceBookID()) {

                            if (this.m_priceBooks[i].GetInitStage() < 2) {
                                const ind = i
                                this.InitiatePriceBookAtIndex(ind)
                                return
                            }
                        }
                    }

                    if (curr != -1) {
                        this.InitiatePriceBookAtIndex(curr)
                        return
                    }

                    // No groups left to be initiated!
                }
                else {
                    // Pricebook is already initiated so we're all set, done here.
                    this.m_loadingStatus.priceBookGenerationInProgress = false
                    return
                }
            }
            else {
                Assert(false, "core.InitiateNextPriceBook - pricebook instance was null!")
            }
        }
    }

    // Loads one of the remaining data groups from the server
    LoadNextGroup() {

        if (this.IsShowingAllPriceBooks() == true) {

            var groupToLoad = null
            var ongoingLoadsDetected = false
            var loadedGroupsAmount = 0
            var priorityFound = 0
            var isPrio: boolean
            var totalGroupsAmount = 0
            var priceBookIdToLoadFrom = -10

            if (this.m_groupBeingLoaded != null) {
                // It seems there is already a group being loaded right now, so this attempt will be interrupted so that the previous one can finish
                return
            }

            try {

                for (var book = 0; book < this.m_priceBooks.length; book++) {
                    var alphabeticalGroups = this.m_priceBooks[book].GetAlphabeticalGroups(false);
                    var nobblessGroups: ArticleGroupGW[] | null = this.m_priceBooks[book].GetNobbUnspecifiedGroups(false);
                    var localGroup = null
                    var priorityFoundLocal = 0

                    if (alphabeticalGroups != null) {
                        totalGroupsAmount += alphabeticalGroups.length

                        for (var i = 0; i < alphabeticalGroups.length; i++) {
                            nobblessGroups = null /* If a price book has alphabetical and hierarchial nobb groups in it, nobbless won't 
                               need to be loaded as it will typically just contain doublettes of what's in the nobb specified groups,
                               but some custom price books may only have uncathegorised groups in them, and then these will be used. */

                            if (alphabeticalGroups[i].GetLoadedStatus().status == 2) {
                                loadedGroupsAmount++
                                continue
                            }

                            if (alphabeticalGroups[i].GetLoadedStatus().status == 1) {
                                ongoingLoadsDetected = true
                                continue
                            }

                            if (priorityFound == 0) {
                                if (alphabeticalGroups[i].GetLoadedStatus().status == 0) {
                                    if (this.isWithinNobb(this.m_lastSelectedNobbGroup, alphabeticalGroups[i].GetGroupID())) {
                                        localGroup = alphabeticalGroups[i]
                                        priorityFound = 1
                                        priorityFoundLocal = 1
                                    }
                                    else {
                                        if (localGroup == null) {
                                            localGroup = alphabeticalGroups[i]
                                        }
                                    }
                                }
                            }
                        }
                    }

                    if (nobblessGroups != null) {
                        totalGroupsAmount += nobblessGroups.length

                        for (var i = 0; i < nobblessGroups.length; i++) {
                            if (nobblessGroups[i].GetLoadedStatus().status == 2) {
                                loadedGroupsAmount++
                                continue
                            }

                            if (nobblessGroups[i].GetLoadedStatus().status == 1) {
                                ongoingLoadsDetected = true
                                continue
                            }

                            if (priorityFound == 0) {
                                if (nobblessGroups[i].GetLoadedStatus().status == 0) {

                                    if (this.m_lastSelectedNobbGroup == nobblessGroups[i].GetGroupID()) {

                                        localGroup = nobblessGroups[i]
                                        priorityFound = 1
                                        priorityFoundLocal = 1
                                    }
                                    else {
                                        if (localGroup == null) {
                                            localGroup = nobblessGroups[i]
                                        }
                                    }
                                }
                            }
                        }
                    }

                    if ((nobblessGroups == null) && (alphabeticalGroups == null)) {
                        continue
                    }

                    if ((localGroup == null) && (priorityFound == 0)) {
                        // this happens when all groups have been loaded already and before loading terminates
                        this.FinalizeGroups(book)
                        continue
                    }
                    else {
                        if ((groupToLoad == null) || (priorityFoundLocal == 1)) {
                            groupToLoad = localGroup
                            priceBookIdToLoadFrom = this.m_priceBooks[book].GetPriceBookId()
                            priorityFoundLocal = 0
                        }
                    }
                }

            }

            catch (e) {
                Assert(false, "core.loadnextgroup upper section failed #1")
            }

            try {

                if (groupToLoad == null) {
                    // this happens when all groups have been loaded already and before loading terminates
                    this.DebugOutput("core.LoadNextGroup - all groups i all books have been loaded")
                    return
                }

                // The group to load has been selected, now let's get to business!
                if (ongoingLoadsDetected == false) {
                    groupToLoad.GetLoadedStatus().status = 1

                    this.m_groupBeingLoaded = groupToLoad

                    Assert(priceBookIdToLoadFrom != -10, "core.LoadNextGroup - priceBookIdToLoadFrom was not set!")

                    this.m_countOfGroupLoadInits++
                    this.m_loadingStatus.loadInProgress = true

                    var usedGroupID = groupToLoad.GetGroupID()
                    if (usedGroupID.slice(0, 11) == "withoutNobb") {
                        const copy = usedGroupID
                        usedGroupID = ""

                        for (var i = 12; i < copy.length; i++) {
                            usedGroupID += copy.charAt(i)
                        }

                        usedGroupID = "group/" + usedGroupID
                    }
                    else {
                        usedGroupID = "nobbgroup/" + usedGroupID
                    }

                    this.m_groupBeingLoadedIsFromPricebookID = priceBookIdToLoadFrom

                    const groupDateAdress = "GroupDate_id_" + groupToLoad.GetGroupID()

                    if (usingDeviceLocalStorage) {
                        this.m_loadingGroupUrl = this.m_serverUrl + "api/price/" + priceBookIdToLoadFrom + "/" + usedGroupID
                        localforage.getItem(groupDateAdress).then(this.OnLocalForageResponseDate.bind(this))
                    }
                    else {
                        this.m_locallyStoredGroupDataIsUsed = false
                        LoadFromUrl((this.m_serverUrl + "api/price/" + priceBookIdToLoadFrom + "/" + usedGroupID), this.OnGroupLoaded.bind(this), this.DefaultLoadErrorHandler.bind(this), this.GetApp().state.accessToken)
                    }

                    var perc = 0.0

                    if (totalGroupsAmount > 0) {
                        perc = loadedGroupsAmount / totalGroupsAmount
                    }

                    this.UpdateLoadPercentage(perc, groupToLoad.GetGroupID()) // Updates the loading screen's percentage bar
                }
                else {
                    PADebug.DebugOnce("core.LoadNextGroup - function called while a group is already being loaded! (upper)")
                }
            }
            catch (e) {
                Assert(false, "core.loadnextgroup upper section threw an exception!")
            }
        }
        else {
            var groupToLoad = null
            var ongoingLoadsDetected = false
            var loadedGroupsAmount = 0
            var totalGroupsAmount = 0
            var priorityFound = 0
            var isPrio: boolean

            if (this.m_groupBeingLoaded != null) {
                // It seems there is already a group being loaded right now, so this attempt will be interrupted so that the previous one can finish
                PADebug.Debug("core.LoadNextGroup m_groupBeingLoaded is set, so interrupting load")
                return
            }

            var alphabeticalGroupsLocal = this.GetAlphabeticalGroups(false);
            var currentBook = this.GetSelectedPriceBook()
            var nobblessGroupsLocal: ArticleGroupGW[] | null = new Array<ArticleGroupGW>()

            if (currentBook) {
                nobblessGroupsLocal = currentBook.GetNobbUnspecifiedGroups(false)
            }
            else {
                Assert(false, "core.LoadNextGroup - current pricebook was null (this should never occur here)")
            }

            if (alphabeticalGroupsLocal != null) {

                totalGroupsAmount += alphabeticalGroupsLocal.length

                for (var i = 0; i < alphabeticalGroupsLocal.length; i++) {
                    nobblessGroupsLocal = null /* If a price book has alphabetical and hierarchial nobb groups in it, nobbless won't 
                        need to be loaded as it will typically just contain doublettes of what's in the nobb specified groups,
                        but some custom price books may only have uncathegorised groups in them, and then these will be used. */

                    if (alphabeticalGroupsLocal[i].GetLoadedStatus().status == 2) {
                        loadedGroupsAmount++
                        continue
                    }

                    if (alphabeticalGroupsLocal[i].GetLoadedStatus().status == 1) {
                        ongoingLoadsDetected = true
                        continue
                    }

                    if ((alphabeticalGroupsLocal[i].GetLoadedStatus().status == 0) && (priorityFound == 0)) {
                        if (this.isWithinNobb(this.m_lastSelectedNobbGroup, alphabeticalGroupsLocal[i].GetGroupID())) {
                            groupToLoad = alphabeticalGroupsLocal[i]
                            priorityFound = 1
                        }
                        else {
                            if (groupToLoad == null) {
                                groupToLoad = alphabeticalGroupsLocal[i]
                            }
                        }
                    }
                }
            }

            if (nobblessGroupsLocal != null) {

                totalGroupsAmount += nobblessGroupsLocal.length

                for (var i = 0; i < nobblessGroupsLocal.length; i++) {

                    if (nobblessGroupsLocal[i].GetLoadedStatus().status == 2) {
                        loadedGroupsAmount++
                        continue
                    }

                    if (nobblessGroupsLocal[i].GetLoadedStatus().status == 1) {
                        ongoingLoadsDetected = true
                        continue
                    }

                    if (priorityFound == 0) {

                        if (nobblessGroupsLocal[i].GetLoadedStatus().status == 0) {

                            if ((this.m_lastSelectedNobbGroup == nobblessGroupsLocal[i].GetGroupID()) || (this.m_lastSelectedNobbGroup == "withoutNobb")) {
                                groupToLoad = nobblessGroupsLocal[i]
                                priorityFound = 1
                                priorityFoundLocal = 1
                            }
                            else {
                                if (groupToLoad == null) {
                                    groupToLoad = nobblessGroupsLocal[i]
                                }
                            }
                        }
                    }
                }
            }

            if (groupToLoad == null) {
                // this happens when all groups have been loaded already and before loading terminates
                this.FinalizeGroups(this.GetSelectedPriceBookIndex())
                PADebug.Log("core.LoadNextGroup - ending after FinalizeGroups at selected pricebook ")
                return
            }

            // The group to load has been selected, now it shall be loaded.

            if (ongoingLoadsDetected == false) {
                groupToLoad.GetLoadedStatus().status = 1
                this.m_countOfGroupLoadInits++
                this.m_groupBeingLoaded = groupToLoad
                this.m_loadingStatus.loadInProgress = true

                this.m_groupBeingLoadedIsFromPricebookID = this.GetSelectedPriceBookID()

                var usedGroupID = groupToLoad.GetGroupID()
                if (usedGroupID.slice(0, 11) == "withoutNobb") {
                    const copy = usedGroupID
                    usedGroupID = ""

                    for (var i = 12; i < copy.length; i++) {
                        usedGroupID += copy.charAt(i)
                    }

                    usedGroupID = "group/" + usedGroupID
                }
                else {
                    usedGroupID = "nobbgroup/" + usedGroupID
                }

                // checking if this group has already been stored locally
                const groupDateAdress = "GroupDate_id_" + groupToLoad.GetGroupID()

                this.m_loadingGroupUrl = this.m_serverUrl + "api/price/" + this.GetSelectedPriceBookID() + "/" + usedGroupID

                if (usingDeviceLocalStorage) {
                    this.m_loadingGroupUrl = this.m_serverUrl + "api/price/" + this.GetSelectedPriceBookID() + "/" + usedGroupID
                    localforage.getItem(groupDateAdress).then(this.OnLocalForageResponseDate.bind(this))
                }
                else {
                    this.m_locallyStoredGroupDataIsUsed = false
                    LoadFromUrl((this.m_serverUrl + "api/price/" + this.GetSelectedPriceBookID() + "/" + usedGroupID), this.OnGroupLoaded.bind(this), this.DefaultLoadErrorHandler.bind(this), this.GetApp().state.accessToken)
                }

                var perc = 0.0

                if (totalGroupsAmount > 0) {
                    perc = loadedGroupsAmount / totalGroupsAmount
                }

                this.UpdateLoadPercentage(perc, groupToLoad.GetGroupID()) // Updates the loading screen's percentage bar
            }
        }
    }

    // Creates a displayable string from a given Date json (from the server) in order to tell when a selecton was last saved.
    public GetTimeFromJsonString(timeStr: string, hourDifference: number) {

        var yearStr = ""
        const compensationForConversion = 2

        for (var i = 0; i < 4; i++) {
            yearStr += timeStr.charAt(i)
        }

        var monthStr = timeStr.charAt(5) + timeStr.charAt(6)

        var dayStr = timeStr.charAt(8) + timeStr.charAt(9)

        var hourStr = timeStr.charAt(11) + timeStr.charAt(12)

        var minuteStr = timeStr.charAt(14) + timeStr.charAt(15)

        var secondStr = timeStr.charAt(17) + timeStr.charAt(17)

        var timeSaved = new Date(this.valueOfString(yearStr), this.valueOfString(monthStr) - 1, this.valueOfString(dayStr), this.valueOfString(hourStr), this.valueOfString(minuteStr), this.valueOfString(secondStr))
        var timeAbsolute: number = timeSaved.valueOf() + ((hourDifference + compensationForConversion) * 3600000)
        var resutlTime = new Date(timeAbsolute)

        return resutlTime
    }

    // Converts a numeric string into a number. The string must not have any comma or decimals in it 
    public valueOfString(numericString: string) {
        var value = 0
        var potence = 1

        for (var pot = numericString.length - 1; pot >= 0; pot-- , potence *= 10) {
            value += this.valueOfLetter(numericString[pot]) * potence
        }

        return value
    }

    // Converts a numeric letter into a number from 0 to 9
    public valueOfLetter(numericLetter: string) {
        if (numericLetter == "0") {
            return 0
        }

        if (numericLetter == "1") {
            return 1
        }

        if (numericLetter == "2") {
            return 2
        }

        if (numericLetter == "3") {
            return 3
        }

        if (numericLetter == "4") {
            return 4
        }

        if (numericLetter == "5") {
            return 5
        }

        if (numericLetter == "6") {
            return 6
        }

        if (numericLetter == "7") {
            return 7
        }

        if (numericLetter == "8") {
            return 8
        }

        if (numericLetter == "9") {
            return 9
        }

        return 0
    }

    // Recives a json of the time when the loading group was stored locally if it was so, otherwise null
    OnLocalForageResponseDate(dateSaved: string) {

        if (dateSaved) {
            var diff = -1

            try {
                var now = this.GetTimeFromJsonString(new Date().toJSON(), 2).valueOf()
                var then = this.GetTimeFromJsonString(dateSaved, 2).valueOf()
                diff = now - then
            }

            catch (e) {
                PADebug.Debug("core.OnLocalForageResponseDate - an error occured while calculating the age of a cashed group! ")
            }

            if ((diff <= deviceLocalDataMaxAgeInSeconds) && (diff != -1)) {
                this.m_loadingGroupDate = dateSaved
                // The group has been stored and the time given will be used here to check if the data is outdated or can be reused

                if (this.IsLocallyStoredGroupUpToDate(dateSaved) == true) {
                    const groupLoading = this.m_groupBeingLoaded
                    if (groupLoading) {
                        const groupAdress = "Group_id_" + groupLoading.GetGroupID()
                        localforage.getItem(groupAdress).then(this.OnLocalForageResponseContent.bind(this))
                    }
                }
                else {
                    this.m_locallyStoredGroupDataIsUsed = false
                    LoadFromUrl(this.m_loadingGroupUrl, this.OnGroupLoaded.bind(this), this.DefaultLoadErrorHandler.bind(this), this.GetApp().state.accessToken)
                }

                return // Interrupts if has reused the cashed data, otherwise proceeds with a regular data refresh as below
            }
        }

        // The group seem not to have been stored locally, so it will be requested from the server instead
        this.m_locallyStoredGroupDataIsUsed = false
        LoadFromUrl(this.m_loadingGroupUrl, this.OnGroupLoaded.bind(this), this.DefaultLoadErrorHandler.bind(this), this.GetApp().state.accessToken)
    }

    // 
    IsLocallyStoredGroupUpToDate(date: string) {
        return true // temporary. replaace with a check of age versus settings for maximum age of locally stored groups
    }

    // 
    OnLocalForageResponseContent(content: string) {
        this.m_locallyStoredGroupDataIsUsed = true
        this.m_locallyStoredGroupData = content
        this.OnGroupLoaded(content)
    }

    // This handler recives the finished request of a group data json file and adds the result into the group arrays 
    OnGroupLoaded(incomingData: string) {
        this.m_countOfGroupLoadCompletes++

        var art: {}
        var inArts: [{
            id: string,
            eanNo: string,
            identifier: string,
            description: string,
            priceBase: number,
            priceRecommended: number,
            unit: string,
            identifierOld: string,

            description2: string,
            packageUnit: string,
            packageAmount: number,
            pricePurchase: number,
            discountFactor: number,
            nobb: string,
            nobbOwner: string,
            nobbModule: string,
            priceType: string,
            itemType: string,
            priceFactory: number
        }]

        var arti: ArticleGW
        var raw: any = null

        Assert(incomingData != null, "core.OnGroupLoaded - incoming data was null!")
        Assert(incomingData != "", "core.OnGroupLoaded - incoming data was an empty string!")

        try {
            raw = JSON.parse(incomingData)
        }

        catch (e) {
            Assert(false, "core.OnGroupLoaded - the result was not parsable from the sent json formate. \nData recieved : " + incomingData)
            this.m_loadingStatus.loadInProgress = false
            this.m_groupBeingLoaded = null
            return
        }

        inArts = raw.items

        var gbl = this.m_groupBeingLoaded

        if (gbl) {
            for (var j = 0; j < inArts.length; j++) {
                const pbid = this.m_groupBeingLoadedIsFromPricebookID

                arti = new ArticleGW(inArts[j].id, inArts[j].eanNo, inArts[j].identifier, inArts[j].identifierOld, gbl.GetGroupID(), inArts[j].description, inArts[j].description2, "", "", "", inArts[j].packageAmount, inArts[j].unit, inArts[j].pricePurchase, inArts[j].priceBase, inArts[j].priceRecommended, inArts[j].priceFactory, inArts[j].packageUnit, "NOK", inArts[j].nobb, inArts[j].nobbOwner, inArts[j].nobbModule, inArts[j].priceType, inArts[j].itemType, "", 0, inArts[j].discountFactor, pbid)
                const pb = this.GetPriceBookByID(pbid)

                var fullData: Array<ArticleGW> | null = null
                var fullNoblessData: Array<ArticleGW> | null = null

                if (pb != null) {
                    fullData = pb.GetFullData();

                    if (gbl.GetGroupID().slice(0, 11) == "withoutNobb") {
                        fullNoblessData = pb.GetNobbUnspecifiedArticles()
                    }
                }

                fullData && fullData.push(arti)
                fullNoblessData && fullNoblessData.push(arti)
                this.m_allPriceBooksFullData.push(arti)
                gbl.GetArticles().push(arti)
            }

            gbl.GetLoadedStatus().status = 2
            this.m_groupBeingLoaded = null

            gbl.SortOwnArticles()
            this.m_loadingStatus.loadInProgress = false

            if (usingDeviceLocalStorage) {
                if (this.m_locallyStoredGroupDataIsUsed == false) {
                    // Storing the loaded dat into local stortage for faster access next time
                    const groupAdress = "Group_id_" + gbl.GetGroupID()
                    localforage.setItem(groupAdress, incomingData, this.OnLocallyStoredGroupSaved.bind(this))
                    const groupDateAdress = "GroupDate_id_" + gbl.GetGroupID()
                    var date = new Date().toJSON()
                    localforage.setItem(groupDateAdress, date, this.OnStoredDateForLocallyStoredGroup.bind(this))
                }
            }

            this.UpdateCathegories(gbl)
        }

        this.m_locallyStoredGroupDataIsUsed = false
    }

    // Currently unused but triggered handler for time values being stored alongside groups when cashed with local forage
    OnStoredDateForLocallyStoredGroup() {
    }

    // Currently unused but triggered handler for groups being stored in temporary internet files using local forage
    OnLocallyStoredGroupSaved() {
    }

    // Whenever a group of articles has been loaded, this function will be called to update the affected views with the new loaded data
    UpdateCathegories(newGroup: ArticleGroupGW) {

        // First let's find out if a nobb group is currently being displayed
        const showingNobb = ((this.m_app.state.DisplayMode == this.m_app.DMode.NobbGroup) || (this.m_app.state.DisplayMode == this.m_app.DMode.Loading))
        var displayedGroupNeedsRefresh = false

        if (showingNobb == true) {
            // Now if the selected nobb group and the recently loaded group would coincide, then we know a refresh will be needed afterwards
            if (this.m_lastSelectedNobbGroup == newGroup.GetGroupID()) {
                displayedGroupNeedsRefresh = true
            }
        }

        // Now let's skim through the hierarchial groups to find groups at L2 and L1 that might need to have their loaded statuses changed
        var recentTopGroup: ArticleGroupGW | null = null
        var recentMidGroup: ArticleGroupGW | null = null
        var MidGroupRemainsUnloaded = false
        var SubGroupRemainsUnloaded = false

        var hierarchialGroups = this.GetHierarchialGroups();

        if (this.m_groupBeingLoadedIsFromPricebookID != null) {
            var targetPriceBook = this.GetPriceBookByID(this.m_groupBeingLoadedIsFromPricebookID)
            if (targetPriceBook != null) {
                hierarchialGroups = targetPriceBook.GetHierarchialGroups()
            }
            else {
                Assert(false, "core.UpdateCathegories - last loaded group was not found using its pricebook id (this will never happen)")
            }
        }
        else {
            Assert(false, "core.UpdateCathegories - last loaded group's pricebook id was null (this will never happen)")
        }

        if (hierarchialGroups == null) {
            if (this.GetSelectedPriceBookID() != -1) {
                return // A recent switch of price book means groups arent initiated and contents aren't ready to be displayed yet, aborting update.
            }

            Assert(false, "core.UpdateCathegories - hierarchialGroups was null when supposed to be valid")
            return
        }

        for (var i = 0; i < hierarchialGroups.length; i++) {
            if (hierarchialGroups[i].GetLevel() == 1) {
                // This is the beginning of a new main nobb cathegory, Lets first make sure the previous one is completed before we dig into it..

                // If there's a recent mid group, that means we're at the end of the last mid group of a previous top group, so let's finish it
                if (recentMidGroup != null) {
                    if (SubGroupRemainsUnloaded == false) {
                        // We've come across a mid group that is loaded completely and needs to have its status updated to loaded (2) if it isn't already so
                        if (recentMidGroup.GetLoadedStatus().status != 2) {
                            // So the mid group wasn't set to loaded before? Then let's also check if it's the selected nobb group..
                            if (this.m_app.state.articlesNobb == recentMidGroup.GetGroupID()) {
                                // It was so, this means the view must be updated!
                                displayedGroupNeedsRefresh = true
                            }

                            recentMidGroup.GetLoadedStatus().status = 2 // ..and set it's loaded flag to loaded

                            //Though for the L2 group to show some articles in it, we also need to refer to these articles from the subgroups into the articles array of this L2 group
                            this.CompleteHierarchialGroup(recentMidGroup)
                        }
                    }
                    else {
                        // So something within this recent mid group yet needs to be loaded, which means this is an unloaded mid group, so the flag must be true
                        MidGroupRemainsUnloaded = true
                    }
                }

                recentMidGroup = null // (No need to check for mid group completion a second time)

                // Next up, let's finish off an eventual previos top group's loaded status
                if (recentTopGroup != null) {
                    if (MidGroupRemainsUnloaded == false) {
                        // The previous top group has nothing left to load, let's see if that's good news or if it is already known..
                        if (recentTopGroup.GetLoadedStatus().status != 2) {
                            // It is news, so let's see to that the view gets an updated if this top group is selected
                            if (this.m_app.state.articlesNobb == recentTopGroup.GetGroupID()) {
                                displayedGroupNeedsRefresh = true
                            }
                            recentTopGroup.GetLoadedStatus().status = 2 // ..and set the loaded flag to loaded

                            //Though for the L1 group to show some articles in it, we also need to refer to these articles from the subgroups into the articles array of this L1 group
                            this.CompleteHierarchialGroup(recentTopGroup)
                        }
                    }
                }

                recentTopGroup = hierarchialGroups[i] // This is where a top group gets remembered prior to having its contents examined
                MidGroupRemainsUnloaded = false
            }
            else {
                if (hierarchialGroups[i].GetLevel() == 2) {
                    // This is the beginning of a new mid level nobb cathegory, Lets complete the eventual previous one first.. 
                    if (recentMidGroup != null) {
                        if (SubGroupRemainsUnloaded == false) {
                            // We've come across a mid group that is loaded completely and needs to have its status updated to loaded (2) if it isn't already so
                            if (recentMidGroup.GetLoadedStatus().status != 2) {
                                // So the mid group wasn't set to loaded before? Then let's also check if it's the selected nobb group..
                                if (this.m_app.state.articlesNobb == recentMidGroup.GetGroupID()) {
                                    // It was so, this means the view must be updated!
                                    displayedGroupNeedsRefresh = true
                                }
                                recentMidGroup.GetLoadedStatus().status = 2 // ..and set it's loaded flag to loaded

                                //Though for the L2 group to show some articles in it, we also need to refer to these articles from the subgroups into the articles array of this L2 group
                                this.CompleteHierarchialGroup(recentMidGroup)
                            }
                        }
                        else {
                            // So something within this recent mid group yet needs to be loaded, which means this is an unloaded mid group, so the flag must be true
                            MidGroupRemainsUnloaded = true
                        }
                    }

                    // The previous mid group has been handled now, this new one will be the next, so we'll keep its reference for later..
                    recentMidGroup = hierarchialGroups[i]
                    SubGroupRemainsUnloaded = false
                }
                else {
                    // This is one of those sub groups, so let's just check if it is loaded or not..
                    if (hierarchialGroups[i].GetLoadedStatus().status != 2) {
                        SubGroupRemainsUnloaded = true
                    }
                }
            }
        }

        /* The whole hierarchy has now been processed, except for the last top and mid level groups, because the loop ends without any new
           top or mid groups being started, and it's when one starts that the prevous groups are finished, so let's now finish what we started..   */

        // We're at the end of the last mid group of the last top group, so let's finish it first
        if (recentMidGroup != null) {
            if (SubGroupRemainsUnloaded == false) {
                // We've come across a mid group that is loaded completely and needs to have its status updated to loaded (2) if it isn't already so
                if (recentMidGroup.GetLoadedStatus().status != 2) {
                    // So the mid group wasn't set to loaded before? Then let's also check if it's the selected nobb group..
                    if (this.m_app.state.articlesNobb == recentMidGroup.GetGroupID()) {
                        // It was so, this means the view must be updated!
                        displayedGroupNeedsRefresh = true
                    }
                    recentMidGroup.GetLoadedStatus().status = 2 // ..and set it's loaded flag to loaded

                    //Though for the L2 group to show some articles in it, we also need to refer to these articles from the subgroups into the articles array of this L2 group
                    this.CompleteHierarchialGroup(recentMidGroup)
                }
            }
            else {
                // So something within this recent mid group yet needs to be loaded, which means this is an unloaded mid group, so the flag must be true
                MidGroupRemainsUnloaded = true
            }
        }

        // Let's finish with checking the last top group's loaded status
        if (recentTopGroup != null) {
            if (MidGroupRemainsUnloaded == false) {
                // The last top group has nothing left to load, let's see if that's good news or if it is already known..
                if (recentTopGroup.GetLoadedStatus().status != 2) {
                    // It is news, so let's see to that the view gets an updated if this top group is selected
                    if (this.m_app.state.articlesNobb == recentTopGroup.GetGroupID()) {
                        displayedGroupNeedsRefresh = true
                    }
                    recentTopGroup.GetLoadedStatus().status = 2 // ..and set the loaded flag to loaded

                    //Though for the L1 group to show some articles in it, we also need to refer to these articles from the subgroups into the articles array of this L1 group
                    this.CompleteHierarchialGroup(recentTopGroup)
                }
            }
        }

        // Now finally, let's carry out the proper actions depending on what has been completely loaded, by updating the view (if the changes took place within the displayed price book)
        if ((displayedGroupNeedsRefresh == true) && (this.GetSelectedPriceBookID() == this.m_groupBeingLoadedIsFromPricebookID)) {
            this.m_app.setState({ DisplayMode: this.m_app.DMode.NobbGroup })
        }

        // Next up, we will update the global hierarchy and the views if all pricebooks are being shown, if not then let's just skip it..
        if (this.GetSelectedPriceBookID() != -1) {
            return
        }

        // Temporarily lets just set the refresh flag to true...
        this.m_allPriceBookHierarchyNeedRefresh = true
    }

    // isWithinNobb makes a check if a nobb group equals or is an undergroup to a certain nobb group 
    isWithinNobb(root: string, group: string) {
        var wrongfulNobb = false

        if (root == "99_splitLetter"){
            return false // This occurs for alphabetical mid groups (by first letter)
        }

        if (root.slice(0, 11) == "withoutNobb") {
            return false
        }

        Assert(((group != null) && (root != null)), "Error in core.iswithinNobb - a nobb string to be compared was null!")

        if (root.length == 0) {
            // This happens prior to when a nobb group has been selected in the tree for the first time
            return false
        }

        if (root.length != 7) {
            wrongfulNobb = true
            PADebug.DebugOnce("Error in core.iswithinNobb - the root string was not 7 chars long like the standard nobb group ID's should be, it was " + root.length.toString() + " : " + root )
        }

        if (group.length != 7) {
            wrongfulNobb = true
            PADebug.DebugOnce("Error in core.iswithinNobb - the group string was not 7 chars long like the standard nobb group ID's should be, it was " + group.length.toString() + " : " + group )
        }

        if (wrongfulNobb == true) {
            if (root == group) {
                return true
            }
            else {
                return false
            }
        }
        
        var level = 3

        if ((root.charAt(6) == "0") && (root.charAt(5) == "0") && (root.charAt(4) == "0")) {
            level = 2
        }

        if ((root.charAt(3) == "0") && (root.charAt(2) == "0")) {
            level = 1
        }

        if (root.charAt(0) != group.charAt(0)) {
            return false
        }

        if (root.charAt(1) != group.charAt(1)) {
            return false
        }

        if (level == 1) {
            return true
        }

        if (root.charAt(2) != group.charAt(2)) {
            return false
        }

        if (root.charAt(3) != group.charAt(3)) {
            return false
        }

        if (level == 2) {
            return true
        }

        if (root.charAt(4) != group.charAt(4)) {
            return false
        }

        if (root.charAt(5) != group.charAt(5)) {
            return false
        }

        if (root.charAt(6) != group.charAt(6)) {
            return false
        }

        return true
    }

    // This is a help function for UpdateCathegories, where a super group gets filled up with article references from its sub groups
    CompleteHierarchialGroup(group: ArticleGroupGW) {
        if (group.GetLevel() == 1) {
            this.m_countOfFinishedHierarchialTopGroups ++ // Info collected for debug uses
        }

        if (group.GetLevel() == 2) {
            this.m_countOfFinishedHierarchialMidGroups++ // Info collected for debug uses
        }
        
        var alphabeticalGroups = null

        var targetPriceBook = this.GetPriceBookByID(this.m_groupBeingLoadedIsFromPricebookID)
        if (targetPriceBook != null) {
            alphabeticalGroups = targetPriceBook.GetAlphabeticalGroups(false)
        }
        else {
            Assert(false, "core.CompleteHierarchialGroup - pricebook id for loaded group was null (this will never happen)")
        }
        
        for (var i = 0; alphabeticalGroups && i < alphabeticalGroups.length; i++) {
            if (this.isWithinNobb(group.GetGroupID(), alphabeticalGroups[i].GetGroupID())) {
                for (var j = 0; j < alphabeticalGroups[i].GetArticles().length; j++) {
                    group.GetArticles().push(alphabeticalGroups[i].GetArticles()[j])
                }
            }
        }

        group.SortOwnArticles()
        this.UpdateGlobalHierarchy()
    }

    // UpdateGlobalHierarchy is called when a nobb group has been loaded, to check if this completes the contents of
    // a cross pricebook hierarchial group, if so, that group will become loaded with references to the included groups
    // and if it's currently opened in the view, the view will be refreshed to show the new contents as well as having
    // searches made available for it in the search input field above.
    UpdateGlobalHierarchy() {
        if (this.m_allPriceBooksHierarchialGroups == undefined) {
            this.GenerateAllPriceBooksHierarchialGroups()
            return
        }

        for (var i = 0; i < this.m_allPriceBooksHierarchialGroups.length; i++) {
            var tested = this.m_allPriceBooksHierarchialGroups[i]
            var globalNobb = tested.GetIDMin()

            if (tested.GetLoadedStatus().status < 2) {

                var groupTotalGroups = 0
                var groupTotalLoadedGroups = 0
                var skipGroup = false

                for (var book = 0; (book < this.m_priceBooks.length) && (skipGroup == false); book++) {
                    var alpha = this.m_priceBooks[book].GetAlphabeticalGroups(false)

                    if (alpha != null) {
                        if (alpha.length > 0) {
                            for (var a = 0; (a < alpha.length) && (skipGroup == false); a++) {
                                if (this.isWithinNobb(globalNobb, alpha[a].GetIDMin())) {
                                    groupTotalGroups++

                                    if (alpha[a].GetLoadedStatus().status < 2) {
                                        if (tested.GetIDMin() != this.m_lastSelectedNobbGroup) {
                                            skipGroup = true
                                        }
                                    }
                                    else {
                                        groupTotalLoadedGroups++
                                    }
                                }
                            }
                        }
                    }
                }

                if (skipGroup == true) {
                    continue
                }
                
                if (groupTotalLoadedGroups == groupTotalGroups) {
                    // A group within the global hierarchy has been found completed
                    this.m_allPriceBookHierarchyNeedRefresh = true
                    tested.GetLoadedStatus().status = 3
                    
                    // We will only trigger a view update if the completed group is also the selected one from the global tree
                    if (this.GetSelectedPriceBookID() == -1) {
                        if (tested.GetIDMin() == this.m_lastSelectedNobbGroup) {
                            this.GetApp().setState({ DisplayMode: this.GetApp().DMode.NobbGroup })
                        }
                    }
                }
                else {
                    // The tested group was not completed, but it's the selected group, so the load bar should be updated
                    if (this.GetSelectedPriceBookID() == -1) {
                        if (tested.GetIDMin() == this.m_lastSelectedNobbGroup) {
                            if (groupTotalGroups > 0) {
                                this.UpdateLoadPercentage(groupTotalLoadedGroups / groupTotalGroups, tested.GetDescription())
                            }
                        }
                    } 
                }
            }
        }
    }
    
    /* FinalizeGroupsAtBookIndex rounds up after a specific price book has completely cashed all of its its groups of articles,
       it enables the search input for the entire data of that price book. */
    FinalizeGroups(priceBookIndex: number) {
        Assert(priceBookIndex < this.m_priceBooks.length, "core.FinalizeGroupsAtBookIndex - index was out of prize book array limits!")
        
        var pricebook = this.m_priceBooks[priceBookIndex]
        // Running a check if all price books have been loaded, so that searches upon all pricebooks at all cathergories should be made available.
        var allBooksAreLoaded = true

        try {
            pricebook && pricebook.FinishLoading()
            pricebook && pricebook.SetInitStage(3)

        }

        catch (e) {
            Assert(false, "core.FinalizeGroups threw an exception - section 1:" + e.toString())
        }

        try {
        
            for (var i = 0; i < this.m_priceBooks.length; i++) {
                if (this.m_priceBooks[i].GetInitStage() != 3) {
                    allBooksAreLoaded = false
                    break
                }
            }
        }

        catch (e) {
            Assert(false, "core.FinalizeGroups threw an exception - section 2:" + e.toString())
        }

        try {
            if (allBooksAreLoaded == true) {
                // there are no more data groups left to be loaded from the server in any available pricebook, 
                // so search input for the entire data of all price books is now enabled. 
                // (no action apart from the update below is presently needed here)
            }
             
            this.m_app.forceUpdate()
        }

        catch (e) {
            Assert(false, "core.FinalizeGroups threw an exception - section 3:" + e.toString() )
        }
    }

    // Returns the extended info data set for the currently selected article, or null if unloaded while posting a request for it from the server, takes a handler as parameter
    LoadExtData(onloaded: (() => void) | null) {
        var handler: () => void;

        handler = onloaded == null ? this.onDefaultExtDataIsReady.bind(this) : onloaded;
        /* In this case the currently selected article will have its extended data loaded for it, unless it is allready in the loading queue, and a default handler
           will make sure the view is updated with the data when it arrives. */

        var article = this.m_app.GetSelectedArticle()

        if (article == null) {
            Assert(false, "BDKalkCore.LoadExtData() was called while no article was currently selected!")
            return null
        }

        if (this.m_extDataForSelectedItem != null) {
            if (this.m_extDataForSelectedItem.GetSelectionLabel() == article.GetSelectionLabel()) {
                return this.m_extDataForSelectedItem
            }
        }

        var extData = article.GetIfHasExtData()
        if (extData != null) {
            this.m_extDataForSelectedItem = extData
            return this.m_extDataForSelectedItem
        }

        if ((article.GetNobb() == "") || (article.GetNobb() == null)) {
            this.m_extDataForSelectedItem = this.m_defaultExtDataForArticlesMissingIt
            setTimeout(handler, 10)
            return this.m_defaultExtDataForArticlesMissingIt
        }

        this.m_extDataLoadCompletionHandler = handler
        LoadFromUrl(this.m_serverUrl + "api/price/nobb/" + article.GetNobb().toString(), this.onIncomingExtData.bind(this), this.DefaultLoadErrorHandler.bind(this), this.GetApp().state.accessToken)

        // Extended data for this article has not been loaded from the server yet, we will post a request for it while returning null at this time
        return null
    }
    
    // Returns the extended info data set for the currently selected article if the extended data is loaded, otherwise null is returned (call LoadExtData to get the missing Ext Data)
    GetExtData() {
        const selID = this.m_app.GetSelectionID()

        if (this.m_extDataForSelectedItem != null) {
            if (this.m_extDataForSelectedItem.GetSelectionLabel() == selID) {
                return this.m_extDataForSelectedItem
            }
        }

        var article = this.m_app.GetSelectedArticle()

        if (article == null) {
            Assert(false, "BDKalkCore.GetExtData() was called while no article was currently selected!")
            return null
        }
        
        if ((article.GetNobb() == "") || (article.GetNobb() == null)) {
            this.m_extDataForSelectedItem = this.m_defaultExtDataForArticlesMissingIt
            return this.m_defaultExtDataForArticlesMissingIt
        }

        if (article.GetIfHasExtData() != null) {
            this.m_extDataForSelectedItem = article.GetIfHasExtData()
            return this.m_extDataForSelectedItem
        }

        return null
    }

    // This handler is triggered when the client recives ext data material for the currently selected article 
    onIncomingExtData(data: string) {
        var extD = new ExtDataGW()
        var hog: any = JSON.parse(data)
        extD.RecieveData(hog)
        var handlerShouldBeCalled: boolean = false
        var selectedArticle = this.GetApp().GetSelectedArticle()
        // Checking if the  currently selected article still matches the requested data
        if (selectedArticle && extD.GetNobbNr().toString() == selectedArticle.GetNobb()) {
            // Seting the pricebook id for this extData according to the pricebook of the currently selected article
            extD.SetSelectionLabel(selectedArticle.GetSelectionLabel())

            // Secondly checks if the current ext data for selected article needs to be updated or if it is already valid
            if (this.m_extDataForSelectedItem != null) {
                if (selectedArticle.GetNobb() != this.m_extDataForSelectedItem.GetNobbNr().toString()) {
                    this.m_extDataForSelectedItem = extD
                    handlerShouldBeCalled = true
                }
            }
            else {
                this.m_extDataForSelectedItem = extD
                handlerShouldBeCalled = true
            }
        }
        else {
            // Assert(false,"core.onIncomingExtData() - it looks like the request for the extended data is no longer about the currently seleceted article, are the requests being made very fast here or is the internet connection being too slow for them? ")
        }

        if (handlerShouldBeCalled && selectedArticle) {
            extD.DirectSubChoise(selectedArticle) // Initiates the default choise of supplier and package type
            this.m_extDataLoadCompletionHandler()
        }
    }

    // Actions upon a loaded Ext Data package 
    onDefaultExtDataIsReady() {
        this.GetExtInfoView().forceUpdate() // (an update will be enough to have the ext data shown as it is available)
    }

    // A check if ext data is active for the selected item, without carrying out any request for it
    HasActiveExtData() {
        var article = this.m_app.GetSelectedArticle()

        if (article == null) {
            return false
        }

        if (this.m_extDataForSelectedItem != null) {
            if (this.m_extDataForSelectedItem.GetNobbNr().toString() == article.GetNobb()) {
                return true
            }
        }

        return false
    }

    // Initial referencing to the titlebar of Qreport
    ConnectQReportTitle(qrt: QReportTitleGW) {
        this.m_QReportTitle = qrt
    }

    // Initial referencing to the titlebar of the BDKalk page
    ConnectTotalDisplay(display: TotalDisplayGW) {
        this.m_totalDisplay = display
    }

    // Initial referencing to the present loading screen being mounted 
    ConnectLoadingView(loadView: LoadingView) {
        this.m_loadingView = loadView
    }

    // Initial referencing to the log view being mounted 
    ConnectLogView(logView: LogView) {
        this.m_logView = logView
    }

    // Initial referencing to the titlebar of the BDKalk page
    UpdateExtInfoViewHandle(view: ExtInfoViewGW) {
        this.m_extInfoView = view
    }

    // During data load, tells how much has already been loaded, to be displayed as a percentage bar on the loading screen
    UpdateLoadPercentage(perc: number, group: string) {
        this.m_dataLoadPercentage = perc
        this.m_dataLoadGroup = group
        
        if (this.m_loadBarHandle != null) {
            if (this.m_loadBarIsMounted == true) {
                this.m_loadBarHandle.forceUpdate()
            }
        }
    }

    // Returns the percentage of all groups that are loaded OR the groups within the currently selected nobb cathegory
    GetLoadPercentage() {
        if (this.m_loadBarHandle != null) {
            if (this.IsShowingAllPriceBooks() == true) {
            
                if (this.m_loadBarHandle.props.allCath == true) {
                    return this.m_dataLoadPercentage
                }
                else {
                    // Here we will look for how many child groups are within the selected nobb cathegory, and return a percentage
                    // for the loaded ones.
                    var loadedContentGroups = 0
                    var contentGroups = 0

                    for (var book = 0; book < this.m_priceBooks.length; book++) {
                        var alphabeticalGroupsLocal = this.m_priceBooks[book].GetAlphabeticalGroups(false)

                        if (alphabeticalGroupsLocal == null) {
                            continue
                        }

                        for (var i = 0; i < alphabeticalGroupsLocal.length; i++) {
                            if (this.isWithinNobb(this.m_lastSelectedNobbGroup, alphabeticalGroupsLocal[i].GetGroupID())) {
                                contentGroups++

                                if (alphabeticalGroupsLocal[i].GetLoadedStatus().status == 2) {
                                    loadedContentGroups++
                                }
                            }
                        }
                    }
                    
                    if (contentGroups == 0) {
                        return 1.5 // If there are no groups under this nobb cathegory (which should not happen but anyway) then a divide with 0 will be avoided this way
                    }
                    else {
                        return 0.00001 + (loadedContentGroups / contentGroups)
                    }
                }
            }
            else {
                if (this.m_loadBarHandle.props.allCath == true) {
                    return this.m_dataLoadPercentage
                }
                else {
                    if (this.m_lastSelectedNobbGroup == "withoutNobb") {
                        var currentBook = this.GetSelectedPriceBook()

                        if (currentBook) {
                            return currentBook.GetNobbUnspecifiedLoadPercentage()
                        }
                    }
                    
                    // Here we will look for how many child groups are within the selected nobb cathegory, and return a percentage
                    // for the loaded ones.
                    var loadedContentGroups = 0
                    var contentGroups = 0
                    var alphabeticalGroups = this.GetAlphabeticalGroups(false);

                    if (alphabeticalGroups == null) {
                        return 0.00001
                    }

                    for (var i = 0; i < alphabeticalGroups.length; i++) {
                        if (this.isWithinNobb(this.m_lastSelectedNobbGroup, alphabeticalGroups[i].GetGroupID())) {
                            contentGroups++
                            if (alphabeticalGroups[i].GetLoadedStatus().status == 2) {
                                loadedContentGroups++
                            }
                        }
                    }

                    if (contentGroups == 0) {
                        return 1.5 // If there are no groups under this nobb cathegory (which should not happen but anyway) then a divide with 0 will be avoided this way
                    }
                    else {
                        return 0.00001 + (loadedContentGroups / contentGroups)
                    }
                }
            }
        }

        return -1
    }

    // Returns the percentage of articles in utvalg which has extended data loaded and attached to it. 
    // 100% means quick report can be calculated and displayed, as this depend on the package data within extData
    GetQReportReadinessPercentage() {
        var tot = this.m_selectedArticles.length

        if (tot == 0) {
            return 1.00001
        }

        var loaded = 0

        for (var i = 0; i < this.m_selectedArticles.length; i++) {
            if (this.m_selectedArticles[i].GetIfHasExtData() != null) {
                loaded++
            }
        }

        return 0.00001 + (loaded / tot)
    }

    // Returns a string telling how many percentage of the data has been loaded, with two decimals
    GetQReportReadinessPercentageString() {
        var lo = ""
        var perc = this.GetQReportReadinessPercentage()
        var ad = (perc * 100.0).toString()
        
        var decims = 0

        for (var i = 0; i < ad.length; i++) {
            if (ad.charAt(i) == '.') {
                decims = 1
                lo += ","
            }
            else {
                if (decims == 0) {
                    lo += ad.charAt(i)
                }
                else {
                    if (decims == 3) {
                        lo += "%"
                        break
                    }
                    else {
                        decims++
                        lo += ad.charAt(i)
                    }
                }
            }
        }

        return lo
    }

    // IsShowingAllPriceBooks returns true if the "All Pricebooks" option is chosen in the price book dropdown list.
    // (this is only an option if there are more than one price book made available for the user account)
    IsShowingAllPriceBooks() {
        if (this.GetSelectedPriceBookID() == -1) {
            return true
        }

        return false
    }

    // Handler for general keydown events on the entire page (App)
    HandleKeyDown(key: string) {
        var selectedArticleItem = this.GetSelectedArticleItem();
        var selectedSelectionItem = this.GetSelectedSelectionItem();

        if (key == "Tab") {
            this.m_functionalKeyPressed = "Tab"
            setTimeout(this.RunKeyEffect.bind(this), 90)

            this.ProcessInputs()
        }

        if (key == "Enter") {
            this.ProcessInputs()
        }

        if (key == "ArrowDown") {
            this.m_functionalKeyPressed = "ArrowDown"
            setTimeout(this.RunKeyEffect.bind(this), 90)

            this.ProcessInputs()
        }

        if (key == "ArrowUp") {
            this.m_functionalKeyPressed = "ArrowUp"
            setTimeout(this.RunKeyEffect.bind(this), 90)

            this.ProcessInputs()
        }

        if (key == "Control") {
            this.m_functionalKeyPressed = "Control"
            setTimeout(this.RunKeyEffect.bind(this), 90)
        }
    }

    // Goes through all possible item inputs an makes sure any value being typed in is put to use as is
    ProcessInputs() {
        // Processing an eventual ongoing input in the Article view
        if (this.m_inputItemArticleView != null) {
            const old = this.m_inputItemArticleView
            this.m_inputItemArticleView = null
            old.m_choise.processInput()
            const selected = this.GetSelectedArticleItem();
            if (selected != null) selected.forceUpdate()
        }

        // Processing an eventual ongoing article input in the Selection view
        if (this.m_inputItemSelectionView != null) {
            const old = this.m_inputItemSelectionView
            this.m_inputItemSelectionView = null
            old.m_choise.processInput()
            const selected = this.GetSelectedSelectionItem();
            if (selected != null) selected.forceUpdate()
        }

        // Processing an eventual ongoing buildpart input in the Selection view
        if (this.m_inputItemBuildPartQRView != null) {
            const old = this.m_inputItemBuildPartQRView
            this.m_inputItemBuildPartQRView = null
            old.m_choiseCtrl.processInput()
            const selected = this.GetSelectedQRBuildPartItem();
            if (selected != null) selected.forceUpdate()
        }

        // Processing an eventual ongoing buildpart input in the buildpart view
        if (this.m_inputItemBuildPartView != null) {
            const old = this.m_inputItemBuildPartView
            this.m_inputItemBuildPartView = null
            old.m_choiseCtrl.processInput()
            const selected = this.GetSelectedBuildPartItem();
            if (selected != null) selected.forceUpdate();
        }
    }

    // Handler for general keyup events on the entire page (App)
    HandleKeyUp(key: string) {
    }

    // StartSearchBeat is triggered whenever the user types into the search field, and starts the countdown before a search is performed
    StartSearchBeat(currentValue: string) {
        if (this.m_searchCallChecks <= 0) {
            this.m_searchCallChecks = searchDelayBeats - 2
            this.m_recentSearchInput = currentValue
            setTimeout(this.SearchBeat.bind(this), searchDelayLength)
        }
        else {
            if (this.m_searchCallChecks < searchDelayBeats) {
                this.m_searchCallChecks = searchDelayBeats + 1
            }
        }
    }
    
    // Continuous countdown until a search will be begun after the user stopped typing into the search field
    SearchBeat() {
        this.m_searchCallChecks--

        if (this.m_searchCallChecks <= 0) {
            return
        }

        if (this.m_searchCallChecks == 1) {
            this.m_searchCallChecks = 0
            this.PerformSearchCheck()
            return
        }
        
        setTimeout(this.SearchBeat.bind(this), searchDelayLength)
    }

    /* PerformSearchCheck carries out a search after keys were entered and then a time has passed
     * since the user last typed a key into the search field. */
    PerformSearchCheck() {
        const currentValue = this.m_app.GetCurrentSearchInputValue()

        if (this.m_recentSearchInput != currentValue) {
            this.m_recentSearchInput = currentValue
            
            if (this.m_app.state.cathegoricSearch == true) {
                var lastGroup = this.GetLastSelectedGroup()
                if (lastGroup != "") {
                    this.RunSearch(currentValue, this.GetArticlesByGroup(lastGroup))
                }
                else {
                    this.RunSearch(currentValue)
                }
            }
            else {
                this.RunSearch(currentValue)
            }
        }
    }

    // Carries out a search for keywords in the string towards the contents of the provided articles and triggers a search result to be shown
    RunSearch(input: string, searchedData?: Array<ArticleGW>) {
        var scoop: ArticleGW[]

        if (searchedData) {
            scoop = searchedData
        }
        else {
            if (this.IsShowingAllPriceBooks() == true) {
                scoop = this.m_allPriceBooksFullData
            }
            else {
                var full = this.GetFullData()
                if (full) {
                    scoop = full
                }
                else {
                    Assert(false, "core.RunSearch - GetFullData() returned undefined! ")
                    scoop = new Array<ArticleGW>()
                }
            }
        }

        if (scoop) {
            var searchCall = new SearchCall(input, scoop, this.BeginNewSearch(), this.GetSearchCallIndex.bind(this), this.OnSearchCompleted.bind(this), this.GetFilterFlags(), this)
            searchCall.BeginSearch()
        }
    }

    // Handler for search results being ready 
    OnSearchCompleted(result: Array<ArticleGW>) {
        PADebug.Log("   --- core.OnSearchCompleted . result.length:" + result.length)
        this.SetDisplayedArticles(result)
        this.m_app.setArticlesPageSearch()
    }
    
    /* This method is called whenever the user types enter while the search field has focus.
     * The search text will be evaluated and used to gather and display a search result  */
    HandleSearchInputOld(input: string, searchCall: number, searchedData?: Array<ArticleGW>) {
        var scoop: ArticleGW[]

        if (searchedData) {
            scoop = searchedData
        }
        else {
            if (this.IsShowingAllPriceBooks() == true) {
                scoop = this.m_allPriceBooksFullData
            }
            else {
                var full = this.GetFullData()
                if (full) {
                    scoop = full
                }
                else {
                    Assert(false, "core.HandleSearchInput - GetFullData() returned undefined! ")
                    scoop = new Array<ArticleGW>()
                }
            }
        }
        
        // Transforming a search command into an array of search words, that was separated by blankspaces, up to a maximum of 6 words
        var pickedWord = ""
        var readWordAmount = 0
        var wordArray = ["", "", "", "", "", ""] // defining the resulting array

        for (var readPosition = 0, currentPos = 0; (readPosition < input.length) && (readWordAmount < 6) && this.IsLatestSearch(searchCall); readPosition++ , currentPos++) {
            if (input[readPosition] != " ") {
                pickedWord += input[readPosition]
            }
            else {
                wordArray[readWordAmount] = pickedWord
                readWordAmount++
                pickedWord = ""
                currentPos = 0
            }
        }

        // if reaching the end of the string, the current read characters will also(or alone) become a word in the array
        if (pickedWord != "") {
            if (readWordAmount < 6) {
                wordArray[readWordAmount] = pickedWord
                readWordAmount++
            }
        }
        
        if (scoop) {
            for (var g = 0; (g < wordArray.length) && (scoop.length > 0) && this.IsLatestSearch(searchCall); g++) {
                if (wordArray[g] != "") {
                    var within = this.SearchWithin(scoop, wordArray[g], searchCall)
                    if (within) {
                        scoop = within
                    }
                }
                else {
                    break
                }
            }

            if (this.IsLatestSearch(searchCall)) {
                this.SetDisplayedArticles(scoop)
            }
        }

        if (this.IsLatestSearch(searchCall)) {
            this.m_app.setArticlesPageSearch()
        }
    }

    // This is called when the user leaves a 'page' of displayed articles, it performs and resets any active amount inputs
    InactivateInputs() {
        this.FinishOngoingInput() // This will actually kill the thread if there's still an active input, but it will anyway leave the following values as null:
    }

    // SearchWithin returns the result from searching for a string within an amount of articles, where the string is assumed to be one word
    SearchWithin(scoop: Array<ArticleGW> | null, word: string, searchCall : number) {
        if (scoop == null) {
            return null
        }

        var searchResult: Array<ArticleGW> = new Array<ArticleGW>()

        for (var i = 0; (i < scoop.length) && this.IsLatestSearch(searchCall); i++) {
            if (this.Match(scoop[i], word, searchCall) == true) {
                searchResult.push(scoop[i])
            }
        }

        if (this.IsLatestSearch(searchCall)) {
            return searchResult
        }
        else {
            return null
        }
    }

    // Match determines if a string can be found within an article. Match is used for searches.
    Match(article: ArticleGW, textCheck: string, searchCall: number) {
        var failedSub = false
        var testedContent = article.GetDescription().toLowerCase()
        var i = 0, j = 0
        var text = textCheck.toLowerCase()
        const flags = this.m_filterFlags

        // if no characters are to be compared, then no match can be confirmed.
        if (text.length == 0) {
            return false
        }

        // Search check : Description
        if ((flags & 1) != 0) {
            // Performing a wordcheck towards the description(1) text
            for (i = 0, j = 0; i < testedContent.length && this.IsLatestSearch(searchCall); i++) {
                failedSub = false

                // Checking if the word is too large to be contained by a possible match from this position
                if ((i + text.length) > testedContent.length) {
                    break // interrupting the wordcheck
                }

                // testing the word from an iterated position in the description text
                for (j = 0; (j < text.length) && (failedSub == false); j++) {
                    if (text[j] != testedContent[i + j]) {
                        failedSub = true
                    }
                }

                // If there's no failed character comparations, a match has been found.
                if (failedSub == false) {
                    return true // 
                }
            }
        }

        // Search check : Description2
        if ((flags & 2) != 0) {
            testedContent = article.GetDescription2().toLowerCase()

            // Performing an equal wordcheck towards the description2 text
            for (i = 0, j = 0; i < testedContent.length && this.IsLatestSearch(searchCall); i++) {
                failedSub = false

                // Checking if the word is too large to be contained by a possible match from this position
                if ((i + text.length) > testedContent.length) {
                    break // interrupting the wordcheck
                }

                // testing the word from an iterated position in the description text
                for (j = 0; (j < text.length) && (failedSub == false); j++) {
                    if (text[j] != testedContent[i + j]) {
                        failedSub = true
                    }
                }

                // If there's no failed character comparations, a match has been found.
                if (failedSub == false) {
                    return true // 
                }
            }
        }

        // Search check : Article ID
        if ((flags & 4) != 0) {
            testedContent = article.GetIdentifier().toLowerCase()

            // Performing an equal wordcheck towards the description2 text
            for (i = 0, j = 0; i < testedContent.length && this.IsLatestSearch(searchCall); i++) {
                failedSub = false

                // Checking if the word is too large to be contained by a possible match from this position
                if ((i + text.length) > testedContent.length) {
                    break // interrupting the wordcheck
                }

                // testing the word from an iterated position in the description text
                for (j = 0; (j < text.length) && (failedSub == false); j++) {
                    if (text[j] != testedContent[i + j]) {
                        failedSub = true
                    }
                }

                // If there's no failed character comparations, a match has been found.
                if (failedSub == false) {
                    return true // 
                }
            }
        }

        // Search check : Nobb number
        if ((flags & 8) != 0) {
            testedContent = article.GetNobb()

            // Looks for a match with the article's nobb number
            for (i = 0, j = 0; i < testedContent.length && this.IsLatestSearch(searchCall); i++) {
                failedSub = false

                // Checking if the word is too large to be contained by a possible match from this position
                if ((i + text.length) > testedContent.length) {
                    break // interrupting the wordcheck
                }

                // testing the word from an iterated position in the description text
                for (j = 0; (j < text.length) && (failedSub == false); j++) {
                    if (text[j] != testedContent[i + j]) {
                        failedSub = true
                    }
                }

                // If there's no failed character comparations, a match has been found.
                if (failedSub == false) {
                    return true // 
                }
            }
        }

        // Search check : Nobb owner number
        if ((flags & 16) != 0) {
            testedContent = article.GetNobbOwner()

            // Looks for a match with the article's nobb number
            for (i = 0, j = 0; i < testedContent.length && this.IsLatestSearch(searchCall); i++) {
                failedSub = false

                // Checking if the word is too large to be contained by a possible match from this position
                if ((i + text.length) > testedContent.length) {
                    break // interrupting the wordcheck
                }

                // testing the word from an iterated position in the description text
                for (j = 0; (j < text.length) && (failedSub == false); j++) {
                    if (text[j] != testedContent[i + j]) {
                        failedSub = true
                    }
                }

                // If there's no failed character comparations, a match has been found.
                if (failedSub == false) {
                    return true // 
                }
            }
        }

        // Search check : Ean number
        if ((flags & 32) != 0) {
            testedContent = article.GetEanNo(null)

            // Looks for a match with the article's nobb number
            for (i = 0, j = 0; i < testedContent.length && this.IsLatestSearch(searchCall); i++) {
                failedSub = false

                // Checking if the word is too large to be contained by a possible match from this position
                if ((i + text.length) > testedContent.length) {
                    break // interrupting the wordcheck
                }

                // testing the word from an iterated position in the description text
                for (j = 0; (j < text.length) && (failedSub == false); j++) {
                    if (text[j] != testedContent[i + j]) {
                        failedSub = true
                    }
                }

                // If there's no failed character comparations, a match has been found.
                if (failedSub == false) {
                    return true // 
                }
            }
        }

        // Search check : Cathegory - within titles of containing nobb groups and super groups
        if ((flags & 64) != 0) {
            var cathWords = article.GetCathegoricWords()
            
            testedContent = ""

            if (cathWords) {
                testedContent = cathWords
                PADebug.Log("core.match - found cathegorical words:" + cathWords)
            }
            else {
                const testedGroupId = article.GetGroupID()

                var books = this.GetPriceBooks()

                for (var b = 0; b < books.length && this.IsLatestSearch(searchCall); b++) {
                    const book = books[b]

                    if (book.GetInitStage() >= 2) {
                        const space = book.GetHierarchialGroups()

                        if (space) {
                            for (var y = 0; y < space.length && this.IsLatestSearch(searchCall); y++) {
                                if (space[y].doesInclude(testedGroupId)) {
                                    testedContent += space[y].GetDescription()
                                }
                            }
                        }
                    }
                }

                testedContent = testedContent.toLowerCase()
                PADebug.Log("core.match - will set cathegorical words:" + testedContent)
                article.SetCathegoricWords(testedContent)
            }
            
            // Looks for a match with the article's nobb number
            for (i = 0, j = 0; i < testedContent.length && this.IsLatestSearch(searchCall); i++) {
                failedSub = false

                // Checking if the word is too large to be contained by a possible match from this position
                if ((i + text.length) > testedContent.length) {
                    break // interrupting the wordcheck
                }

                // testing the word from an iterated position in the description text
                for (j = 0; (j < text.length) && (failedSub == false); j++) {
                    if (text[j] != testedContent[i + j]) {
                        failedSub = true
                    }
                }

                // If there's no failed character comparations, a match has been found.
                if (failedSub == false) {
                    return true // 
                }
            }
        }

        // Supplier searches are not activated. reason is that the name is found in 
        // the extended data for the article and not within the article (ArticeGW), so searches
        // among artiles that has not got any ext info loeaded (mostly the case) won't find any 
        // supplier names, as things are presently. Searches for nobbOwner however, does just that
        // since the nobb owner id number is tied to the supplier, though it's a number rather than 
        // a company name, but otherwise it does the trick just fine and replaces this check for now. 
        if ((flags & 128) != 0) {
            testedContent = article.GetSupplier().toLowerCase()

            // Looks for a match with the article's nobb number
            for (i = 0, j = 0; i < testedContent.length && this.IsLatestSearch(searchCall); i++) {
                failedSub = false

                // Checking if the word is too large to be contained by a possible match from this position
                if ((i + text.length) > testedContent.length) {
                    break // interrupting the wordcheck
                }

                // testing the word from an iterated position in the description text
                for (j = 0; (j < text.length) && (failedSub == false); j++) {
                    if (text[j] != testedContent[i + j]) {
                        failedSub = true
                    }
                }

                // If there's no failed character comparations, a match has been found.
                if (failedSub == false) {
                    return true // 
                }
            }
        }

        // If there were a match, it would have been found at this point, so the result will be negative
        return false
    }

    // This is a help method used within GenerateGroups() below.
    AddParentGroup(title: string, identifier: string) {
        /* What is supposed to happen here is that a none bottom level group
         * is generated for the NOBB hierarchial tree, it can be at highest level (1)
         * or mid level (2). These higher leveled group separators will then be added
         * to the array of nobb groups, and as that array afterwards get sorted, the
         * parrent groups will naturally find their positions in the tree structure,
         * based on their identifier value. Clicking a parent group will also
         * reveal the entire selection of articles belonging under that parent group. */
        var hierarchialGroups = this.GetHierarchialGroups();
        if (hierarchialGroups != null) {
            if (identifier.length == 2) {
                // this is a top level (1) group.
                var idMin = identifier + "00000"
                var idMax = identifier + "99999"
                var group = new ArticleGroupGW(idMin, idMax, identifier + " " + this.GetNordic(title), 1, this.GetNordic(title))
                hierarchialGroups.push(group)
            }

            if (identifier.length == 4) {
                // this is a mid level (2) group.
                var idMin = identifier + "000"
                var idMax = identifier + "999"
                var group = new ArticleGroupGW(idMin, idMax, identifier + " " + this.GetNordic(title), 2, this.GetNordic(title))
                hierarchialGroups.push(group)
            }
        }
    }

    // Renders the selectionn of items as a list and possibly with custom supplier and package choises within it 
    public RenderOrders(shouldConvert: boolean, fpakSum: { sum: number }) {

        var priceBookSections = new Array<JSX.Element>()
        var articles = this.GetSelectedArticles()

        if (this.m_printableArticleSource != null) {
            articles = this.m_printableArticleSource
        }
        else {
            Assert(false, "core.RenderOrders - missing collection of articles for displaying and printing!")
        }

        for (var p = 0; p < this.m_priceBooks.length; p++) {
            var bookSpecificArticles = new Array<ArticleGW>()

            for (var a = 0; a < articles.length; a++) {
                if (articles[a].GetPriceBook() == this.m_priceBooks[p].GetPriceBookId()) {
                    bookSpecificArticles.push(articles[a])
                }
            }

            if (bookSpecificArticles.length > 0) {
                var elements: Array<JSX.Element> = new Array<JSX.Element>()

                for (var i = 0; i < bookSpecificArticles.length; i++) {
                    elements[i] = bookSpecificArticles[i].RenderOrders(this.m_priceRadioPos, this.m_priceFlags, shouldConvert, fpakSum)
                }

                priceBookSections.push(<div key={this.GetUniqueKey()} style={{ display: "flex", flexDirection: "row" }}>
                    <div key={this.GetUniqueKey()} style={{ marginLeft: '20px', height: "28px", fontWeight: "bold" }} >Prisbok: </div>
                    <div style={{ width: "20px" }}></div>
                    <div key={this.GetUniqueKey()} style={{ height: "28px" }} >{this.m_priceBooks[p].GetTitle()}</div>
                </div>)

                priceBookSections.push(< div key={this.GetUniqueKey()} className="pageSection" style={{ height: "26px", width: "100%", display: "flex", flexDirection: "row", borderTop: "1px solid #B9B9B9", borderBottom: "1px solid #B9B9B9", font: "bold" }}>

                    {/* Central dynamic columns area */}
                    <div style={{ width: "calc(100% - 20px)", display: 'flex', flexDirection: 'row', marginLeft: '20px' }} >
                        <div style={{ width: "8%", position: "relative", top: "6px", fontWeight: "bold" }}>Varenr</div>
                        <div style={{ width: "10%", position: "relative", top: "6px", textAlign: "right", fontWeight: "bold" }}>Antall</div>

                        <div style={{ width: "3%" }}>
                        </div>

                        <div style={{ width: "8%", position: "relative", top: "6px", fontWeight: "bold" }}>Enhet</div>
                        <div style={{ width: "8%", position: "relative", top: "6px", fontWeight: "bold", textAlign: "right" }}>pris/enh.</div>
                        <div style={{ width: "10%", position: "relative", top: "6px", textAlign: "right", fontWeight: "bold" }}>Pris (NOK)</div>

                        <div style={{ width: "3%" }}>
                        </div>

                        <div style={{ width: "52%", position: "relative", top: "6px", fontWeight: "bold" }}>Betegnelse</div>
                        <div style={{ width: "0%", position: "relative", top: "6px" }}></div>
                    </div>
                </div>)

                priceBookSections.push(<div key={this.GetUniqueKey()}>{elements}</div>)

                priceBookSections.push(<div key={this.GetUniqueKey()} style={{ height: '19px' }} ></div>) // margin before nest pricebook
            }
        }

        if (priceBookSections.length > 0) {
            return <div>{priceBookSections}</div>
        }
        else {
            return <div></div>
        }
    }

    // Collects the selected articles and contents from all selected build parts into one array of separate articles, 
    // adds amount values, loads extended data where it's missing, then triggers a given handler with the array as a parameter
    public GenerateArticleSummary() {
        this.m_printableArticlesReady = false
        this.m_printableArticlesLoadIndex = 0
        var arts = new Array<ArticleGW>()
        // var exts = new Array<ExtDataGW | null>()

        for (var i = 0; i < this.m_selectedArticles.length; i++) {
            const art = this.m_selectedArticles[i]

            arts[i] = new ArticleGW(art.GetArticleID(), art.GetEanNo(null), art.GetIdentifier(), art.GetSupplyIdentifierOld(), art.GetGroupID(),
                art.GetDescription(), art.GetDescription2(), art.GetDescriptionUrl(), art.GetWebsiteUrl(), art.GetImageUrl(), art.GetAmountPerPackage(),
                art.GetUnit(), art.GetPurchasePrize(), art.GetBasePrize(), art.GetRecommendedPrize(), art.GetFactoryPrize(), art.GetPackageUnit(), "NOK", art.GetNobb(), art.GetNobbOwner(),
                art.GetNobbModule(), art.GetPrizeType(), art.GetType(), art.GetSupplier(), art.GetAmount(), art.GetDiscountFactor(), art.GetPriceBook())

            arts[i].SetExtData(art.GetIfHasExtData())
        }

        this.m_printableArticleSource = arts

        for (var bp = 0; bp < this.m_selectedBuildParts.length; bp++) {
            this.GenerateSummaryBPRecursion(this.m_selectedBuildParts[bp], this.m_selectedBuildParts[bp].GetAmount())
        }

        this.GenerateSummaryLoadNextExtData()
    }

    // recursively collecting article amounts owned by build parts and their included build parts into a collection for a printable summary
    GenerateSummaryBPRecursion(part: BuildPartGW, multiplier: number) {
        this.GenerateSummaryArticleAdd(part.GetArticles(), multiplier)
        const subs = part.GetIncludedBuildParts()

        for (var i = 0; i < subs.length; i++) {
            this.GenerateSummaryBPRecursion(subs[i].ref, multiplier * subs[i].amount)
        }
    }

    // Help function for calculating the content amounts and prices within build parts, used for creating a quick report summary
    GenerateSummaryArticleAdd(articles: Array<ArticleGW>, multiplier: number) {
        var addedToAmount: boolean

        var state = "none"

        if (this.m_printableArticleSource == null) {
            Assert(false, "core.GenerateSummaryArticleAdd - missing article source array!")
            return
        }

        for (var a = 0; a < articles.length; a++) {
            addedToAmount = false

            for (var i = 0; i < this.m_printableArticleSource.length; i++) {
                if (this.m_printableArticleSource[i] != null) {
                    if (this.m_printableArticleSource[i].GetIdentifier() == articles[a].GetIdentifier()) {
                        addedToAmount = true
                        this.m_printableArticleSource[i].AddAmount(articles[a].GetAmount() * multiplier)
                    }
                }
            }

            if (addedToAmount == false) {
                const art = articles[a]
                const newPos = this.m_printableArticleSource.length

                this.m_printableArticleSource[newPos] = new ArticleGW(art.GetArticleID(), art.GetEanNo(null), art.GetIdentifier(), art.GetSupplyIdentifierOld(), art.GetGroupID(),
                    art.GetDescription(), art.GetDescription2(), art.GetDescriptionUrl(), art.GetWebsiteUrl(), art.GetImageUrl(), art.GetAmountPerPackage(),
                    art.GetUnit(), art.GetPurchasePrize(), art.GetBasePrize(), art.GetRecommendedPrize(), art.GetFactoryPrize(), art.GetPackageUnit(), "NOK", art.GetNobb(), art.GetNobbOwner(),
                    art.GetNobbModule(), art.GetPrizeType(), art.GetType(), art.GetSupplier(), art.GetAmount() * multiplier, art.GetDiscountFactor(), art.GetPriceBook())

                this.m_printableArticleSource[newPos].SetExtData(art.GetIfHasExtData())
            }
        }
    }

    // Part of the ext data loading process for buld part owned articles, prior to displaying a selected article summary (for printing)
    GenerateSummaryLoadNextExtData() {
        if (this.m_printableArticleSource == null) {
            return
        }

        if (this.m_printableArticleSource.length <= this.m_printableArticlesLoadIndex) {
            this.OnSummaryGenerated()
            return
        }

        if (this.m_printableArticleSource[this.m_printableArticlesLoadIndex] != null) {
            if (this.m_printableArticleSource[this.m_printableArticlesLoadIndex].GetIfHasExtData() == null) {
                LoadFromUrl(this.m_serverUrl + "api/price/nobb/" + this.m_printableArticleSource[this.m_printableArticlesLoadIndex].GetNobb().toString(), this.OnExtDataLoadedForSummaryGeneration.bind(this), this.DefaultLoadErrorHandler.bind(this), this.GetApp().state.accessToken)
                return
            }
        }

        this.m_printableArticlesLoadIndex++
        setTimeout(this.GenerateSummaryLoadNextExtData.bind(this), 5)
    }

    // Part of the ext data loading process for buld part owned articles, prior to displaying a selected article summary (for printing)
    OnExtDataLoadedForSummaryGeneration(data: string) {
        var extD = new ExtDataGW()
        var hog: any = JSON.parse(data)
        extD.RecieveData(hog)

        if (this.m_printableArticleSource != null) {
            if (this.m_printableArticleSource[this.m_printableArticlesLoadIndex] != null) {
                this.m_printableArticleSource[this.m_printableArticlesLoadIndex].SetExtData(extD)
                extD.DirectSubChoise(this.m_printableArticleSource[this.m_printableArticlesLoadIndex]) // Initiates the default choise of supplier and package type
                this.m_printableArticleSource[this.m_printableArticlesLoadIndex].ApplyLocalAmount()
            }
        }

        this.m_printableArticlesLoadIndex++
        setTimeout(this.GenerateSummaryLoadNextExtData.bind(this), 5)
    }

    // Positive end sequence of the ext data loading process for buld part owned articles, prior to displaying a selected article summary (for printing)
    OnSummaryGenerated() {
        if (this.m_summaryView != null) {
            this.m_summaryView.OnArticleSummaryLoaded()
        }
    }

    // generates a numeric value which has not yet been used as key within arrays of graphical elements
    GetUniqueKey() {
        const key = this.m_indexForUniqueKeys++
        return key
    }
   
    // Renders the unspecified cathegory at the top in the tree to the left if there are nobb unspecified contents to show within the pricebook
    RenderNobbUnspecifiedArticlesTree(tree: TreeViewGW, itemWidth: string, itemHeight: string) {
        var book = this.GetSelectedPriceBook()

        if (!book) {
            return <div key={this.GetUniqueKey()}></div>
        }
        
        // var elements: Array<{ element: JSX.Element }> = new Array < { element: JSX.Element }>()
        var nobblessGroups = book.GetNobbUnspecifiedGroups(true)

        if (nobblessGroups == null) {
            return <div key={this.GetUniqueKey()}>
                <div>Inhenter data...</div>
            </div>
        }

        if (nobblessGroups.length == 0) {
            // There are no articles to be shown witin the nobb unspecified groups, so nothing will be shown, this also occurs 
            // when there are contents within the nobb specified groups, which replaces the unspecified groups if there are any
            return <div>
            </div>
        }

        var elements: Array<JSX.Element> = new Array<JSX.Element>()
        elements.length = nobblessGroups.length + 1 // initiating the length of the array to its final length for optimization

        /* Empty tree nodes make no sence, do they? So as the groups turn into tree items, let's check if the
         * groups hold any articles in them before they are added as tree nodes. The j below is incremented each time a
         * group is skipped, so there won't be any "holes" in the resulting array of graphical tree components
         * ((.. This exclusion has later been commented out due to a different startup procedure! ))*/

        var j = 0

        // First the child satus and stage are calculated
        var currentChildStatus = 0  // 0: has no parrent, 1: is child, 2: is last child
        var currentStage = 1  // 0: hidden under a collapsed parrent, 1: displayed, has no children , 2:displayed, has children
        var currentExpansionDeapth = 1  // this will follow the development of the tree and determine if eace items will be displayed
        var displayedItemsAmount = 0 // keeps count on how many items has been inserted into the tree view
        var ignores = 0 // The number of groups that was not turned into tree items (because they didn't hold any articles in them)

        for (var i = 0; i < nobblessGroups.length; i++) {
            const index = i

            if (i == 0) {
                // This is the first node in the tree
                if (nobblessGroups.length == 1) {
                    // this is also the last (only) item in the tree
                    currentChildStatus = 0 // has no parent
                    currentStage = 1 // has no children
                }
                else {
                    // This is the first item in the tree and has following items
                    currentChildStatus = 0 // has no parent
                    if (nobblessGroups[i + 1].GetLevel() > nobblessGroups[i].GetLevel()) {
                        currentStage = 2 // has children

                        if (tree.isExpanded(nobblessGroups[i].GetLevel(), i)) {
                            // this is the expanded/selected item on this hierarchial level, so the
                            // expansion deapth will increase into the child's level before next iteration
                            currentExpansionDeapth = nobblessGroups[i + 1].GetLevel()
                        }
                    }
                    else {
                        currentStage = 1 // has no children
                    }
                }
            }
            else { // This is not the first tree item

                if (nobblessGroups[i].GetLevel() > currentExpansionDeapth) {
                    // This item is hidden under a parent item and won't be displayed.
                    continue
                }

                if (nobblessGroups.length == i + 1) {
                    // This is the last tree item to be read
                    currentChildStatus = (nobblessGroups[i].GetLevel() >= 2) ? 2 : 0 // either last child (2) or not a child (0)
                    currentStage = 1 // displayed and has no children
                }
                else { // this tree item is not the first or the last one.
                    currentChildStatus = 0 // default (displayed as when not having a parent node)
                    if (nobblessGroups[i].GetLevel() >= 2) {
                        /* This item should have a parent, at least if the tree was properly structured in the json file,
                         * if not so however, then the worst outcome of that would be a slight graphical inperfection in
                         * how the tree item is displayed, while technically the tree view would still work as normally, but
                         * anyhow please follow the instruction above this class to structure the json file correctly   */
                        currentChildStatus = 1 // is a child

                        if (nobblessGroups[i].GetLevel() > nobblessGroups[i + 1].GetLevel()) {
                            // this is the last child of its parent node
                            currentChildStatus = 2 // is last child
                        }
                    }

                    // exits an expansion deapth when back at a higher level
                    if (currentExpansionDeapth > nobblessGroups[i].GetLevel()) {
                        currentExpansionDeapth = nobblessGroups[i].GetLevel()
                    }

                    if (nobblessGroups[i + 1].GetLevel() > nobblessGroups[i].GetLevel()) {
                        currentStage = 2 // has children

                        if (tree.isExpanded(nobblessGroups[i].GetLevel(), i)) {
                            // this is the expanded/selected item on this hierarchial level, so the
                            // expansion deapth will increase into the child's level before next iteration
                            currentExpansionDeapth = nobblessGroups[i + 1].GetLevel()
                        }
                    }
                    else {
                        currentStage = 1 // has no children
                    }
                }
            }

            elements[displayedItemsAmount] = (<TreeItemGW key={index} itemText={nobblessGroups[i].GetDescription()} level={nobblessGroups[i].GetLevel()} readingUrl={""} nobbGroup={nobblessGroups[i].GetGroupID()} width={itemWidth} height={itemHeight} app={this.m_app} tree={tree} itemID={i} childStatus={currentChildStatus} lastBorn={nobblessGroups[i].IsLastBorn(102)} stage={currentStage} ></TreeItemGW>)
            displayedItemsAmount++
        }
        
        elements.length = displayedItemsAmount // Reducing the unused indexes from the final result
        
        return <div key={this.GetUniqueKey()}>
            {elements}
        </div>
    }

    // Used for refreshing the views until the Tree view has been properly loaded and displays correctly
    RepeatUpdateViews() {
        this.m_app.forceUpdate()
    }

    // Renders an alphabetical tree of nobb groups for the tree view 
    RenderAlphabeticalTree(tree: TreeViewGW, itemWidth: string, itemHeight: string) {
        var alphabeticalGroups = this.GetAlphabeticalGroups(true);
        var showingNobbless = false
        
        if (alphabeticalGroups == null) {
            showingNobbless = true
        }
        else {
            if (alphabeticalGroups.length == 0) {
                showingNobbless = true
            }
        }

        if (showingNobbless == true) {
            var book = this.GetSelectedPriceBook()
            
            if (book) {
                return <div>
                    {this.RenderNobbUnspecifiedArticlesTree(tree, itemWidth, itemHeight)}
                </div>
            }
            else {
                return <div>
                    <div>Inhenter data...</div>
                </div>
            }
        }

        if (alphabeticalGroups == undefined) {
            // This cannot happen but the compiler needs to be sure alphabeticalGroups is not undefined further on so...
            return <div></div>
        }

        var elements: Array<JSX.Element> = new Array<JSX.Element>()
        elements.length = alphabeticalGroups.length + 1 // initiating the length of the array to its final length for optimization

        /* Empty tree nodes make no sence, do they? So as the groups turn into tree items, let's check if the
         * groups hold any articles in them before they are added as tree nodes. The j below is incremented each time a
         * group is skipped, so there won't be any "holes" in the resulting array of graphical tree components
         * ((.. This exclusion has later been commented out due to a different startup procedure! )) */
        
        // First the child satus and stage are calculated
        var currentChildStatus = 0  // 0: has no parrent, 1: is child, 2: is last child
        var currentStage = 1  // 0: hidden under a collapsed parrent, 1: displayed, has no children , 2:displayed, has children
        var currentExpansionDeapth = 1  // this will follow the development of the tree and determine if eace items will be displayed
        var displayedItemsAmount = 0 // keeps count on how many items has been inserted into the tree view

        for (var i = 0; i < alphabeticalGroups.length; i++) {
            const index = i

            if (i == 0) {
                // This is the first node in the tree
                if (alphabeticalGroups.length == 1) {
                    // this is also the last (only) item in the tree
                    currentChildStatus = 0 // has no parent
                    currentStage = 1 // has no children
                }
                else {
                    // This is the first item in the tree and has following items
                    currentChildStatus = 0 // has no parent
                    if (alphabeticalGroups[i + 1].GetLevel() > alphabeticalGroups[i].GetLevel()) {
                        currentStage = 2 // has children

                        if (tree.isExpanded(alphabeticalGroups[i].GetLevel(), i)) {
                            // this is the expanded/selected item on this hierarchial level, so the
                            // expansion deapth will increase into the child's level before next iteration
                            currentExpansionDeapth = alphabeticalGroups[i + 1].GetLevel()
                        }
                    }
                    else {
                        currentStage = 1 // has no children
                    }
                }
            }
            else { // This is not the first tree item

                if (alphabeticalGroups[i].GetLevel() > currentExpansionDeapth) {
                    // This item is hidden under a parent item and won't be displayed.
                    continue
                }

                if (alphabeticalGroups.length == i + 1) {
                    // This is the last tree item to be read
                    currentChildStatus = (alphabeticalGroups[i].GetLevel() >= 2) ? 2 : 0 // either last child (2) or not a child (0)
                    currentStage = 1 // displayed and has no children
                }
                else { // this tree item is not the first or the last one.
                    currentChildStatus = 0 // default (displayed as when not having a parent node)
                    if (alphabeticalGroups[i].GetLevel() >= 2) {
                        /* This item should have a parent, at least if the tree was properly structured in the json file,
                         * if not so however, then the worst outcome of that would be a slight graphical inperfection in
                         * how the tree item is displayed, while technically the tree view would still work as normally, but
                         * anyhow please follow the instruction above this class to structure the json file correctly   */
                        currentChildStatus = 1 // is a child

                        if (alphabeticalGroups[i].GetLevel() > alphabeticalGroups[i + 1].GetLevel()) {
                            // this is the last child of its parent node
                            currentChildStatus = 2 // is last child
                        }
                    }

                    // exits an expansion deapth when back at a higher level
                    if (currentExpansionDeapth > alphabeticalGroups[i].GetLevel()) {
                        currentExpansionDeapth = alphabeticalGroups[i].GetLevel()
                    }

                    if (alphabeticalGroups[i + 1].GetLevel() > alphabeticalGroups[i].GetLevel()) {
                        currentStage = 2 // has children

                        if (tree.isExpanded(alphabeticalGroups[i].GetLevel(), i)) {
                            // this is the expanded/selected item on this hierarchial level, so the
                            // expansion deapth will increase into the child's level before next iteration
                            currentExpansionDeapth = alphabeticalGroups[i + 1].GetLevel()
                        }
                    }
                    else {
                        currentStage = 1 // has no children
                    }
                }
            }

            const mode = (this.m_selectedPriceBookID == -1)?100:102

            elements[displayedItemsAmount] = (<TreeItemGW key={this.GetUniqueKey()} itemText={alphabeticalGroups[i].GetDescription()} level={alphabeticalGroups[i].GetLevel()} readingUrl={""} nobbGroup={alphabeticalGroups[i].GetGroupID()} width={itemWidth} height={itemHeight} app={this.m_app} tree={tree} itemID={i} childStatus={currentChildStatus} lastBorn={alphabeticalGroups[i].IsLastBorn(mode)} stage={currentStage} ></TreeItemGW>)
            displayedItemsAmount++
        }

        // Adding non nobb specified articles
        elements[displayedItemsAmount] = <div key={this.GetUniqueKey()}>{this.RenderNobbUnspecifiedArticlesTree(tree, itemWidth, itemHeight)}</div>
        displayedItemsAmount++
        elements.length = displayedItemsAmount // Reducing the unused indexes from the final result
        
        return <div>
            {elements}
        </div>
    }
    
    // Renders a hierarchial nobb groups tree for the tree view
    RenderHierarchyTree(tree: TreeViewGW, itemWidth: string, itemHeight: string) { 

        var elements: Array<JSX.Element> = new Array<JSX.Element>() // A holder to the tree items that shall be generated
        // var elements: Array<{ key: JSX.Element }> = new Array<{JSX.Element }>() // A holder to the tree items that shall be generated
        var hierarchialGroups = this.GetHierarchialGroups();

        if (hierarchialGroups == undefined) {
            setTimeout(this.RepeatUpdateViews.bind(this), 400)

            var book = this.GetSelectedPriceBook()
            
            if (book) {
                return <div>
                    {this.RenderNobbUnspecifiedArticlesTree(tree, itemWidth, itemHeight)}
                </div>
            }
            else {
                return <div>
                    <div>Inhenter data...</div>
                </div>
            }
        }

        elements[hierarchialGroups.length + 1] = <div key={-1}></div> // this forces the array to grow into a sufficient size at once (for optimization reasons)

        // First the child satus and stage are calculated
        var currentChildStatus = 0  // 0: has no parrent, 1: is child, 2: is last child
        var currentStage = 1  // 0: hidden under a collapsed parrent, 1: displayed, has no children , 2:displayed, has children
        var currentExpansionDeapth = 1  // this will follow the development of the tree and determine if eace items will be displayed
        var displayedItemsAmount = 0 // keeps count on how many items has been inserted into the tree view
        var ignores = 0 // The number of groups that was not turned into tree items (because they didn't hold any articles in them)

        for (var i = 0; i < hierarchialGroups.length; i++) {
            const index = i

            if (i == 0) {
                // This is the first node in the tree
                if (hierarchialGroups.length == 1) {
                    // this is also the last (only) item in the tree
                    currentChildStatus = 0 // has no parent
                    currentStage = 1 // has no children
                }
                else {
                    // This is the first item in the tree and has following items
                    currentChildStatus = 0 // has no parent
                    if (hierarchialGroups[i + 1].GetLevel() > hierarchialGroups[i].GetLevel()) {
                        currentStage = 2 // has children

                        if (tree.isExpanded(hierarchialGroups[i].GetLevel(), i)) {
                            // this is the expanded/selected item on this hierarchial level, so the
                            // expansion deapth will increase into the child's level before next iteration
                            currentExpansionDeapth = hierarchialGroups[i + 1].GetLevel()
                        }
                    }
                    else {
                        currentStage = 1 // has no children
                    }
                }
            }
            else { // This is not the first tree item

                if (hierarchialGroups[i].GetLevel() > currentExpansionDeapth) {
                    // This item is hidden under a parent item and won't be displayed.
                    continue
                }

                if (hierarchialGroups.length == i + 1) {
                    // This is the last tree item to be read
                    currentChildStatus = (hierarchialGroups[i].GetLevel() >= 2) ? 2 : 0 // either last child (2) or not a child (0)
                    currentStage = 1 // displayed and has no children
                }
                else { // this tree item is not the first or the last one.
                    currentChildStatus = 0 // default (displayed as when not having a parent node)
                    if (hierarchialGroups[i].GetLevel() >= 2) {
                        /* This item should have a parent, at least if the tree was properly structured in the json file,
                         * if not so however, then the worst outcome of that would be a slight graphical inperfection in
                         * how the tree item is displayed, while technically the tree view would still work as normally, but
                         * anyhow please follow the instruction above this class to structure the json file correctly   */
                        currentChildStatus = 1 // is a child

                        if (hierarchialGroups[i].GetLevel() > hierarchialGroups[i + 1].GetLevel()) {
                            // this is the last child of its parent node
                            currentChildStatus = 2 // is last child
                        }
                    }

                    // exits an expansion deapth when back at a higher level
                    if (currentExpansionDeapth > hierarchialGroups[i].GetLevel()) {
                        currentExpansionDeapth = hierarchialGroups[i].GetLevel()
                    }

                    if (hierarchialGroups[i + 1].GetLevel() > hierarchialGroups[i].GetLevel()) {
                        currentStage = 2 // has children

                        if (tree.isExpanded(hierarchialGroups[i].GetLevel(), i)) {
                            // this is the expanded/selected item on this hierarchial level, so the
                            // expansion deapth will increase into the child's level before next iteration
                            currentExpansionDeapth = hierarchialGroups[i + 1].GetLevel()
                        }
                    }
                    else {
                        currentStage = 1 // has no children
                    }
                }
            }

            const mode = (this.m_selectedPriceBookID == -1) ? 101 : 102

            elements[displayedItemsAmount] = (<TreeItemGW key={this.GetUniqueKey()} itemText={hierarchialGroups[i].GetDescription()} level={hierarchialGroups[i].GetLevel()} readingUrl={""} nobbGroup={hierarchialGroups[i].GetGroupID()} width={itemWidth} height={itemHeight} app={this.m_app} tree={tree} itemID={i} childStatus={currentChildStatus} lastBorn={hierarchialGroups[i].IsLastBorn(mode)} stage={currentStage} ></TreeItemGW>)
            displayedItemsAmount++
        }

        // Adding non nobb specified articles
        elements[displayedItemsAmount] = <div key={this.GetUniqueKey()}>{this.RenderNobbUnspecifiedArticlesTree(tree, itemWidth, itemHeight)}</div>
        displayedItemsAmount++
        
        elements.length = displayedItemsAmount // Reducing the unused indexes from the final result
        return <div>{elements}</div>
    }

    // Locates an input component with ID:"currentInput" within the page and gives it focus
    SetFocusOnItemInput() {
        var currentInput = document.getElementById("currentInput");
        currentInput && currentInput.focus();
    }

    // Delayed update of the search field
    UpdateSearchFieldTimed() {
        setTimeout(this.UpdateSearchFieldUntimed.bind(this), 50)
    }

    // Direct uptade of the search field
    UpdateSearchFieldUntimed() {
        if (this.m_searchField != null) {
            this.m_searchField.forceUpdate()
        }
    }

    // Handlers for all ImageButton clicks
    HandleButton(buttonID: string) {

        if (buttonID == "favorites") {
            this.m_app.ScrollToTop()
            this.ClearSelection()
            this.DisplayFavorites()
        }

        if (buttonID == "utvidetInfo") {
            this.m_app.ToggleExtInfo()
        }

        if (buttonID == "hurtigRapport") {
            this.m_app.ScrollToTop()
            this.ClearSelection()

            if (this.m_app.state.DisplayMode == this.m_app.DMode.renderOrders) {
                if (this.m_summaryView != null) {
                }
            }
            else {
                this.m_app.setState({ DisplayMode: this.m_app.DMode.renderOrders })
            }
        }
        
        if (buttonID == "priceSettings") {
            this.m_app.setState({ DisplayMode: this.m_app.DMode.priceSettings })
            this.m_app.ScrollToTop()
        }

        if (buttonID == "settings") {
            // this.m_app.setState({ DisplayMode: this.m_app.DMode.Settings })
            this.m_app.setState({ DisplayMode: this.m_app.DMode.searchSettings })
            this.m_app.ScrollToTop()
        }

        if (buttonID == "help") {
            this.m_app.ScrollToTop()
            this.m_app.setState({ DisplayMode: this.m_app.DMode.help })
        }

        if (buttonID == "10treffer") {
            this.m_app.setState({ resultGroupLength: 10 })
        }

        if (buttonID == "15treffer") {
            this.m_app.setState({ resultGroupLength: 15 })
        }

        if (buttonID == "20treffer") {
            this.m_app.setState({ resultGroupLength: 20 })
        }

        if (buttonID == "25treffer") {
            this.m_app.setState({ resultGroupLength: 25 })
        }

        if (buttonID == "50treffer") {
            this.m_app.setState({ resultGroupLength: 50 })
        }

        if (buttonID == "100treffer") {
            this.m_app.setState({ resultGroupLength: 100 })
        }

        if (buttonID == "lagreUtvalg") {
            if (this.GetApp().state.DisplayMode == this.GetApp().DMode.projectSave) {
                // returning to the initial state if this button is clicked while already in projectSave mode
                if (this.m_projectSaveView != null) {
                    this.m_projectSaveView.setState({ stage: "initial" })
                    this.m_projectSaveView.forceUpdate()
                }
            }

            this.m_app.ScrollToTop()
            this.GetApp().setState({ DisplayMode: this.GetApp().DMode.projectSave })
        }

        if (buttonID == "henteUtvalg") {
            if (this.GetApp().state.DisplayMode == this.GetApp().DMode.projectLoad) {
                // returning to the initial state if this button is clicked while already in projectLoad mode
                if (this.m_projectView != null) {
                    this.m_projectView.setState({ stage: "initial" })
                    this.m_projectView.forceUpdate()
                }
            }

            this.m_app.ScrollToTop()
            this.SelectionLoadMenuStepOne()
        }

        if (buttonID == "henteByggdel") {
            this.m_app.ScrollToTop()
            this.BuildPartLoadMenu()
        }

        if (buttonID == "debugInfo") {
            this.m_app.ScrollToTop()
            this.DebugOutput("(Debug info was requested by a click on the debug info button)")
        }

        if (buttonID == "nobbAlpha") {
            this.m_app.ScrollToTop()
            this.m_alphabeticalDisplay = !this.m_alphabeticalDisplay

            if (this.GetTreeView() != null) {
                this.GetTreeView().setState({ hasDerivedFromPreviousSelection: false })
            }

            this.GetApp().forceUpdate()
        }

        if (buttonID == "toolbarNew") {
            this.m_app.ScrollToTop()
            this.m_app.setState({ DisplayMode: this.m_app.DMode.newProject })
        }

        if (buttonID == "creationView") {
            this.m_app.ScrollToTop()
            this.m_app.setState({ DisplayMode: this.m_app.DMode.creative })
        }
        
        if (buttonID == "debugAction") {
            this.DebugAction()
        }

        if (buttonID == "showLog") {
            this.m_app.ScrollToTop()
            this.m_app.setState({ DisplayMode: this.m_app.DMode.log })
        }
    }
    
    // Handlers for all the orientation button clicks
    HandleOriButton(buttonID: string, position: number) {
        const total = this.m_app.state.resultTotalArticles
        const step = this.m_app.state.resultGroupLength
        const lastSize = total % step
        const groups = ((total - lastSize) / step) + ((lastSize != 0) ? 1 : 0)
        const range = this.m_app.state.resultGroupRange

        // Moves the viewed group all the way to the start (0)
        if (buttonID == "allBack") {
            this.m_app.setState({ resultGroup: 0, resultGroupStart: 0 })
            this.ClearFocus()
        }

        // Moves the viewed group 10 steps backward
        if (buttonID == "rewind") {
            var newPos = this.m_app.state.resultGroup - range
            if (newPos < 0) {
                newPos = 0
            }

            var newStart = newPos - (range / 2)

            if (newStart < 0) {
                newStart = 0
            }

            this.m_app.setState({ resultGroup: newPos, resultGroupStart: newStart })
            this.ClearFocus()
        }

        // Moves the viewed group 1 step backward
        if (buttonID == "backward") {
            var newPos = this.m_app.state.resultGroup - 1
            if (newPos < 0) {
                newPos = 0
            }

            var newStart = this.m_app.state.resultGroupStart
            if (newStart > newPos) {
                newStart -= range
                if (newStart < 0) {
                    newStart = 0
                }
            }

            this.m_app.setState({ resultGroup: newPos, resultGroupStart: newStart })
            this.ClearFocus()
        }

        // Moves the viewed group 1 step forward
        if (buttonID == "forward") {
            var newPos = this.m_app.state.resultGroup + 1
            var newStart = this.m_app.state.resultGroupStart

            if ((newStart + range) <= newPos) {
                newStart += range
            }

            if (newPos >= groups) {
                newPos = groups - 1
                newStart = groups - range
            }

            if (newStart < 0) {
                newStart = 0
            }

            this.m_app.setState({ resultGroup: newPos, resultGroupStart: newStart })
            this.ClearFocus()
        }

        // Moves the viewed group 10 steps forward
        if (buttonID == "fastForward") {
            var newPos = this.m_app.state.resultGroup + range

            if (newPos >= groups) {
                newPos = groups - 1
            }

            var newStart = newPos - (range / 2)

            if (newStart < 0) {
                newStart = 0
            }

            this.m_app.setState({ resultGroup: newPos, resultGroupStart: newStart })
            this.ClearFocus()
        }

        // Moves the viewed group all the way to the last one
        if (buttonID == "toEnd") {
            var newPos = groups - 1
            if (newPos < 0) {
                newPos = 0
            }

            var newStart = newPos - (range - 1)

            if (newStart < 0) {
                newStart = 0
            }

            this.m_app.setState({ resultGroup: newPos, resultGroupStart: newStart })
            this.ClearFocus()
        }

        // Numerical buttons
        if (buttonID == "numerical") {

            this.m_app.setState({ resultGroup: position })
            this.ClearFocus()
        }

        this.m_app.ScrollToTop()
    }

    // Specifically updates the displayed total sum of articles prizes
    RedrawTotalSum() {
        this.m_totalDisplay && this.m_totalDisplay.forceUpdate()
    }
    
    // A slightly delayed sub method for the (above) HandleKeyArticleItem method
    RunKeyEffect() {
        // Space opens/closes Ext info if the search input does not have focus 
        if (this.m_functionalKeyPressed == "Ent") {
            this.m_functionalKeyPressed = ""
            this.m_app.ToggleExtInfo()
        }

        const SelectedID = this.GetApp().GetSelectionID()
        
        if (this.m_app.state.DisplayMode == this.m_app.DMode.QReport) { // Qreport is up 

            // Steping from a selected build part
            const selectedQRBuildPartItem = this.GetSelectedQRBuildPartItem();
            if (selectedQRBuildPartItem != null) {
                var bps = this.GetSelectedBuildParts()

                if (this.m_functionalKeyPressed == "ArrowDown") {
                    this.m_functionalKeyPressed = ""

                    for (var i = 0; i < bps.length; i++) {
                        if (bps[i].GetId() == SelectedID) {

                            if ((i + 1) < bps.length) {
                                this.m_app.state.selectionID = bps[i + 1].GetId()
                                this.m_arrowKeyInputFlag = bps[i + 1].GetId()
                                selectedQRBuildPartItem.state.mouseState = "out"
                                selectedQRBuildPartItem.m_choiseCtrl.state.hovering = false
                                selectedQRBuildPartItem.m_choiseCtrl.state.plusPressed = false
                                selectedQRBuildPartItem.m_choiseCtrl.state.minusPressed = false
                                selectedQRBuildPartItem.forceUpdate()
                                this.m_selectionView.forceUpdate()
                            }

                            break
                        }
                    }
                }

                if (this.m_functionalKeyPressed == "ArrowUp") {
                    this.m_functionalKeyPressed = ""

                    for (var i = 0; i < bps.length; i++) {
                        if (bps[i].GetId() == SelectedID) {

                            // Regular stepwise focus change up the list:
                            if (i > 0) {
                                this.m_app.state.selectionID = bps[i - 1].GetId()
                                this.m_arrowKeyInputFlag = bps[i - 1].GetId()
                                selectedQRBuildPartItem.state.mouseState = "out"
                                selectedQRBuildPartItem.m_choiseCtrl.state.hovering = false
                                selectedQRBuildPartItem.m_choiseCtrl.state.plusPressed = false
                                selectedQRBuildPartItem.m_choiseCtrl.state.minusPressed = false
                                selectedQRBuildPartItem.forceUpdate()
                                this.m_selectionView.forceUpdate()
                            }

                            // At the beginning of the build part list - may leap to the last selected article if there are articles
                            if (i == 0) {
                                if (this.m_selectedArticles.length > 0) {
                                    this.m_app.state.selectionID = this.m_selectedArticles[this.m_selectedArticles.length - 1].GetSelectionLabel()
                                    this.m_arrowKeyInputFlag = this.m_selectedArticles[this.m_selectedArticles.length - 1].GetSelectionLabel()
                                    selectedQRBuildPartItem.state.mouseState = "out"
                                    selectedQRBuildPartItem.m_choiseCtrl.state.hovering = false
                                    selectedQRBuildPartItem.m_choiseCtrl.state.plusPressed = false
                                    selectedQRBuildPartItem.m_choiseCtrl.state.minusPressed = false
                                    selectedQRBuildPartItem.forceUpdate()
                                    this.m_selectedArticle = this.m_selectedArticles[this.m_selectedArticles.length - 1]
                                    this.m_selectionView.forceUpdate()
                                }
                            }

                            break
                        }
                    }
                }
                
                return
            }
            
            // Selection view up/down arrow key iteration between articles:
            var selectedSelectionItem = this.GetSelectedSelectionItem();
            if (selectedSelectionItem != null) {
                if (this.m_functionalKeyPressed == "ArrowDown") {
                    this.m_functionalKeyPressed = ""
                    
                    for (var i = 0; i < this.m_selectedArticles.length; i++) {
                        if (this.m_selectedArticles[i].GetSelectionLabel() == SelectedID) {

                            // Normal stepwise focus change down the list:
                            if ((i + 1) < this.m_selectedArticles.length) {
                                this.m_app.state.selectionID = this.m_selectedArticles[i + 1].GetSelectionLabel()
                                this.m_arrowKeyInputFlag = this.m_selectedArticles[i + 1].GetSelectionLabel()
                                selectedSelectionItem.state.mouseState = "out"
                                selectedSelectionItem.m_choise.state.hovering = false
                                selectedSelectionItem.m_choise.state.plusPressed = false
                                selectedSelectionItem.m_choise.state.minusPressed = false
                                this.m_selectedArticle = this.m_selectedArticles[i + 1]
                                this.m_selectionView.forceUpdate()
                            }

                            // At the end of the list - may leap to the uppermost selected build part if there is at least one build part
                            if ((i + 1) == this.m_selectedArticles.length) {
                                var bps = this.GetSelectedBuildParts()

                                if (bps == null) {
                                    break
                                }

                                if (bps.length > 0) {
                                    this.m_app.state.selectionID = bps[0].GetId()
                                    this.m_arrowKeyInputFlag = bps[0].GetId()
                                    selectedSelectionItem.state.mouseState = "out"
                                    selectedSelectionItem.m_choise.state.hovering = false
                                    selectedSelectionItem.m_choise.state.plusPressed = false
                                    selectedSelectionItem.m_choise.state.minusPressed = false
                                    this.m_selectedArticle = null
                                    this.m_selectionView.forceUpdate()
                                }
                            }

                            break
                        }
                    }
                }

                if (this.m_functionalKeyPressed == "ArrowUp") {
                    this.m_functionalKeyPressed = ""
                    const SelectedID = this.GetApp().GetSelectionID()
                    for (var i = 0; i < this.m_selectedArticles.length; i++) {
                        if (this.m_selectedArticles[i].GetSelectionLabel() == SelectedID) {
                            if (i > 0) {
                                this.m_app.state.selectionID = this.m_selectedArticles[i - 1].GetSelectionLabel()
                                this.m_arrowKeyInputFlag = this.m_selectedArticles[i - 1].GetSelectionLabel()
                                selectedSelectionItem.state.mouseState = "out"
                                selectedSelectionItem.m_choise.state.hovering = false
                                selectedSelectionItem.m_choise.state.plusPressed = false
                                selectedSelectionItem.m_choise.state.minusPressed = false
                                this.m_selectedArticle = this.m_selectedArticles[i - 1]
                                this.m_selectionView.forceUpdate()
                            }

                            break
                        }
                    }
                }
            }

            this.UpdateExtInfoView()
            return
        }

        if (this.m_app.state.DisplayMode == this.m_app.DMode.byggdelLoad) { // BuildPart view is up 

            // Steping from a selected build part
            const selectedBuildPartItem = this.GetSelectedBuildPartItem();
            if (selectedBuildPartItem != null) {
                var bps = this.GetBuildParts()

                if (this.m_functionalKeyPressed == "ArrowDown") {
                    this.m_functionalKeyPressed = ""

                    for (var i = 0; i < bps.length; i++) {
                        if (bps[i].GetId() == SelectedID) {

                            if ((i + 1) < bps.length) {
                                this.m_app.state.selectionID = bps[i + 1].GetId()
                                this.m_arrowKeyInputFlag = bps[i + 1].GetId()
                                selectedBuildPartItem.state.mouseState = "out"
                                selectedBuildPartItem.m_choiseCtrl.state.hovering = false
                                selectedBuildPartItem.m_choiseCtrl.state.plusPressed = false
                                selectedBuildPartItem.m_choiseCtrl.state.minusPressed = false
                                selectedBuildPartItem.forceUpdate()
                                this.m_buildPartView.forceUpdate()
                            }

                            break
                        }
                    }
                }

                if (this.m_functionalKeyPressed == "ArrowUp") {
                    this.m_functionalKeyPressed = ""

                    for (var i = 0; i < bps.length; i++) {
                        if (bps[i].GetId() == SelectedID) {

                            // Regular stepwise focus change up the list:
                            if (i > 0) {
                                this.m_app.state.selectionID = bps[i - 1].GetId()
                                this.m_arrowKeyInputFlag = bps[i - 1].GetId()
                                selectedBuildPartItem.state.mouseState = "out"
                                selectedBuildPartItem.m_choiseCtrl.state.hovering = false
                                selectedBuildPartItem.m_choiseCtrl.state.plusPressed = false
                                selectedBuildPartItem.m_choiseCtrl.state.minusPressed = false
                                selectedBuildPartItem.forceUpdate()
                                this.m_buildPartView.forceUpdate()
                            }
                            
                            break
                        }
                    }
                }
            }
            
            return
        }

        if (this.m_app.state.DisplayMode == this.m_app.DMode.projectLoad) { // Project view is up 

            // Steping from a selected build part
            if (this.GetSelectedProjectItem() != null) {
                var pro = this.m_projectSummaries

                if (this.m_functionalKeyPressed == "ArrowUp") {
                    this.m_functionalKeyPressed = ""

                    for (var i = 0; i < pro.length; i++) {
                        if (pro[i].GetId() == SelectedID) {

                            if ((i + 1) < pro.length) {
                                this.m_app.state.selectionID = pro[i + 1].GetId()
                                this.m_arrowKeyInputFlag = pro[i + 1].GetId()
                                this.GetSelectedProjectItem().state.mouseState = "out"
                                this.m_projectView.forceUpdate()
                            }

                            break
                        }
                    }
                }

                if (this.m_functionalKeyPressed == "ArrowDown") {
                    this.m_functionalKeyPressed = ""

                    for (var i = 0; i < pro.length; i++) {
                        if (pro[i].GetId() == SelectedID) {

                            // Regular stepwise focus change up the list:
                            if (i > 0) {
                                this.m_app.state.selectionID = pro[i - 1].GetId()
                                this.m_arrowKeyInputFlag = pro[i - 1].GetId()
                                this.GetSelectedProjectItem().state.mouseState = "out"
                                this.m_projectView.forceUpdate()
                            }

                            break
                        }
                    }
                } 
            }

            return
        }

        if (true) {
            // Article view up/down arrow key iteration between articles:
            const selectedArticleItem = this.GetSelectedArticleItem();
            if (selectedArticleItem != null) {
                if (this.m_functionalKeyPressed == "ArrowDown") {
                    this.m_functionalKeyPressed = ""
                    const SelectedID = this.GetApp().GetSelectionID()
                    for (var i = 0; i < this.GetRenderedArticleGroup().length; i++) {
                        if (this.GetRenderedArticleGroup()[i].GetSelectionLabel() == SelectedID) {
                            if ((i + 1) < this.GetRenderedArticleGroup().length) {
                                this.m_app.state.selectionID = this.GetRenderedArticleGroup()[i + 1].GetSelectionLabel()
                                this.m_arrowKeyInputFlag = this.GetRenderedArticleGroup()[i + 1].GetSelectionLabel()
                                selectedArticleItem.state.mouseState = "out"
                                selectedArticleItem.m_choise.state.hovering = false
                                selectedArticleItem.m_choise.state.plusPressed = false
                                selectedArticleItem.m_choise.state.minusPressed = false
                                this.m_selectedArticle = this.GetRenderedArticleGroup()[i + 1]
                                this.m_articleView.forceUpdate()
                            }
                            else { // We have come to the bottom of the search result page, so instead we try to iterate into next page of results
                                if (this.GetApp().GetIsLastGroup() == false) {
                                    this.SetRollIn(1)
                                    this.HandleOriButton("forward", -1)
                                }
                            }

                            break
                        }
                    }
                }

                if (this.m_functionalKeyPressed == "ArrowUp") {
                    this.m_functionalKeyPressed = ""
                    const SelectedID = this.GetApp().GetSelectionID()
                    for (var i = 0; i < this.GetRenderedArticleGroup().length; i++) {
                        if (this.GetRenderedArticleGroup()[i].GetSelectionLabel() == SelectedID) {
                            if (i > 0) {
                                this.m_app.state.selectionID = this.GetRenderedArticleGroup()[i - 1].GetSelectionLabel()
                                this.m_arrowKeyInputFlag = this.GetRenderedArticleGroup()[i - 1].GetSelectionLabel()
                                selectedArticleItem.state.mouseState = "out"
                                selectedArticleItem.m_choise.state.hovering = false
                                selectedArticleItem.m_choise.state.plusPressed = false
                                selectedArticleItem.m_choise.state.minusPressed = false
                                this.m_selectedArticle = this.GetRenderedArticleGroup()[i - 1]
                                this.m_articleView.forceUpdate()
                            }
                            else { // We have come to the top of the search result page, so instead we try to iterate backward into the previous page of results
                                if (this.GetApp().GetIsFirstGroup() == false) {
                                    this.SetRollIn(-1)
                                    this.HandleOriButton("backward", -1)
                                }
                            }

                            break
                        }
                    }
                }
            }

            this.UpdateExtInfoView()
        }
    }

    // Access to the app object
    GetApp() {
        return this.m_app
    }

    // Performs a translation from certain character combines within a string into nordic letters
    GetNordic(text: string) {
        var result = ""
        for (var i = 0; i < text.length; i++) {
            if (text[i] == "<") {
                i++
                if (text[i] == "A") {
                    result += "Å"
                    continue
                }

                if (text[i] == "a") {
                    result += "å"
                    continue
                }

                if (text[i] == "E") {
                    result += "Æ"
                    continue
                }

                if (text[i] == "e") {
                    result += "æ"
                    continue
                }

                if (text[i] == "O") {
                    result += "Ø"
                    continue
                }

                if (text[i] == "o") {
                    result += "ø"
                    continue
                }

                result += "<" + text[i]
            }
            else {
                if (text[i] == "°") {
                    result += "ø"
                    continue
                }

                if (text[i] == "Ï") {
                    result += "Ø"
                    continue
                }

                if (text[i] == "Õ") {
                    result += "å"
                    continue
                }

                if (text[i] == "Š") {
                    result += "Å"
                    continue
                }

                if (text[i] == "µ") {
                    result += "æ"
                    continue
                }

                if (text[i] == "Æ") { // unknown glitch char - fill in if you find an example!"
                    result += "Æ"
                    continue
                }

                result += text[i]
            }
        }

        return result
    }

    // RenderDebugDisplay renders some stats below the tree view for debug uses only (comment away this function call in App.render() when it's not needed)
    RenderDebugDisplay() {
        if ((PADebug.IsDebuging() == false) || (debugRenderDisplay == false)) {
            return null
        }

        var buffer = new Array<JSX.Element>()
        var keyIndex = 0

        buffer.push(<div key={keyIndex++}>
            Debug info:
        </div>)

        var selectedArticleItem = this.GetSelectedArticleItem();
        if (selectedArticleItem != null) {
            buffer.push(<div key={keyIndex++}>
                m_selectedArticleItem : {selectedArticleItem.GetArticle().GetDescription()}
            </div>)
        }
        else {
            buffer.push(<div key={keyIndex++}>
                m_selectedArticleItem : null
            </div>)
        }

        var selectedSelectionItem = this.GetSelectedSelectionItem()
        if (selectedSelectionItem != null) {
            buffer.push(<div key={keyIndex++}>
                m_selectedQRArticleItem : {selectedSelectionItem.GetArticle().GetDescription()}
            </div>)
        }
        else {
            buffer.push(<div key={keyIndex++}>
                m_selectedQRArticleItem : null
            </div>)
        }

        if (this.m_extDataForSelectedItem != null) {
            buffer.push(<div key={keyIndex++}>
                m_extDataForSelectedItem nobb: {this.m_extDataForSelectedItem.GetNobbNr().toString()}
            </div>)
        }
        else {
            buffer.push(<div key={keyIndex++}>
                m_extDataForSelectedItem : null
            </div>)
        }

        return buffer
    }

    /* Adds the amount of a certain build part into selection, this will insert the buildpart into selection uless
     * it is already there, then the amount will be added to it instead */
    AddSelectBuildPart(buildPartInQuestion: BuildPartGW) {

        // First let's see if this buldpart is already in the selection array...
        for (var i = 0; i < this.m_selectedBuildParts.length; i++) {
            if (buildPartInQuestion.GetId() == this.m_selectedBuildParts[i].GetId()) {
                return
            }
        }

        this.m_selectedBuildParts.push(buildPartInQuestion)
    }

    // InsertSelectArticle adds an article into the list of chosen articles
    InsertSelectArticle(articleInQuestion: ArticleGW) {
        // First let's see if this particular article is already queued for instertion..
        for (var i = 0; i < this.m_qreportInsertionQueue.length; i++) {
            if (this.m_qreportInsertionQueue[i].GetSelectionLabel() == articleInQuestion.GetSelectionLabel()) {
                return // Skipping the request as the article is alredy within the selected articles array
            }
        }

        this.m_qreportInsertionQueue.push(articleInQuestion)
        this.m_qreportInsertionPulseOn = true
        this.InsertSelectArticlePulse()
    }

    // Keeps calling itself until all queued articles have been properly inserted into the selection array after loading its extended data (or reusing if it's already loaded)
    InsertSelectArticlePulse() {
        if (this.m_qreportInsertionPulseOn == false) {
            return
        }
        else {
            setTimeout(this.InsertSelectArticlePulse.bind(this), 60) // (Delayed recursion)
        }

        if (this.m_qreportInsertionQueue.length > 0) {
            if (this.m_qreportInsertionPreviousIncomplete == true) {
                // Assert(false, "InsertSelectArticlePulse - many requests at the same time? (Not an error, just checking if it happens)")
                // This pulse will not be used but wait for the allready loading article's data to completed first, then a later pulse will do the job
            }
            else {
                // Pulse is ready to take on a new article to insert among the selected articles
                var newArticle = this.m_qreportInsertionQueue.pop()
                Assert(newArticle != null, "in core.InsertArticlePulse() - the article in queue is null for some strange reason (!?!?!)")
                if (newArticle != null) {
                    // First let's find out if extended data for this article has already been loaded, if so that data should be used and no additional request should be sent to the server
                    var extInfo: ExtDataGW | null = null

                    // First checking the m_extDataForSelectedItem, which may have extended data for this article, which is not necessarily yet tied to the article but may be temporary and used for display only
                    if (this.m_app.GetSelectionID() == newArticle.GetSelectionLabel()) {
                        if (this.m_extDataForSelectedItem != null) {
                            if (this.m_extDataForSelectedItem.GetNobbNr().toString() == newArticle.GetNobb()) {
                                extInfo = this.m_extDataForSelectedItem
                            }
                        }
                    }

                    // Now let's check if the article already holds extended data on it's handle and if so we'll use that
                    if (extInfo == null) {
                        if (newArticle.GetIfHasExtData() != null) {
                            extInfo = newArticle.GetIfHasExtData()
                        }
                    }

                    // Next check is if this article is already insterted into the array of selected articles
                    if (this.SelectedFind(newArticle.GetSelectionLabel()) == false) {
                        /* This article will be inserted, but ext data will only be loaded if at this point we've not found any already loaded extended data,
                           but first, let's also check and use an eventual article reference with this ID if it's found within favorites..   */
                        var articleToInsert = this.GetFavorite(newArticle.GetSelectionLabel())

                        if (articleToInsert == null) {
                            articleToInsert = newArticle
                        }

                        // We're also checking if this article has an instance within the displayed articles, in order not to end up with any multiple instances of this article
                        var alternativeArticle = this.GetDisplayedArticle(newArticle.GetSelectionLabel())

                        if (alternativeArticle != null) {
                            articleToInsert = alternativeArticle

                            // if the instance from displayed articles happened to hold any extended data, that instance of data must be used
                            if (extInfo == null) {
                                if (alternativeArticle.GetIfHasExtData() != null) {
                                    extInfo = alternativeArticle.GetIfHasExtData()
                                }
                            }
                        }

                        if (extInfo == null) {
                            // Extended data was nowhere to be found for this article, so let's load it from back end and then await the response as it triggers the InsertSelectArticleCompletion handler
                            this.m_qreportInsertionPreviousIncomplete = true // Blocking the pulse from taking on any additional tasks until this one is done
                            this.m_articleBeingInsertedAmongSelectedArticles = articleToInsert

                            var artNobb = articleToInsert.GetNobb().toString()

                            if (artNobb == "") {
                                // Nobb number for the article was not set, meaning it is incomplete and cannot display any extended info etc.
                                // a replacement default extended info instance will be attatched to this article instead, so that the article won't trigger any more attempts to load extended info in the future.
                                // This will take place in core.InsertSelectArticleCompletion, so that any other failed ext info loads will cause the article to be treated the same way.
                                this.InsertSelectArticleCompletion("")
                            }
                            else {
                                LoadFromUrl(this.m_serverUrl + "api/price/nobb/" + artNobb, this.InsertSelectArticleCompletion.bind(this), this.DefaultLoadErrorHandler.bind(this), this.GetApp().state.accessToken)
                            }
                        }
                        else {
                            // We should check if this article, in its un extended state, had an amount provided for it which should be used also as the extended data's amount
                            const carriedValue = articleToInsert.GetAmount()

                            // we've already got the extended data, so let's just insert the article with that data attatched to it
                            articleToInsert.SetExtData(extInfo)

                            // the carried value will be transfered into the ext data, with default unit and supplier
                            articleToInsert.SetAmount(carriedValue)

                            if (carriedValue > 0) {
                                articleToInsert.SetAmount(carriedValue) // this will be setting the extended data's set amount, as we know it is already attatched to the article
                            }

                            Assert((articleToInsert.GetIfHasExtData() != null), "Error - InsertSelectArticlePulse - the article should have ext data at this point, but GetIfHasExtData still returns null! Check it out")

                            this.m_selectedArticles[this.m_selectedArticles.length] = articleToInsert
                            this.SortSelectedArticles()
                            this.m_app.forceUpdate()
                        }
                    }
                    else {
                        // The article is already insterted into the array of selected articles, so we're not going to do anything this time.
                        // Assert(false,"core.InsertSelectArticlePulse() - the article is already within the selected articles (not an error, just checking if it occurs)")
                    }
                }
            }
        }
        else {
            this.m_qreportInsertionPulseOn = false
        }
    }

    

    // The last step of inserting a selected article, with recieved data from back end provided
    InsertSelectArticleCompletion(articleData: string) {
        var newArticle = this.m_articleBeingInsertedAmongSelectedArticles
        if (newArticle != null) {
            this.m_articleBeingInsertedAmongSelectedArticles = null
            const carriedValue = newArticle.GetAmount()
            Assert(newArticle != null, "core.InsertSelectArticleCompletion - new article to be added extended data to was null!")

            var extD: ExtDataGW = this.m_defaultExtDataForArticlesMissingIt

            if (articleData == "" || articleData == null) {
            }
            else {
                
                var hog: any 
                try {
                    hog = JSON.parse(articleData)
                    extD = new ExtDataGW()
                    extD.RecieveData(hog)
                    newArticle.SetExtData(extD)
                    extD.DirectSubChoise(newArticle)
                }

                catch (e) {
                    PADebug.Debug("InsertSelectArticleCompletion - article was missing ext data!")
                    extD = this.m_defaultExtDataForArticlesMissingIt
                    newArticle.SetExtData(extD)
                }
            }
            
            this.m_selectedArticles[this.m_selectedArticles.length] = newArticle
            newArticle.SetAmount(carriedValue)
            this.m_qreportInsertionPreviousIncomplete = false // unblocking the pulse from inserting more articles from the queue
            this.SortSelectedArticles()
            this.m_app.forceUpdate()
        }
    }

    // RemoveSelectArticle removes an article from the list of chosen articles
    RemoveSelectArticle(articleID: string) {
        var hasFound = false
        for (var i = 0; (i < this.m_selectedArticles.length) && (hasFound == false); i++) {
            if (this.m_selectedArticles[i].GetSelectionLabel() == articleID) {
                this.m_selectedArticles.splice(i, 1)
                hasFound = true
            }
        }

        this.m_app.forceUpdate()
        return hasFound
    }

    // SelectedFind returns true if an article with the given ID can be found within the list of chosen articles
    SelectedFind(articleID: string) {
        var hasFound = false
        for (var i = 0; (i < this.m_selectedArticles.length) && (hasFound == false); i++) {
            if (this.m_selectedArticles[i].GetSelectionLabel() == articleID) {
                hasFound = true
            }
        }

        return hasFound
    }

    // GetSelectedArticle returns an article if it exists within the list of chosen articles, or null if it doesn't
    GetSelectedArticle(articleID: string) {
        var hasFound = false
        for (var i = 0; (i < this.m_selectedArticles.length) && (hasFound == false); i++) {
            if (this.m_selectedArticles[i].GetSelectionLabel() == articleID) {
                return this.m_selectedArticles[i]
            }
        }

        return null
    }

    // GetSelectedArticle returns an article if it exists within the list of favorites, or null if it doesn't
    GetFavorite(articleID: string) {
        var hasFound = false
        for (var i = 0; (i < this.m_favorites.length) && (hasFound == false); i++) {
            if (this.m_favorites[i].GetSelectionLabel() == articleID) {
                return this.m_favorites[i]
            }
        }

        return null
    }

    // Returns the array of favorites
    GetFavorites() {
        return this.m_favorites
    }

    // GetSelectedArticle returns an article if it is currently shown within the articles view, or null if it isn't
    GetDisplayedArticle(articleID: string) {
        var hasFound = false
        for (var i = 0; (i < this.GetDisplayedArticles().length) && (hasFound == false); i++) {
            if (this.GetDisplayedArticles()[i].GetSelectionLabel() == articleID) {
                return this.GetDisplayedArticles()[i]
            }
        }

        return null
    }

    // Returns a reference to the renderable treeview (on the left side)
    GetTreeView() {
        return this.m_treeView
    }

    // Returns a reference to the renderable articlesview (in the center)
    GetArticleView() {
        return this.m_articleView
    }

    // Returns a reference to the renderable selections view (the qreport at the bottom)
    GetSelectionView() {
        return this.m_selectionView
    }

    /* CheckForSelectionContents helps when nodes in the tree view determines if their cathegory contains any selected articles,
     * in which case a "*" sign will be shown to clarify that there are selected articles to be found within that tree item.
     * The search for contents will look slightly different depending on the hierarchial level within the tree, where either
     * the first 2 letters, the first 4 or all letters in the group ID will be compared. */
    CheckForSelectionContents(groupID: string, hierarchyLevel: number) {
        var pricebook = this.GetSelectedPriceBook();
        var pricebookId = pricebook && pricebook.GetPriceBookId();

        if (this.m_treeView.state.treeSource == "optimera_nobb") {
            // We begin by checking if there's content matching the first 2 letters
            var foundContent = false
            var i = 0
            for (i = 0; i < this.m_selectedArticles.length; i++) {
                if (groupID[0] == this.m_selectedArticles[i].GetGroupID()[0]) {
                    if (groupID[1] == this.m_selectedArticles[i].GetGroupID()[1]) {
                        if (this.m_selectedArticles[i].GetPriceBook() == pricebookId) {
                            foundContent = true
                            break
                        }
                    }
                }
            }

            if (foundContent == false) {
                return false
            }

            // Passing this point means there are contents matching the first 2 letters in the ID
            if (hierarchyLevel == 1) {
                return true // What we needed to know for L1 is allready known and we can return true
            }

            // For L2 we'll make a check for matches on the first 4 letters in the ID
            if (hierarchyLevel == 2) {
                for (i = 0; i < this.m_selectedArticles.length; i++) {
                    if ((groupID[0] == this.m_selectedArticles[i].GetGroupID()[0])
                        && (groupID[1] == this.m_selectedArticles[i].GetGroupID()[1])
                        && (groupID[2] == this.m_selectedArticles[i].GetGroupID()[2])
                        && (groupID[3] == this.m_selectedArticles[i].GetGroupID()[3])) {
                        return true
                    }
                }
            }

            // For L3 we'll compare the whole group ID string
            if (hierarchyLevel == 3) {
                for (i = 0; i < this.m_selectedArticles.length; i++) {
                    if (groupID == this.m_selectedArticles[i].GetGroupID()) {
                        return true
                    }
                }
            }

            return false
        }
        else {
            // If the mode is Optimera Alphabetical, a full group match is the only thing needed:
            if (this.m_treeView.state.treeSource == "optimera_alfabetisk") {
                for (i = 0; i < this.m_selectedArticles.length; i++) {
                    if (groupID == this.m_selectedArticles[i].GetGroupID()) {
                        if (this.m_selectedArticles[i].GetPriceBook() == pricebookId) {
                            foundContent = true
                            break
                        }
                    }
                }
            }

            return false
        }
    }

    // All items within the list of favorites are shown within the articles view 
    // (which happens when clicking the favorites button at the top)
    DisplayFavorites() {
        this.m_app.setArticlesPageFavorites()
    }

    // Initial referencing to the app object
    ConnectApp(app: App) {
        if (this.m_app == null) {
            this.m_app = app
        }
    }

    // Initial referencing to the tree view (left hand side)
    ConnectTreeView(treeView: TreeViewGW) {
        if (this.m_treeView == null) {
            this.m_treeView = treeView
        }
    }

    // Initial referencing to the articles view (center-right)
    ConnectArticleView(ArticleView: ArticleViewGW) {
        this.m_articleView = ArticleView
    }

    // Initial referencing to the selection view (the qreport window at the bottom)
    ConnectSelectionView(SelectionView: SelectionViewGW) {
        this.m_selectionView = SelectionView
    }

    // Initial referencing to the currently selected project item
    ConnectSelectedProjectItem(item: ProjectItem) {
        this.m_selectedProjectItem = item
    }

    // Access to an array of articles limited by a given nobbGroup string
    GetArticlesByGroup(nobbGroup: string) {
        PADebug.Log("core.GetArticlesByGroup - at start")
        if (nobbGroup) {
            if (nobbGroup.slice(0, 11) == "withoutNobb") {
                var book = this.GetSelectedPriceBook()
                
                if (nobbGroup == "withoutNobb") {
                    // This is the head group of all the nobbless groups
                    
                    if (book) {
                        if (book.GetNobbLessIsLoaded() == true) {
                            return book.GetNobbUnspecifiedArticles()
                        }
                        else {
                            return
                        }
                    }
                    else {
                        // All price books selected, returning a collection of all uncathegorised articles from all pricebooks
                        return this.GetNobbUnspecifiedArticlesGlobal()
                    }
                }
                else {
                    // This is a subgroup
                    var idStr = ""

                    for (var i = 12; i < nobbGroup.length; i++) {
                        idStr += nobbGroup.charAt(i)
                    }

                    var groups: Array<ArticleGroupGW> | undefined

                    if (book) {
                        groups = book.GetNobbUnspecifiedGroups(false)

                    }
                    else {
                        PADebug.Log("core.GetArticlesByGroup - Entered withoutNobb section as all pricebooks")
                        groups = this.GetAllPriceBooksAlphabeticalGroups(false)
                    }

                    if (groups) {
                        for (var i = 0; i < groups.length; i++) {
                            if (groups[i].GetIDMin() == nobbGroup) {
                                PADebug.Log("core.GetArticlesByGroup - found corresponding group:" + nobbGroup)
                                if (groups[i].GetLoadedStatus().status == 2) {
                                    // Group found and is loaded. returning contents
                                    return groups[i].GetArticles()
                                }
                                else {
                                    // Group found but is not yet loaded
                                    return
                                }
                            }
                        }
                    }
                    else {
                        Assert(false, "core.GetArticlesByGroup - global and undefined group array to be scanned for a matching group!")
                    }
                }
            }
        }
        
        var hierarchicalGroups = this.GetHierarchialGroups();

        if (hierarchicalGroups == null) {
            return
        }
        
        for (var i = 0; i < hierarchicalGroups.length; i++) {
            if (hierarchicalGroups[i].GetIDMin() == nobbGroup) {
                this.m_lastSelectedNobbGroup = nobbGroup
                return hierarchicalGroups[i].GetArticles()
            }
        }

        return
    }

    // Returns an array containing all articles from all pricebooks where the source 
    // in use for the pricebook is its nobb unspecified groups (used if nobbgroups were missing within a price book).
    GetNobbUnspecifiedArticlesGlobal() {
        return this.m_nobbUnspecifiedArticlesFromAllPriceBooks
    }

    AddToNobbUnspecifiedArticlesGlobal(articles: Array<ArticleGW>) {

        for (var i = 0; i < articles.length; i++) {
            this.m_nobbUnspecifiedArticlesFromAllPriceBooks.push(articles[i])
        }
    }

    // Access to setting last selecteed without calling the GetArticlesBygroup method
    SetLastSelectedNobbGroup(nobbGroup: string) {
        this.m_lastSelectedNobbGroup = nobbGroup
    }

    // Reads the list of favorites and returns it as a renderable object
    RenderFavorites() {
        return this.RenderArticles(this.m_favorites, "favorites")
    }

    // Reads the list of currently displayed articles and returns it as a renderable object
    RenderArticles(art: Array<ArticleGW>, view :string) {
        if (art == null) {
            Assert(false, "core.RenderArticles - null was entered for rendering!")
            this.SetDisplayedArticles( new Array<ArticleGW>() )
            return <div></div> // showing it empty
        }
        else {
            this.m_renderedArticleGroup = art
        }

        var i = -1;
        const core = this
        return <div>{
            art.map(function () {
                i++
                return core.RenderArticle(art[i], i, view)
            })
        }</div>
    }
    
    // Reads the list of chosen articles and returns it as a renderable object (for the quick report)
    RenderQreport(art: Array<ArticleGW>) {
        var buffer = new Array<JSX.Element>()
        var lastPriceBook = -99
        var fistSection = true

        for (var i = 0; i < art.length; i++) {
            if (art[i].GetPriceBook() != lastPriceBook) {
                if (lastPriceBook != -99) {
                    fistSection = false
                }

                lastPriceBook = art[i].GetPriceBook()
                const pb = this.GetPriceBookByID(lastPriceBook)
                if (pb) {
                    if (fistSection == false) {
                        // Margin between pricebook sections
                        buffer.push(<div key={this.GetUniqueKey()} style={{ height: "65px" }}></div>)
                    }

                    buffer.push(<div key={this.GetUniqueKey()} style={{ display: "flex", flexDirection: "row" }}>
                        <div key={this.GetUniqueKey()} style={{ marginLeft: '35px', height: "28px", fontWeight: "bold" }} >Prisbok: </div>
                        <div style={{ width: "20px" }}></div>
                        <div key={this.GetUniqueKey()} style={{ height: "28px" }} >{pb.GetTitle()}</div>
                    </div>)

                    buffer.push(<div key={this.GetUniqueKey()}>
                        {this.m_app.RenderQuickReportTitle()}
                    </div>)

                    // Margin between title and contents
                    buffer.push(<div key={this.GetUniqueKey()} style={{ height: "12px" }}></div>)
                }
            }
            
            buffer.push(this.RenderSelection(art[i], i))
            // buffer.push(<div key={this.GetUniqueKey()}>{i.toString()}</div>)
        }
        
        return <div>{
            buffer
        }</div>
    }
    
    // Reads a single article and converts it into a renderable object for the articles display view
    RenderArticle(art: ArticleGW, key: number, view: string) {
        if ((this.GetApp().IsArticleSelected(art.GetSelectionLabel()) == true) && (this.GetApp().state.ExtInfoUp == true)) {
            return <div key={key}>
                <ArticleItemGW article={art} core={this} view={view}>
                </ArticleItemGW>
                <ExtInfoViewGW app={this.GetApp()} />
                <div style={{ height: spaceBetweenArticles }}></div>
            </div>
        }
        else {
            return <div key={key}>
                <ArticleItemGW article={art} core={this} view={view}>
                </ArticleItemGW>
                <div style={{ height: spaceBetweenArticles }}></div>
            </div>
        }
    }

    // Reads a single article and converts it into a renderable object for the quick report view
    RenderSelection(art: ArticleGW, index: number) {
        if ((this.GetApp().IsArticleSelected(art.GetSelectionLabel()) == true) && (this.GetApp().state.ExtInfoUp == true)) {
            return <div key={index}>
                <SelectionItemGW article={art} core={this}>
                </SelectionItemGW>
                <ExtInfoViewGW app={this.GetApp()} />
                <div style={{ height: spaceBetweenArticles }}></div>
            </div>
        }
        else {
            return <div key={index}>
                <SelectionItemGW article={art} core={this}>
                </SelectionItemGW>
                <div style={{ height: spaceBetweenArticles }}></div>
            </div>
        }
    }
    
    // Accessing the displayed articles
    GetDisplayedArticles() {
        return this.m_displayedArticles
    }

    // Accessing the articles which can be found in the selection view
    GetSelectedArticles() {
        return this.m_selectedArticles
    }

    // Accessing the amount of articles marked as favorites
    GetFavoritesAmount() {
        return this.m_favorites.length
    }

    // Returns true if articleID is represented within the favorites
    FavoritesFind(checkLabel: string) {
        if (this.m_favorites == null) {
        }

        var hasFound = false
        for (var i = 0; (i < this.m_favorites.length) && (hasFound == false); i++) {
            if (this.m_favorites[i].GetSelectionLabel() == checkLabel) {
                hasFound = true
            }
        }

        return hasFound
    }

    // Inserts a copy of the chosen article into the favorites list
    FavoritesInsert(newFavorite: ArticleGW) {
        if (this.FavoritesFind(newFavorite.GetSelectionLabel()) == false) {
            if (this.SelectedFind(newFavorite.GetSelectionLabel())) {
                var selectedArticle = this.GetSelectedArticle(newFavorite.GetSelectionLabel());
                if (selectedArticle != null)
                    this.m_favorites[this.m_favorites.length] = selectedArticle;
            }
            else {
                var displ = this.GetDisplayedArticle(newFavorite.GetSelectionLabel())
                if (displ != null) {
                    this.m_favorites[this.m_favorites.length] = displ
                }
                else {
                    this.m_favorites[this.m_favorites.length] = newFavorite
                }
            }

            this.SortFavorites()
            this.m_app.forceUpdate()
            return true
        }
        else {
            return false
        }
    }

    // FavoritesRemove removes an article from the favorites list and returns true if an article was removed
    FavoritesRemove(articleLabel: string) {
        var hasFound = false
        for (var i = 0; (i < this.m_favorites.length) && (hasFound == false); i++) {
            if (this.m_favorites[i].GetSelectionLabel() == articleLabel) {
                this.m_favorites.splice(i, 1)
                hasFound = true
            }
        }

        this.m_app.forceUpdate()

        if (this.m_selectionView != null) {
            this.m_selectionView.forceUpdate()
        }

        return hasFound
    }

    // Returns the total prize for all of the chosen articles
    TotalSum() {
        var sum = 0

        for (var i = 0; i < this.m_selectedArticles.length; i++) {
            sum += this.m_selectedArticles[i].GetSum(this.m_priceRadioPos, this.m_priceFlags)
        }

        sum += this.GetBuildPartsSum()

        return sum
    }

    // Returns the total prize for all of the chosen articles but with the MVA flag set to false regardless of its chosen state
    TotalSumExMva(){
        var sum = 0

        for (var i = 0; i < this.m_selectedArticles.length; i++) {
            sum += this.m_selectedArticles[i].GetSum(this.m_priceRadioPos, this.m_priceFlags & 3)
        }

        sum += this.GetBuildPartsSum()

        return sum
    }

    // Here the prize of an article is calculated as it should be displayed on various items and affect the total sum
    GetArticlePrizeString(article: ArticleGW) {
        if (article == null) {
            Assert(false, "core.GetArticlePrizeString - examined article was null!")
        }

        var extD = article.GetIfHasExtData()

        if (extD == null) {
            if (this.m_extDataForSelectedItem != null) {
                if (this.m_extDataForSelectedItem.GetNobbNr().toString() == article.GetNobb()) {
                    extD = this.m_extDataForSelectedItem
                }
            }
        }

        return article.GetUnitPriceString(this.m_priceRadioPos, this.m_priceFlags, false, extD)
    }
    
    // Accessing what article is being typed an amount into within the Article view
    GetInputItemArt() {
        return this.m_inputItemArticleView
    }

    // Accessing what article is being typed an amount into within the Article view
    GetInputItemBP() {
        return this.m_inputItemBuildPartView
    }

    // Access : a number telling which package unit the user has clicked upon and is entering a value into
    GetInputUnitExtInfo() {
        return this.m_inputUnitExtInfo
    }

    // Connects the quick report tab component for access and updates
    ConnectQReportTab(tab: QReportTabGW) {
        this.m_QRTab = tab
    }

    // Updating the quick report tab (in the upper right part of main screen)
    UpdateQReportTab() {
        if (this.m_QRTab != null) {
            this.m_QRTab.forceUpdate()
        }
    }

    // Making sure a certain build part has been inserted within selection if it has been given a positive amount
    EnsureBuildPartInserted(buildPartID: string) {
        for (var i = 0; i < this.m_selectedBuildParts.length; i++) {
            if (this.m_selectedBuildParts[i].GetId() == buildPartID) {
                // The build part is alredy inserted within the selected build parts
                return
            }
        }

        for (var i = 0; i < this.m_buildParts.length; i++) {
            if (this.m_buildParts[i].GetId() == buildPartID) {
                if (this.m_buildParts[i].GetAmount() > 0) {
                    // the build part was found and has a positive amount value, so it shall be inserted.
                    this.m_selectedBuildParts.push(this.m_buildParts[i])
                    return
                }
            }
        }
    }
    
    // Accessing what article is being typed an amount into within the Article view
    GetInputItemBPQR() {
        return this.m_inputItemBuildPartQRView
    }
    
    // Accessing what article is being typed an amount into within the Selection view
    GetInputItemSel() {
        return this.m_inputItemSelectionView
    }

    // Setting what article is being typed an amount into within the Article view
    SetInputItemArt(item: ArticleItemGW) {
        if (item == null) {
            Assert(false, "Debug Error! BDKalkCore.SetInputItemArt - null value was recieved from parameter!")
        }

        // Making sure any item allready being inserted value into gets out of that state, and if so cancelling that input
        if (this.m_inputItemArticleView != null) {

            if (this.m_inputItemArticleView.props.article.GetSelectionLabel() != item.props.article.GetSelectionLabel()) {
                var handle = this.m_inputItemArticleView
                this.m_inputItemArticleView = item // Ensures that the core's value is up to date before the item is redrawn
                handle.forceUpdate()
                item.forceUpdate()
            }

            if (this.m_inputItemSelectionView != null) {
                var handle2 = this.m_inputItemSelectionView
                this.m_inputItemSelectionView = null // Ensures that the previous core's value won't still be there on a refresh
                handle2.forceUpdate() // Refreshes the previous item, although it cannot currently be displayed in the present configuration
            }
        }
        else {
            this.m_inputItemArticleView = item
            this.m_inputItemArticleView.forceUpdate()

            if (this.m_inputItemSelectionView != null) {
                var handle2 = this.m_inputItemSelectionView
                this.m_inputItemSelectionView = null // Ensures that the previous core's value won't still be there on a refresh
                handle2.forceUpdate() // Refreshes the previous item, although it cannot currently be displayed in the present configuration
            }
        }

        this.ClearInput(1)
        this.UpdateExtInfoView()
    }

    // Setting what build part is being typed an amount into within the build part view
    SetInputItemBP(item: BuildPartItem) {
        if (item == null) {
            Assert(false, "Debug Error! BDKalkCore.SetInputItemBP - null value was recieved from parameter!")
        }

        // Making sure any item allready being inserted value into gets out of that state, and if so cancelling that input
        if (this.m_inputItemBuildPartView != null) {

            if (this.m_inputItemBuildPartView.props.buildPart.GetId() != item.props.buildPart.GetId()) {
                var handle = this.m_inputItemBuildPartView
                this.m_inputItemBuildPartView = item // Ensures that the core's value is up to date before the item is redrawn
                handle.forceUpdate()
                item.forceUpdate()
            }

            if (this.m_inputItemSelectionView != null) {
                var handle2 = this.m_inputItemSelectionView
                this.m_inputItemSelectionView = null // Ensures that the previous core's value won't still be there on a refresh
                handle2.forceUpdate() // Refreshes the previous item, although it cannot currently be displayed in the present configuration
            }
        }
        else {
            this.m_inputItemBuildPartView = item
            this.m_inputItemBuildPartView.forceUpdate()

            if (this.m_inputItemSelectionView != null) {
                var handle2 = this.m_inputItemSelectionView
                this.m_inputItemSelectionView = null // Ensures that the previous core's value won't still be there on a refresh
                handle2.forceUpdate() // Refreshes the previous item, although it cannot currently be displayed in the present configuration
            }
        }

        this.ClearInput(3)
    }

    // Setting what build part is being typed an amount into within the build part view
    SetInputItemBPQR(item: BuildPartQRItem) {

        if (item == null) {
            Assert(false, "Debug Error! BDKalkCore.SetInputItemBPQR - null value was recieved from parameter!")
        }

        // Making sure any item allready being inserted value into gets out of that state, and if so cancelling that input
        if (this.m_inputItemBuildPartQRView != null) {
            
            if (this.m_inputItemBuildPartQRView.props.buildPart.GetId() != item.props.buildPart.GetId()) {
                var handle = this.m_inputItemBuildPartQRView
                this.m_inputItemBuildPartQRView = item // Ensures that the core's value is up to date before the item is redrawn
                handle.forceUpdate()
                item.forceUpdate()
            }

            if (this.m_inputItemSelectionView != null) {
                var handle2 = this.m_inputItemSelectionView
                this.m_inputItemSelectionView = null // Ensures that the previous core's value won't still be there on a refresh
                handle2.forceUpdate() // Refreshes the previous item, although it cannot currently be displayed in the present configuration
            }
        }
        else {
            this.m_inputItemBuildPartQRView = item
            this.m_inputItemBuildPartQRView.forceUpdate()

            if (this.m_inputItemSelectionView != null) {
                var handle2 = this.m_inputItemSelectionView
                this.m_inputItemSelectionView = null // Ensures that the previous core's value won't still be there on a refresh
                handle2.forceUpdate() // Refreshes the previous item, although it cannot currently be displayed in the present configuration
            }
        }

        this.ClearInput(4)
    }

    // Setting what article is being typed an amount into within the Selection view
    SetInputItemSel(item: SelectionItemGW) {
        if (item == null) {
            Assert(false, "Debug Error! BDKalkCore.SetInputItemSel - null value was recieved from parameter!")
        }

        // Making sure any item allready being inserted value into gets out of that state, and if so cancelling that input
        if (this.m_inputItemSelectionView != null) {
            if (this.m_inputItemSelectionView.props.article.GetSelectionLabel() != item.props.article.GetSelectionLabel()) {
                var handle = this.m_inputItemSelectionView
                this.m_inputItemSelectionView = null // Ensures that the previous core's value won't still be there on a refresh
                handle.forceUpdate() // Refreshes the previous item
            }

            if (this.m_inputItemArticleView != null) {
                var handle2 = this.m_inputItemArticleView
                this.m_inputItemArticleView = null // Ensures that the previous core's value won't still be there on a refresh
                handle2.forceUpdate() // Refreshes the previous item, although it cannot currently be displayed in the present configuration
            }
        }

        // setting the new input item
        this.ClearInput(2)
        this.m_inputItemSelectionView = item // Ensures that the current core's value is correct before the refresh takes place
        item.setState({ inputActive: true })
        this.UpdateExtInfoView()
    }



    // Clear input will update any previously focused input field as not being in focus, and takes a parameter telling what type 
    // of input to leave unchanged(like the currently active one), if any, if none then just enter the number 0 as parameter
    ClearInput(except: number) {
        if (except != 1) {
            // Making sure any item allready being inserted value into gets out of that state, and if so cancelling that input
            if (this.m_inputItemArticleView != null) {
                var handle1 = this.m_inputItemArticleView
                this.m_inputItemArticleView = null // Ensures that the previous core's value won't still be there on a refresh
                handle1.onMouseOut()
                handle1.forceUpdate() // Refreshes the previous item
            }
        }

        if (except != 2) {
            if (this.m_inputItemSelectionView != null) {
                var handle2 = this.m_inputItemSelectionView
                this.m_inputItemSelectionView = null // Ensures that the previous core's value won't still be there on a refresh
                handle2.onMouseOut()
                handle2.forceUpdate() // Refreshes the previous item
            }
        }

        if (except != 3) {
            if (this.m_inputItemBuildPartView != null) {
                var handle3 = this.m_inputItemBuildPartView
                this.m_inputItemBuildPartView = null // Ensures that the previous core's value won't still be there on a refresh
                handle3.onMouseOut()
                handle3.forceUpdate() // Refreshes the previous item
            }
        }

        if (except != 4) {
            if (this.m_inputItemBuildPartQRView != null) {
                var handle4 = this.m_inputItemBuildPartQRView
                this.m_inputItemBuildPartQRView = null // Ensures that the previous core's value won't still be there on a refresh
                handle4.onMouseOut()
                handle4.forceUpdate() // Refreshes the previous item
            }
        }

        if (except != 5) {
            if (this.m_inputUnitExtInfo != -1) {
                this.m_inputUnitExtInfo = -1 // Ensures that the previous core's value won't still be there on a refresh
                this.UpdateExtInfoView()
            }
        }

        if (except != 6) {
            if (this.m_searchInFocus == true) {
                this.m_searchInFocus = false // Ensures that the previous core's value won't still be there on a refresh
                this.m_app.forceUpdate()
            }
        }

        if (except != 7) { // Prislister
            
        }
    }

    // Causing the search input to gain focus
    SetFocusOnSearchInput() {
        this.ClearInput(6)

        // ..And finally puting focus on the search input, once the other focusing procedures has been deactivated
        var element = document.getElementById("searchInput")
        if (element != null) {
            element.focus()
        }
    }
    
    // Access to the recently selected nobb group as a string
    GetLastSelectedGroup() {
        return this.m_lastSelectedNobbGroup
    }

    // Returns the currently loading nobb group's group ID string
    GetDataLoadGroup() {
        return this.m_dataLoadGroup
    }

    // Returns true if all data has been loaded properly and is ready to use within general searches
    isLoaded() {
        if (this.GetInitStage() < 3) {
            return false
        }
        else {
            return true
        }
    }

    // During the system and pricebook opening process, Get Init stage tells where in the process we are currently
    GetInitStage() {
        if (this.m_initStage == 0) {
            return 0
        }

        var pricebook = this.GetSelectedPriceBook();
        if (pricebook != null) {
            var stage = pricebook.GetInitStage()
            return pricebook.GetInitStage()
        }
        else {
            // when GetPriceBook returns null, that means the 'all price books' option has been chosen, so 
            // the returned init stage will depend on all price books init stages.

            var lowestInitStage = 10
            for (var i = 0; i < this.m_priceBooks.length; i++) {
                if (this.m_priceBooks[i].GetInitStage() < lowestInitStage) {

                    var hier = this.m_priceBooks[i].GetHierarchialGroups()
                    var hgs = 0

                    if (hier != null) {
                        if (hier.length > 0) {
                            hgs = hier.length
                        }
                    }

                    // If there are no groups within the pricebook, it is a custom pricebook and won't 
                    // count as any regular one which needs to be cashed before use, so it is ignored here.
                    if (hgs > 0) {
                        lowestInitStage = this.m_priceBooks[i].GetInitStage()
                    }
                    else {
                        // ignoring custom price book (since it does not follow any standard requirements but should be handled separately)
                    }
                }
            }

            if (lowestInitStage == 10) {
                Assert(false, "core.GetInitStage lowestInitStage was unchanged by pricebook init stages!")
            }

            return lowestInitStage
        }
    }

    // Help method for changing the selected item with the use of arrow keys
    ConnectSelectedArticleItem(selectedItem: ArticleItemGW) {
        this.SetSelectedArticleItem(selectedItem)

        // When using the arrowUp and ArrowDown keys, seletion will move up or down accordingly, this check is part of that process
        if (this.m_arrowKeyInputFlag != null) {
            if (selectedItem.GetArticle().GetSelectionLabel() == this.m_arrowKeyInputFlag) {
                this.m_arrowKeyInputFlag = null
                this.m_inputItemArticleView = selectedItem
            }
        }
    }

    // Help method for changing the selected item with the use of arrow keys
    ConnectSelectedQRArticleItem(selectedItem: SelectionItemGW) {
        this.m_selectedBuildPartQRItem = null
        this.SetSelectedSelectionItem(selectedItem)

        // When using the arrowUp and ArrowDown keys, seletion will move up or down accordingly, this check is part of that process
        if (this.m_arrowKeyInputFlag != null) {
            if (selectedItem.GetArticle().GetSelectionLabel() == this.m_arrowKeyInputFlag) {
                this.m_arrowKeyInputFlag = null
                this.m_inputItemSelectionView = selectedItem
            }
        }
    }

    // If a build part item inthe  build part view is selected, it will reference to itself using this method
    ConnectSelectedBuildPartItem(selectedItem: BuildPartItem) {
        this.m_selectedBuildPartItem = selectedItem

        // When using the arrowUp and ArrowDown keys, seletion will move up or down accordingly, this check is part of that process
        if (this.m_arrowKeyInputFlag != null) {
            if (selectedItem.props.buildPart.GetId() == this.m_arrowKeyInputFlag) {
                this.m_arrowKeyInputFlag = null
                this.m_inputItemBuildPartView = selectedItem
            }
        }
    }

    // If a build part item in the quick report is selected, it will reference to itself using this method
    ConnectSelectedBuildPartQRItem(selectedItem: BuildPartQRItem) {
        this.m_selectedQRArticleItem = null
        this.m_selectedBuildPartQRItem = selectedItem

        // When using the arrowUp and ArrowDown keys, seletion will move up or down accordingly, this check is part of that process
        if (this.m_arrowKeyInputFlag != null) {
            if (selectedItem.props.buildPart.GetId() == this.m_arrowKeyInputFlag) {
                this.m_arrowKeyInputFlag = null
                this.m_inputItemBuildPartQRView = selectedItem
            }
        }
    }

    // Renders and returns a toolbar button based on the given parameters
    RenderToolbarButton(imageNr: number, buttonID: string, tooltipText: string) {
        // First calculating the row and column offsets for locating button symbol in the "toolbarFaces.png"
        var columnOffset = imageNr % 10
        var rowOffset = (imageNr - columnOffset) / 10
        columnOffset *= 25
        rowOffset *= 30

        return <div></div>
    }

    // Returns name of the logged in user
    GetLoggedInName() {
        if (this.m_loginName == null) {

        }

        return this.m_loginName
    }

    // Returns company name of the logged in user
    GetLoggedInCompanyName() {
        return this.m_loginCompany
    }

    // Sets the logged in person to nobody
    LogOut() {
        this.m_loginName = null
        this.m_loginCompany = null
        this.m_app.state.accessToken = undefined
        window.location.href = this.m_idServerUrl + "connect/endsession";
    }

    // Sets the logged in person to the given name and company name (will be displayed up to the right of the toolbar buttons)
    LogIn(name: String, company: String, role:number) {
        if (name != null && company != null) {
            this.m_loginName = name
            this.m_loginCompany = company
            this.m_userRole = role

            if (role == 3) { // admin
                this.m_userBuildPartOn = true
            }
            else {
                this.m_userBuildPartOn = defaultBuildpartsAccessed

                if (PADebug.IsDebuging() == true) { // Buildparts is available for development 
                    this.m_userBuildPartOn = true
                }
            }

            this.m_app.forceUpdate()
        }
    }
    
    // Enforces an update on whatever item is currently selected
    UpdateSelectedItem() {
        var selectedArticleItem = this.GetSelectedArticleItem();
        if (selectedArticleItem != null) {
            selectedArticleItem.forceUpdate()
        }

        var selectedSelectionItem = this.GetSelectedSelectionItem();
        if (selectedSelectionItem != null) {
            selectedSelectionItem.forceUpdate()
        }
    }

    // ClearFocus exits any ongoing inputs by moving the focus away from it.
    ClearFocus() {
        // Making sure any item allready being inserted value into gets out of that state, and if so cancelling that input
        if (this.m_inputItemArticleView != null) {
            var handle1 = this.m_inputItemArticleView
            this.m_inputItemArticleView = null // Ensures that the previous core's value won't still be there on a refresh
            handle1.onMouseOut()
            handle1.forceUpdate() // Refreshes the previous item
        }

        if (this.m_inputItemSelectionView != null) {
            var handle2 = this.m_inputItemSelectionView
            this.m_inputItemSelectionView = null // Ensures that the previous core's value won't still be there on a refresh
            handle2.onMouseOut()
            handle2.forceUpdate() // Refreshes the previous item
        }

        // ..And finally puting focus on the search input, once the other focusing procedures has been deactivated
        var field = document.getElementById("appWindow")
        if (field != null) {
            field.focus()
        }
    }

    // Returns a handle to the current ext info view
    GetExtInfoView() {
        return this.m_extInfoView
    }

    // Get the roll in value which tells if the last or first article item on a search result page should be selected due to a page change using the up/down keys
    GetRollIn() {
        return this.m_selectionRollIn
    }

    // Set the roll in value which tells if the last or first article item on a search result page should be selected due to a page change using the up/down keys
    SetRollIn(rollIn: number) {
        this.m_selectionRollIn = rollIn
    }

    // PerformRollIn selects the first or last item in a search result in the case that the up/down arrows lead to that the page of results changed
    PerformRollIn() {
        // A backward roll across the border will select the last item in the previous page of results
        if (this.GetRollIn() == -1) {
            this.SetRollIn(0)
            this.m_ArticleItemLast.onClick()
        }

        // A backward roll across the border will select the last item in the previous page of results
        if (this.GetRollIn() == 1) {
            this.SetRollIn(0)
            this.m_ArticleItemFirst.onClick()
        }
    }

    // Sets the handle to the first item within a page of search results
    SetArticleItemFirst(first: ArticleItemGW) {
        this.m_ArticleItemFirst = first
    }

    // Sets the handle to the last item within a page of search results
    SetArticleItemLast(last: ArticleItemGW) {
        this.m_ArticleItemLast = last
    }

    // Returns the most recently rendered set of articles (usually a page of search results but can also be favorites)
    GetRenderedArticleGroup() {
        return this.m_renderedArticleGroup
    }

    // Initiates the load bar for visible updates of the percentage
    InitLoadBarHandle(handle: LoadBarGW) {
        this.m_loadBarHandle = handle
    }

    // This causes an update of App if a new set of articles is to be displayed, this method is called with a delay on it, during rendering time of the article view
    UpdateAppStateTotalArticles() {
        this.m_app.setState({ resultTotalArticles: this.m_incomingResultLength, resultGroup: 0 })
    }

    // When a search result is changed, it will trigger some changes of the result showing controls if the amount differs from the previous, by comparing to this kept value.
    SetIncomingResultLength(newLength: number) {
        this.m_incomingResultLength = newLength
    }

    // Returns true if a specified nobb groups is loaded and ready to use
    isGroupLoaded(nobbGroup: string) {
        if (nobbGroup == "") {
            // this happens if no group has ever been chosen before this method is called
            return true
        }
        
        if (nobbGroup) {
            if (nobbGroup.slice(0, 11) == "withoutNobb") {
                var book = this.GetSelectedPriceBook()

                if (nobbGroup == "withoutNobb") {
                    // This is the head group of all the nobbless groups

                    if (book) {
                        if (book.GetNobbLessIsLoaded() == true) {
                            return true
                        }
                        else {
                            return false
                        }
                    }
                    else {
                        // All price books selected, checking if all uncathegorised articles from all pricebooks are loaded
                        for (var i = 0; i < this.m_priceBooks.length; i++) {
                            if (this.m_priceBooks[i].GetNobbLessIsLoaded() == false) {
                                return false
                            }
                        }

                        return true
                    }
                }
                else {
                    // This is a subgroup
                    if (book) {
                        var groups = book.GetNobbUnspecifiedGroups(false)

                        for (var i = 0; i < groups.length; i++) {
                            if (groups[i].GetIDMin() == nobbGroup) {
                                if (groups[i].GetLoadedStatus().status == 2) {
                                    // this.Debug("core.isGroupLoaded - found loaded group: " + nobbGroup)
                                    return true
                                }

                                else {
                                    // Group found but is not yet loaded
                                    // this.Debug("core.isGroupLoaded - found unloaded group: " + nobbGroup)
                                    return false
                                }
                            }
                        }

                        // Assert(false, "core.isGroupLoaded - group not found: " + nobbGroup)
                    }
                    else {

                        // All price books selected, checking a selection within a collective of cross pricebook groups 
                        var groupsAll = this.GetAllPriceBooksAlphabeticalGroups(false)

                        if (groupsAll) {
                            for (var i = 0; i < groupsAll.length; i++) {
                                if (groupsAll[i].GetIDMin() == nobbGroup) {
                                    if (groupsAll[i].GetLoadedStatus().status == 2) {
                                        return true
                                    }
                                    else {
                                        // Group found but is not yet loaded
                                        return false
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        var hierarchicalGroups = this.GetHierarchialGroups();
        if (hierarchicalGroups == null) {
            return false
        }

        for (var i = 0; i < hierarchicalGroups.length; i++) {
            if (hierarchicalGroups[i].GetGroupID() == nobbGroup) {
                if (hierarchicalGroups[i].GetLoadedStatus().status == 2) {
                    return true
                }
                else {
                    return false
                }
            }
        }

        PADebug.Log("core.isGroupLoaded : the requested nobb group did not match any of the nobb groups within the used nobb hierarchy - nobbgroup: " + nobbGroup)
        return false
    }

    // Returns true if the last selected nobb group is loaded and ready for searches within
    isCurrentCathLoaded() {
        return this.isGroupLoaded(this.m_lastSelectedNobbGroup)
    }

    // Sets the flag telling if the load bar for search field is currently mounted and can recieve update requests (without warnings)
    SetLoadBarMounted(monted: boolean) {
        this.m_loadBarIsMounted = monted
    }

    // Arranging pricebooks in alphabetical order by their title
    SortPriceBooks() {
        this.m_priceBooks.sort(function (a, b) {
            var x = a.GetTitle();
            var y = a.GetTitle();
            if (x < y) { return -1; }
            if (x > y) { return 1; }
            return 0;
        })
    }

    // Returns the loaded contents for the prislister dropdown menu or a replacement if not yet loaded (will trigger the loading of it)
    GetContentStringForPrislister() {
        this.SortPriceBooks()

        var cont = new Array<any>()
        var instance: {
            "shownText": string,
            "FileBase": string,
            "id": number
        }

        for (var i = 0; i < this.m_priceBooks.length; i++) {
            instance = {
                "shownText": this.m_priceBooks[i].GetTitle(),
                "FileBase": this.m_priceBooks[i].GetFileBase(),
                "id": this.m_priceBooks[i].GetPriceBookId()
            }

            cont.push(instance)
        }

        if (this.m_priceBooks.length > 1) {
            instance = {
                "shownText": "Alle prisbøker",
                "FileBase": "",
                "id": -1
            }

            cont.push(instance)
        }

        return JSON.stringify(cont)
    }

    // Clears the quick report list of selected articles and returns all amounts to zero for those articles
    ClearAllQRArticles() {
        var ext: ExtDataGW

        for (var i = 0; i < this.m_selectedArticles.length; i++) {
            this.m_selectedArticles[i].ClearExtData()
            this.m_selectedArticles[i].SetAmount(0)
        }

        this.m_selectedArticles = new Array<ArticleGW>()
    }

    // Resets the amount of all buildparts to 0 and clears the selection of buildparts
    ClearAllQRBuildParts() {
        for (var i = 0; i < this.m_buildParts.length; i++) {
            this.m_buildParts[i].SetAmount(0)
        }

        this.m_selectedBuildParts = new Array<BuildPartGW>()

        this.m_app.forceUpdate()
    }

    // Prepares a selection set manager to be used
    InitiateSelectionSetManager(token: ClientOAuth2.Token) {
        this.m_selectionSetManager = new SelectionSetManagerGW(this.m_serverUrl)
        this.m_selectionSetManager.InitCore(this)
        this.m_selectionSetManager.SetAccessToken(token)
    }

    // First step in opening the load project view
    SelectionLoadMenuStepOne() {
        LoadFromUrl(this.m_serverUrl + "api/selection", this.SelectionLoadMenuStepTwo.bind(this), this.DefaultLoadErrorHandler.bind(this), this.GetApp().state.accessToken)
    }

    // Second step in opening the load project view
    SelectionLoadMenuStepTwo() {
        this.m_app.setState({ DisplayMode : this.m_app.DMode.projectLoad })
    }

    // Opening the build part menu, where stored buildparts are enlisted and managed (currently disabled functionality, but might be enabled again in a future pro version)
    BuildPartLoadMenu() {
        if (this.GetApp().state.DisplayMode == this.GetApp().DMode.byggdelLoad) {
            // returning to the initial state if this button is clicked while already in buildpart mode
            if (this.m_buildPartView != null) {
                this.m_buildPartView.setState({ stage: "initial" })
                this.UpdateBuildPartView()
            }
        }

        this.m_app.setState({ DisplayMode: this.m_app.DMode.byggdelLoad })
    }
    
    // Initiation: handler to the project view
    ConnectProjectView(pw: ProjectView) {
        this.m_projectView = pw
    }

    // Initiation: handler to the new project view
    ConnectNewProjectView(npw: NewProjectView) {
        this.m_newProjectView = npw
    }
    
    // Initiation: handler to the project save view
    ConnectProjectSaveView(psw: ProjectSaveView) {
        this.m_projectSaveView = psw
    }

    // Initiation: handler to the project view
    ConnectBuildPartView(pw: BuildPartView) {
        this.m_buildPartView = pw
    }

    // Initiation: handler to the project save view
    ConnectBuildPartSaveView(psw: BuildPartSaveView) {
        this.m_buildPartSaveView = psw
    }

    // Initiation: handler to the favorites delete view
    ConnectFavoritesDeleteView(fdv: FavoritesDeleteView) {
        this.m_favoritesDeleteView = fdv
    }

    // Access: handler to the project view
    GetProjectView() {
        return this.m_projectView
    }

    // Access: handler to the project view
    GetBuildPartView() {
        return this.m_buildPartView
    }
    
    // Access to the (single) selection set manager where selections are transfered to and from the server
    GetSelectionSetManager() {
        return this.m_selectionSetManager
    }

    // Reciving and inserting article selections loaded from the server account data
    onSelectionLoad(inputData: string) {
        var sel = new SelectionImportObject()
        sel.SetCoreReference(this)
        sel.GenerateObjects(inputData)
        sel.InsertNextObject() // this will begin the process of inserting the new selection insert objects, which will go on until all amounts are inserted
    }

    // Reciving and inserting favorites from the server
    onFavoritesLoad(favData: string) {
        var obj = JSON.parse(favData)
        var result = new Array<ArticleGW>()

        for (var pb = 0; pb < obj.length; pb++) {
            for (var g = 0; g < obj[pb].groups.length; g++) {
                for (var i = 0; i < obj[pb].groups[g].items.length; i++) {
                    const ar = obj[pb].groups[g].items[i]
                    const newArt = new ArticleGW(ar.id, ar.eanNo, ar.identifier, ar.identifierOld, obj[pb].groups[g].groupId, ar.description, ar.description2, ar.descriptionUrl,
                        "", "", ar.packageAmount, ar.unit, ar.pricePurchase, ar.priceBase, ar.priceRecommended, ar.priceFactory, ar.packageUnit, "NOK", ar.nobb, ar.nobbOwner, ar.nobbModule,
                        ar.priceType, ar.itemType, ar.leverandor, 0, ar.discountFactor, obj[pb].id)

                    result.push(newArt)
                }
            }
        }
        
        this.m_favorites = result
        this.SortFavorites()
    }
    
    // This will insert the values of one SelectionImportObject into the corresponding selectionArticle (which should already have extInfo applied to it), or
    // if the extended data is missing, a call for it will be made before the amount will be added and the insertion cycle will continue
    public SelectionLoadObject(sio: SelectionImportObject) {

        // checking towards the current selection (to add amounts rather than inserting articles)
        for (var i = 0; i < this.m_selectedArticles.length; i++) {
            if (this.m_selectedArticles[i].GetArticleID() == sio.Get_id()) {
                
                const target = this.m_selectedArticles[i]
                
                sio.SetTargetArticle(target)
                sio.CompleteAmountInsertion()

                return
            }
        }
        
        var foundArticle: ArticleGW | null = null

        // checking towards favorites 
        for (var j = 0; j < this.m_favorites.length; j++) {
            if (sio.Get_id() == this.m_favorites[j].GetArticleID()) {
                foundArticle = this.m_favorites[j]
            }
        }

        // checking towards cashed articles
        var alphabeticalGroups = this.GetAlphabeticalGroups(false);
        if (alphabeticalGroups != null) {
            for (var cGrp = 0; cGrp < alphabeticalGroups.length; cGrp++) {
                if (alphabeticalGroups[cGrp].GetGroupID() == sio.Get_groupIdTree()) {
                    var group = alphabeticalGroups[cGrp]
                    if (group.GetLoadedStatus() == { status: 2 }) { // 2 means the group has been loaded and is ready
                        for (var ar = 0; ar < group.GetArticles().length; ar++) {
                            if (group.GetArticles()[ar].GetArticleID() == sio.Get_id()) {
                                // we have found an instance of the corresponding article among the cashed ones. It will be used.
                                foundArticle = group.GetArticles()[ar]
                            }
                        }
                    }
                    else {
                        // group is found but is not yet loaded, so then this check will be skipped..
                        break;
                    }
                }
            }
        }
        
        if (foundArticle != null) {
            this.m_selectedArticles.push(foundArticle)
            sio.SetTargetArticle(foundArticle)
            sio.CompleteAmountInsertion()
            this.SortSelectedArticles()
        }
        else {
            // No corresponding article was located, so instead we will create and insert a new one..
            var newArticle: ArticleGW = new ArticleGW(sio.Get_id(), sio.Get_eanNo(), sio.Get_identifier(), sio.Get_identifierOld(), sio.Get_groupIdTree(), sio.Get_description(), sio.Get_description2(), "", "", "", sio.Get_packageAmount(), sio.Get_unit(), sio.Get_pricePurchase(), sio.Get_priceBase(), sio.Get_priceRecommended(), sio.Get_priceFactory(), sio.Get_packageUnit(), "NOK", sio.Get_nobb(), sio.Get_nobbOwner(), sio.Get_nobbModule(), sio.Get_priceType(), sio.Get_itemType(), sio.Get_supplierID(), 0, sio.Get_discountFactor(), sio.Get_prizeBook())
            this.m_selectedArticles.push(newArticle)
            sio.SetTargetArticle(newArticle)
            sio.CompleteAmountInsertion()
            this.SortSelectedArticles()
        }
    }

    // OnSelectionLoadObject handles the arriving extData string from the server for an article which were previously missing ext Data 
    public OnSelectionLoadObject(sio: SelectionImportObject, data: string) {
        var extD = new ExtDataGW()
        var hog: any = JSON.parse(data)
        extD.RecieveData(hog)

        // Checking if the target article matches the extended data (just for debug reasons)
        if (extD.GetNobbNr().toString() == sio.GetTargetArticle().GetNobb()) {
            sio.GetTargetArticle().SetExtData(extD)
            extD.DirectSubChoise(sio.GetTargetArticle())
        }
        else {
            Assert(false, "core.OnSelectionLoadObject - it looks like the target article and the recived ext data does not match..!? ")
        }

        // Now the article should be ready to insert as a selection article
        this.m_selectedArticles.push(sio.GetTargetArticle())
        
        // insertion complete, cycle will be continued...
        sio.CompleteAmountInsertion()
        this.SortSelectedArticles()
    }

    // 
    public GetSelectedPriceBookID() {
        if (this.m_selectedPriceBookID == null) {
            if (this.m_defaultPriceBook != undefined) {
                const defaultID = this.m_defaultPriceBook

                if (this.m_priceBooks.length > 0) {
                    for (var book = 0; book < this.m_priceBooks.length; book++) {
                        if (this.m_priceBooks[book].GetPriceBookId().toString() == defaultID) {
                            const newID = this.m_priceBooks[book].GetPriceBookId()
                            this.m_selectedPriceBookID = newID
                            return newID
                        }
                    }

                    Assert(false, "core.GetSelectedPriceBookID - the requested default pricebook ID was not found among the available price books!")
                }
                else {
                    Assert(false, "core.GetSelectedPriceBookID - the amount of pricebooks was 0")
                }
            }
            else {
                if (this.m_priceBooks.length > 0) {
                    this.m_selectedPriceBookID = this.m_priceBooks[0].GetPriceBookId()
                    return this.m_priceBooks[0].GetPriceBookId()
                }
                else {
                    Assert(false, "core.GetSelectedPriceBookID - the amount of pricebooks was 0")
                }

                Assert(false, "core.GetSelectedPriceBookID - m_defaultPriceBook was undefined")
            }
            
            this.m_selectedPriceBookID = -1
            return -1 // All pricebooks is default return value if no other is given
        }
        else {
            return this.m_selectedPriceBookID
        }
    }

    // 
    public GetSelectedPriceBookIndex() {

        if (this.GetSelectedPriceBookID() == -1) { // All price books
            return -1
        }

        for (var i = 0; i < this.m_priceBooks.length; i++) {
            if (this.m_priceBooks[i].GetPriceBookId() == this.GetSelectedPriceBookID()) {
                return i
            }
        }

        Assert(false, "core.GetSelectedPriceBookIndex - the selected pricebook was unknown!")
        return -2
    }

    // Returns the currently selected price book
    public GetSelectedPriceBook() {

        if (this.GetSelectedPriceBookID() == -1) { // All price books
            return null
        }

        for (var i = 0; i < this.m_priceBooks.length; i++) {
            if (this.m_priceBooks[i].GetPriceBookId() == this.GetSelectedPriceBookID()) {
                return this.m_priceBooks[i]
            }
        }

        Assert(false, "core.GetPriceBook - the selected pricebook was unknown!")
        return null
    }

    // Returns the pricebook that matches the given pricebook ID if it is loaded
    public GetPriceBookByID(bookID: number) {

        for (var i = 0; i < this.m_priceBooks.length; i++) {
            if (this.m_priceBooks[i].GetPriceBookId() == bookID) {
                return this.m_priceBooks[i]
            }
        }

        Assert(false, "core.GetPriceBookByID - the selected pricebook was not found!")
        return null
    }

    // Sets the selected pricebook ID
    public SetPricebook(index: number) {
        if (index > this.m_priceBooks.length) {
            Assert(false, "core.SetPricebook - given index was out of the m_priceBooks reach!")
            return
        }

        if ((index > 1) && (index == this.m_priceBooks.length)) {  // All price books
            this.m_selectedPriceBookID = -1
            
            this.UpdateCrossPriceBookHierarchy()
            this.GetApp().forceUpdate()
        }
        else {
            this.m_selectedPriceBookID = this.m_priceBooks[index].GetPriceBookId()
            
            if (this.GetTreeView() != null) {
                this.GetTreeView().setState({ hasDerivedFromPreviousSelection: false })
            }

            this.GetApp().forceUpdate()
        }
    }
    
    // Method used for preserving a selection of nobb group through a change in pricebook
    public PassOnPreviousNobbGroupSelection() {
        var previousNobbGroup = this.m_lastSelectedNobbGroup
    }

    public GetFullData() {
        var pricebook = this.GetSelectedPriceBook();
        if (pricebook != null)
            return pricebook.GetFullData()
    }

    // access to the presently selected price book's Alphabetical nobb groups array
    public GetAlphabeticalGroups(withFirstLetterHierarchy:boolean) {
        var pricebook = this.GetSelectedPriceBook();

        if ((this.m_selectedPriceBookID != -1) && (pricebook != null)) {
            return pricebook.GetAlphabeticalGroups(withFirstLetterHierarchy)
        }
        else {
            return this.GetAllPriceBooksAlphabeticalGroups(withFirstLetterHierarchy)
        }
    }

    // access to the presently selected price book's hierarchial nobb groups array
    public GetHierarchialGroups() {
        var pricebook = this.GetSelectedPriceBook();

        if ((this.m_selectedPriceBookID != -1) && (pricebook != null)) {
            return pricebook.GetHierarchialGroups()
        }
        else {
            return this.GetAllPriceBooksHierarchialGroups()
        }
    }

    // 
    public GetAllPriceBooksHierarchialGroups() {
        if ((this.m_allPriceBookHierarchyNeedRefresh == true) || (this.m_allPriceBooksHierarchialGroups == undefined)) {
            this.m_allPriceBookHierarchyNeedRefresh = false
            this.GenerateAllPriceBooksHierarchialGroups()
        }

        return this.m_allPriceBooksHierarchialGroups
    }
 
    // 
    public GetAllPriceBooksAlphabeticalGroups(withFirstLetterHierarchy: boolean) {
        // this.GenerateAlphabeticalHierarchyAllBooks()
        if ((this.m_allPriceBookHierarchyNeedRefresh == true) || (this.m_allPriceBooksHierarchialGroups == undefined)) {
            this.m_allPriceBookHierarchyNeedRefresh = false
            this.GenerateAllPriceBooksHierarchialGroups()
            this.GenerateAlphabeticalHierarchyAllBooks()
        }

        if (withFirstLetterHierarchy == true) {
            var hi = this.m_allPriceBooksAlphabeticalGroupsHierarchial

            if (hi) {
                return hi
            }
            else {
                this.GenerateAlphabeticalHierarchyAllBooks()
                return this.m_allPriceBooksAlphabeticalGroupsHierarchial
            }
        }
        else {
            return this.m_allPriceBooksAlphabeticalGroups
        }
    }

    // Genertes an alphbetically sorted group structure gathering groups from all price books into one alphabetical tree
    GenerateAlphabeticalHierarchyAllBooks() {
        var ag = this.m_allPriceBooksAlphabeticalGroups
        var prevLetter = "£"
        var alphaGroup: ArticleGroupGW
        if (ag) {
            this.m_allPriceBooksAlphabeticalGroupsHierarchial = new Array<ArticleGroupGW>()

            for (var i = 0; i < ag.length; i++) {
                if (ag[i].GetAlphaPos().length > 0) {
                    if (ag[i].GetAlphaPos().charAt(0) == prevLetter) {
                        this.m_allPriceBooksAlphabeticalGroupsHierarchial.push(ag[i])
                    }
                    else {
                        prevLetter = ag[i].GetAlphaPos().charAt(0)
                        alphaGroup = new ArticleGroupGW("99_splitLetter", "00", prevLetter.toUpperCase(), 1, prevLetter.toUpperCase())
                        this.m_allPriceBooksAlphabeticalGroupsHierarchial.push(alphaGroup)
                        this.m_allPriceBooksAlphabeticalGroupsHierarchial.push(ag[i])
                    }
                }
            }
        }
    }
    
    // UpdateCrossPriceBookHierarchy regenerates the global hierarchy of nobb groups and is called for example when a new group has been loaded.
    public UpdateCrossPriceBookHierarchy() {
        PADebug.Log("core.UpdateCrossPriceBookHierarchy called")
        this.m_allPriceBookHierarchyNeedRefresh = true

        if ((this.m_treeView != null) && (this.m_selectedPriceBookID == -1)) {
            this.m_treeView.setState({ hasDerivedFromPreviousSelection: false })
            this.m_treeView.forceUpdate()
        }
    }
    
    // Collects nobb groups from all price books and gather them into a set of nobb groups that will contain everything, 
    // both in form of nobb hierarchial and an aplhabetical array of nobb groups (at the bottom level of the nobb hierarchy). 
    public GenerateAllPriceBooksHierarchialGroups() {
        this.m_allPriceBooksHierarchialGroups = new Array<ArticleGroupGW>()
        this.m_allPriceBooksAlphabeticalGroups = new Array<ArticleGroupGW>()
        this.m_allPriceBooksNobblessGroups = new Array<ArticleGroupGW>()

        for (var i = 0; i < this.m_priceBooks.length; i++) {
            var hierarcs = this.m_priceBooks[i].GetHierarchialGroups()
            var couldBeNobless = false

            if (hierarcs == undefined) {
                couldBeNobless = true
            }
            else {
                if (hierarcs.length == 0) {
                    couldBeNobless = true
                }
            }
            
            if (couldBeNobless == true) {
                
                /* found undefined or empty hierarchial groups. 
                   This is a price book which doesn't use any nobb hierarchy, as with some custom price books. 
                   It might still have groups within it, which will be added as items into the alphabetical tree. */

                var nobbless = this.m_priceBooks[i].GetNobbUnspecifiedGroups(false)
                
                if (!nobbless) {
                    continue
                }
                
                for (var t = 0; t < nobbless.length; t++) {
                    var groupNo = nobbless[t].GetDescription()
                    var found = false
                    var foundGroup: ArticleGroupGW | null = null

                    for (var n = 0; n < this.m_allPriceBooksNobblessGroups.length; n++) {
                        if (this.m_allPriceBooksNobblessGroups[n].GetDescription() == groupNo) {
                            found = true
                            foundGroup = this.m_allPriceBooksNobblessGroups[n]
                            break
                        }
                    }

                    if (found == false) {
                        // this group is not yet represented within the new array

                        const original = nobbless[t]
                        var newGroup = new ArticleGroupGW(original.GetIDMin(), original.GetIDMax(), original.GetDescription(), original.GetLevel(), original.GetAlphaPos())

                        newGroup.SetLoadedStatus(original.GetLoadedStatus())

                        for (var l = 0; l < original.GetArticles().length; l++) {
                            newGroup.insert(original.GetArticles()[l])
                        }

                        this.m_allPriceBooksNobblessGroups.push(newGroup)
                    }
                    else {
                        const source = nobbless[t]

                        if (foundGroup != null) {
                            for (var l = 0; l < source.GetArticles().length; l++) {
                                foundGroup.insert(source.GetArticles()[l])
                            }

                            if (source.GetLoadedStatus().status < foundGroup.GetLoadedStatus().status) {
                                foundGroup.SetLoadedStatus(source.GetLoadedStatus())
                            }
                        }
                    }
                }

                continue
            }

            for (var j = 0; j < hierarcs.length; j++) {
                var groupNr = hierarcs[j].GetGroupID()
                var found = false
                var foundGroup: ArticleGroupGW | null = null

                for (var k = 0; k < this.m_allPriceBooksHierarchialGroups.length; k++) {
                    if (this.m_allPriceBooksHierarchialGroups[k].GetGroupID() == groupNr) {
                        found = true
                        foundGroup = this.m_allPriceBooksHierarchialGroups[k]
                        break
                    }
                }

                if (found == false) {
                    // this group is not yet represented within the new array

                    const original = hierarcs[j]
                    var newGroup = new ArticleGroupGW(original.GetIDMin(), original.GetIDMax(), original.GetDescription(), original.GetLevel(), original.GetAlphaPos())

                    newGroup.SetLoadedStatus( original.GetLoadedStatus() )

                    for (var l = 0; l < original.GetArticles().length; l++) {
                        newGroup.insert(original.GetArticles()[l])
                    }

                    this.m_allPriceBooksHierarchialGroups.push(newGroup)
                }
                else {
                    const source = hierarcs[j]

                    if (foundGroup != null) {
                        for (var l = 0; l < source.GetArticles().length; l++) {
                            foundGroup.insert(source.GetArticles()[l])
                        }

                        if (source.GetLoadedStatus().status < foundGroup.GetLoadedStatus().status) {
                            foundGroup.SetLoadedStatus(source.GetLoadedStatus())
                        }
                    }
                }
            }
        }

        /* We will sort the hierarchial groups by their minimum values */
        this.m_allPriceBooksHierarchialGroups.sort(function (a, b) {
            var x = a.GetIDMin();
            var y = b.GetIDMin();
            if (x < y) { return -1; }
            if (x > y) { return 1; }
            return 0;
        });

        // Preparing the groups to look right in a tree structure by removing the hierarchial 
        // connection towards L1 from undergroups at the end of the array
        var isLastBorn = true
        for (var i = this.m_allPriceBooksHierarchialGroups.length - 1; i >= 0; i--) {
            if (this.m_allPriceBooksHierarchialGroups[i].GetLevel() == 1) {
                isLastBorn = true
            }
            else {
                if (this.m_allPriceBooksHierarchialGroups[i].GetLevel() == 2) {
                    this.m_allPriceBooksHierarchialGroups[i].SetLastBorn(isLastBorn, 101)
                    isLastBorn = false
                }
                else {
                    this.m_allPriceBooksHierarchialGroups[i].SetLastBorn(isLastBorn, 101)
                }
            }
        }
        
        var indexReach = 0

        if (this.m_allPriceBooksNobblessGroups) {
            var indexReach = this.m_allPriceBooksNobblessGroups.length
        }

        /* Now that the complete nobb hierarchy is generated, we'll collect references of the leef groups into one
         * array and sort it alphabetically and store it for displaying all price books nobb groups as alphabetical groups.*/
        for (var e = 0; e < this.m_allPriceBooksHierarchialGroups.length; e++) {
            if (this.m_allPriceBooksHierarchialGroups[e].GetLevel() == 3) {
                if (this.m_allPriceBooksNobblessGroups) {
                    // Checking group titles to see if there is a match towards nobb unspecifed groups, if so then they will be fused into one group.
                    const ap = this.m_allPriceBooksHierarchialGroups[e].GetAlphaPos()

                    for (var nlg = 0; nlg < indexReach; nlg++) {
                        if (ap == this.m_allPriceBooksNobblessGroups[nlg].GetAlphaPos()) {
                            // Fusing the nobbless group into the hierarchial group

                            const base = this.m_allPriceBooksHierarchialGroups[e]
                            const add = this.m_allPriceBooksNobblessGroups[nlg]
                            
                            for (var l = 0; l < add.GetArticles().length; l++) {
                                base.insert(add.GetArticles()[l])
                            }

                            if (add.GetLoadedStatus().status < base.GetLoadedStatus().status) {
                                base.SetLoadedStatus(add.GetLoadedStatus())
                            }

                            // removing the nobbless group from the checked collection
                            if (indexReach > 1) {
                                this.m_allPriceBooksNobblessGroups[nlg] = this.m_allPriceBooksNobblessGroups[indexReach - 1]
                            }

                            indexReach--
                            break
                        }
                    }
                }

                this.m_allPriceBooksAlphabeticalGroups.push(this.m_allPriceBooksHierarchialGroups[e])
            }
        }

        // Throwing in the remaining nobb unspecified groups into the alphabetical hierarchy
        if (this.m_allPriceBooksNobblessGroups) {

            for (var nlg = 0; nlg < indexReach; nlg++) {
                this.m_allPriceBooksAlphabeticalGroups.push(this.m_allPriceBooksNobblessGroups[nlg])
            }
        }
          
        /* and finally the alphabetical array is sorted */
        this.m_allPriceBooksAlphabeticalGroups.sort(function (a, b) {
            var x = a.GetAlphaPos();
            var y = b.GetAlphaPos();
            if (x < y) { return -1; }
            if (x > y) { return 1; }
            return 0;
        });

        // The alphabetically sorted array is ready, however, to display it correctly the visible relation between
        // L1 and L2 must be shown or hidden depending on the position of the undergroup, this has already been
        // adjusted for the hierarchial groups, so in order not to mess up that adjustment, and still being able
        // to show the alphabetical tree correctly, a copied set of groups will be used instead, where the connection
        // is set depending on alphabetical position rather than the nobb hierarchial position

        var newAlpha = this.m_allPriceBooksAlphabeticalGroups

        // Preparing the newAlpha to look right in a tree structure by removing the hierarchial
        // connection towards L1 from undergroups at the end of the array
        var isLastBorn = true
        for (var i = newAlpha.length - 1; i >= 0; i--) {
            if (newAlpha[i].GetLevel() == 1) {
                isLastBorn = true
            }
            else {
                if (newAlpha[i].GetLevel() == 2) {
                    newAlpha[i].SetLastBorn(isLastBorn, 100)
                    isLastBorn = false
                }
                else {
                    newAlpha[i].SetLastBorn(isLastBorn, 100)
                }
            }
        }
        
    }
    
    // Returns a description string for a given pricebook id
    public GetPriceBookDescription(bookID: number) {
        var book = this.GetPriceBookByID(bookID)

        if (book != null) {
            return book.GetTitle()
        }

        return "(untitled) ID:" + bookID.toString()
    }

    // Returns true if current mode is to show nobb groups as an alphabetical list of sub groupa rather than a hierarchial tree
    public GetTreeIsAlphabetical() {
        if (this.m_alphabeticalDisplay == true) {
            return true
        }
        else {
            return false
        }
    }

    // Sets the mode to (if true) show nobb groups as an alphabetical list of sub groupa rather than a hierarchial tree
    public SetTreeIsAlphabetical(mode: boolean) {
        this.m_alphabeticalDisplay = mode

        if (this.GetTreeView() != null) {
            this.GetTreeView().setState({ hasDerivedFromPreviousSelection: false })
        }
    }

    // Updates the tree view with a new group ID to determine what items should be expanded 
    public ExpandGroupInTreeView(group: string) {
        if (this.GetTreeView() != null) {
            this.GetTreeView().ExpandBySelection(group)
        }
    }

    // Returns the constant setting for how many decimals should be shown after a comma sign, globally for various 
    // values (while not affecting the precision of the actual values). The constant can be edited near the beginning of this file.
    public Get_max_Shown_Decimal_Amount() {
        return Max_Shown_Decimal_Amount
    }

    // GroupIsWithinPricebook returns true if a given nobbgroup ID can be found within the currently chosen pricebook (at any hierarchial level)
    public GroupIsWithinPricebook(groupID: string) {
        var groups = this.GetHierarchialGroups()
        if (groups == null) return false;

        if (groupID.length == 2) {
            for (var i = 0; i < groups.length; i++) {

                if (groupID.length == 2) {
                    if (groupID.charAt(0) != groups[i].GetGroupID().charAt(0)) {
                        continue
                    }

                    if (groupID.charAt(1) == groups[i].GetGroupID().charAt(1)) {
                        return true
                    }
                }
            }
        }

        if (groupID.length == 4) {
            for (var i = 0; i < groups.length; i++) {

                if (groupID.length == 4) {
                    if (groupID.charAt(0) != groups[i].GetGroupID().charAt(0)) {
                        continue
                    }

                    if (groupID.charAt(1) != groups[i].GetGroupID().charAt(1)) {
                        continue
                    }

                    if (groupID.charAt(2) != groups[i].GetGroupID().charAt(2)) {
                        continue
                    }

                    if (groupID.charAt(3) == groups[i].GetGroupID().charAt(3)) {
                        return true
                    }
                }
            }
        }

        if (groupID.length == 7) {
            for (var i = 0; i < groups.length; i++) {

                if (groupID.length == 7) {
                    if (groupID.charAt(0) != groups[i].GetGroupID().charAt(0)) {
                        continue
                    }

                    if (groupID.charAt(1) != groups[i].GetGroupID().charAt(1)) {
                        continue
                    }

                    if (groupID.charAt(2) != groups[i].GetGroupID().charAt(2)) {
                        continue
                    }

                    if (groupID.charAt(3) != groups[i].GetGroupID().charAt(3)) {
                        continue
                    }

                    if (groupID.charAt(4) != groups[i].GetGroupID().charAt(4)) {
                        continue
                    }

                    if (groupID.charAt(5) != groups[i].GetGroupID().charAt(5)) {
                        continue
                    }

                    if (groupID.charAt(6) != groups[i].GetGroupID().charAt(6)) {
                        continue
                    }

                    if (groupID.charAt(7) == groups[i].GetGroupID().charAt(7)) {
                        return true
                    }
                }
            }
        }
        
        return false
    }

    // This starts a countdown to saving all changes thats recently been made to favorites if there isn't already one started.
    public UpdateFavorites() {
        if (this.m_favoritesUpdateStage == 0) {
            this.m_favoritesUpdateStage = 10
            this.FavoritesCountdown()
        }
    }

    // Stepwise countdown timer
    FavoritesCountdown() {
        if (this.m_favoritesUpdateStage > 0) {
            this.m_favoritesUpdateStage--

            if (this.m_favoritesUpdateStage > 0) {
                setTimeout(this.FavoritesCountdown.bind(this), 100)
            }
            else {
                this.GetSelectionSetManager().SaveFavorites()
            }
        }
        else {
            this.m_favoritesUpdateStage = 0
        }
    }

    // Access : buildparts
    public GetBuildParts() {
        return this.m_buildParts
    }

    // Access : buildparts
    public GetProjectSummaries() {
        return this.m_projectSummaries
    }
    
    // Access : selected buildparts
    public GetSelectedBuildParts() {
        return this.m_selectedBuildParts
    }

    // Access : buildparts
    public SetBuildParts(buildParts: Array<BuildPartGW>) {
        this.m_buildParts = buildParts
    }

    // Access : project summaries
    public SetProjectSummaries(summaries: Array<BuildPartGW>) {
        this.m_projectSummaries = summaries
    }

    // Access : selected buildparts
    public SetSelectedBuildParts(buildParts: Array<BuildPartGW>) {
        this.m_selectedBuildParts = buildParts
    }

    // Causing build parts to be queuedly loaded from the server using the provided selection sets for them (previously given from the server)
    public LoadBuildParts(BuildPartSets: Array<SelectionSetGW>, projectSets: Array<SelectionSetGW>) {
        this.m_buildPartLoadingSets = new Array<SelectionSetGW>()
        this.m_projectLoadingSets = new Array<SelectionSetGW>()

        for (var i = 0; i < BuildPartSets.length; i++) {
            this.m_buildPartLoadingSets.push(BuildPartSets[i])
        }

        for (var i = 0; i < projectSets.length; i++) {
            this.m_projectLoadingSets.push(projectSets[i])
        }

        this.m_buildParts = new Array<BuildPartGW>()
        this.m_projectSummaries = new Array<BuildPartGW>()

        this.LoadNextBuildPart()
    }

    // Processing another build part queued to be loaded from the server 
    public LoadNextBuildPart() {

        if ((this.m_buildPartLoadingSets == null) || (this.m_projectLoadingSets == null)) {
            return
        }

        if ((this.m_buildPartLoadingSets.length == 0) && (this.m_projectLoadingSets.length == 0)) {
            this.m_buildPartLoadingSets = null
            this.m_projectLoadingSets = null
            this.InterConnectBuildparts()
            this.m_app.forceUpdate()
            return
        }

        if (this.m_buildPartLoadingSets.length > 0) {
            var set = this.m_buildPartLoadingSets.pop()
            this.m_selectionSetManager.loadBuildPart(set)
            return
        }
        
        if (this.m_projectLoadingSets.length > 0) {
            var set = this.m_projectLoadingSets.pop()
            this.m_selectionSetManager.loadProjectSummary(set)
            return
        }
    }

    // A check if there are more build parts queued for generation
    public HasUnloadedBuildParts() {
        if (this.m_buildPartLoadingSets != null) {
            if (this.m_buildPartLoadingSets.length > 0) {
                return true
            }
        }

        return false
    }

    // Handles the response from the server upon each recived build part, then proceeds with the queued ones  
    public OnBuildPartLoaded(data: string) {
        var obj = JSON.parse(data)
        var result: Array<ArticleGW> = new Array<ArticleGW>()
        var part: BuildPartGW = new BuildPartGW(this.m_buildPartLoadingSet, this)

        part.SetId(this.m_buildPartLoadingSet.GetId())
        part.SetMeta(this.m_buildPartLoadingSet.GetMeta())
        part.SetTitle(this.m_buildPartLoadingSet.GetName())

        for (var pb = 0; pb < obj.length; pb++) {
            for (var g = 0; g < obj[pb].groups.length; g++) {
                for (var i = 0; i < obj[pb].groups[g].items.length; i++) {
                    const newArticle: ArticleGW = new ArticleGW(
                        obj[pb].id,
                        obj[pb].groups[g].items[i].eanNo,
                        obj[pb].groups[g].items[i].identifier,
                        obj[pb].groups[g].items[i].identifierOld,
                        obj[pb].groups[g].id,
                        obj[pb].groups[g].items[i].description,
                        obj[pb].groups[g].items[i].description2,
                        "",
                        "",
                        "",
                        obj[pb].groups[g].items[i].packageAmount,
                        obj[pb].groups[g].items[i].unit,
                        obj[pb].groups[g].items[i].pricePurchase,
                        obj[pb].groups[g].items[i].priceBase,
                        obj[pb].groups[g].items[i].priceRecommended,
                        obj[pb].groups[g].items[i].priceFactory,
                        obj[pb].groups[g].items[i].packageUnit,
                        "NOK",
                        obj[pb].groups[g].items[i].nobb,
                        obj[pb].groups[g].items[i].nobbOwner,
                        obj[pb].groups[g].items[i].nobbModule,
                        obj[pb].groups[g].items[i].priceType,
                        "",
                        "",
                        obj[pb].groups[g].items[i].amount,
                        obj[pb].groups[g].items[i].discountFactor,
                        obj[pb].id)

                    result.push(newArticle)
                }
            }
        }

        part.SetArticles(result)
        this.m_buildParts.push(part)
        this.UpdateBuildPartView()
        this.LoadNextBuildPart()
    }

    // Handles the response from the server upon each recived project summary, then proceeds with the queued ones  
    public OnProjectSummaryLoaded(data: string) {
        var obj = JSON.parse(data)
        var result: Array<ArticleGW> = new Array<ArticleGW>()
        var part: BuildPartGW = new BuildPartGW(this.m_projectSummaryLoadingSet, this)

        part.SetId(this.m_projectSummaryLoadingSet.GetId())
        part.SetMeta(this.m_projectSummaryLoadingSet.GetMeta())
        part.SetTitle(this.m_projectSummaryLoadingSet.GetName())

        for (var pb = 0; pb < obj.length; pb++) {
            for (var g = 0; g < obj[pb].groups.length; g++) {
                for (var i = 0; i < obj[pb].groups[g].items.length; i++) {
                    const newArticle: ArticleGW = new ArticleGW(
                        obj[pb].id,
                        obj[pb].groups[g].items[i].eanNo,
                        obj[pb].groups[g].items[i].identifier,
                        obj[pb].groups[g].items[i].identifierOld,
                        obj[pb].groups[g].id,
                        obj[pb].groups[g].items[i].description,
                        obj[pb].groups[g].items[i].description2,
                        "",
                        "",
                        "",
                        obj[pb].groups[g].items[i].packageAmount,
                        obj[pb].groups[g].items[i].unit,
                        obj[pb].groups[g].items[i].pricePurchase,
                        obj[pb].groups[g].items[i].priceBase,
                        obj[pb].groups[g].items[i].priceRecommended,
                        obj[pb].groups[g].items[i].priceFactory,
                        obj[pb].groups[g].items[i].packageUnit,
                        "NOK",
                        obj[pb].groups[g].items[i].nobb,
                        obj[pb].groups[g].items[i].nobbOwner,
                        obj[pb].groups[g].items[i].nobbModule,
                        obj[pb].groups[g].items[i].priceType,
                        "",
                        "",
                        obj[pb].groups[g].items[i].amount,
                        obj[pb].groups[g].items[i].discountFactor,
                        obj[pb].id)

                    result.push(newArticle)
                }
            }
        }
        
        part.SetArticles(result)
        this.m_projectSummaries.push(part)
        this.LoadNextBuildPart()
    }

    /* Interconnect buildparts ensures that all the loaded build parts have established references to all build parts
       wich are included by the buildpart.  */
    public InterConnectBuildparts() {
        for (var sub = 0; sub < this.m_buildParts.length; sub++) {
            var bset = new BuildPartSet()
            bset.InitiateFromMeta(this.m_buildParts[sub].GetSet().GetMeta())
            
            this.m_buildParts[sub].ResetIncludedBuildPartsArray()
            
            for (var obj = 0; obj < this.m_buildParts.length; obj++) {
                for (var i = 0; i < bset.GetBuildParts().length; i++) {
                    var freshSet = this.GetSelectionSetManager().GetLatestBuildPartVersion(bset.GetBuildParts()[i].id)

                    if (freshSet == null) {
                        continue
                    }

                    if (freshSet.GetId() == this.m_buildParts[obj].GetId()) {
                        this.m_buildParts[sub].InsertBuildPartReferencedAmount(this.m_buildParts[obj], bset.GetBuildParts()[i].amount)
                    }
                }
            }
        }

        // Next up we'll make sure that also the saved projects have their build part references properly set..
        for (var sub = 0; sub < this.m_projectSummaries.length; sub++) {
            var bset = new BuildPartSet()
            bset.InitiateFromMeta(this.m_projectSummaries[sub].GetSet().GetMeta())

            this.m_projectSummaries[sub].ResetIncludedBuildPartsArray()

            for (var obj = 0; obj < this.m_buildParts.length; obj++) {
                for (var i = 0; i < bset.GetBuildParts().length; i++) {
                    var freshSet = this.GetSelectionSetManager().GetLatestBuildPartVersion(bset.GetBuildParts()[i].id)

                    if (freshSet == null) {
                        continue
                    }

                    if (freshSet.GetId() == this.m_buildParts[obj].GetId()) {
                        this.m_projectSummaries[sub].InsertBuildPartReferencedAmount(this.m_buildParts[obj], bset.GetBuildParts()[i].amount)
                    }
                }
            }
        }
    }

    // Access : m_buildPartLoadingSet
    public SetBuildPartLoadingSet(set: SelectionSetGW) {
        this.m_buildPartLoadingSet = set
    }

    // Access : projectSummaryLoadingSet
    public SetProjectSummaryLoadingSet(set: SelectionSetGW) {
        this.m_projectSummaryLoadingSet = set
    }

    // GetBuildPartsSum returns the total price for all build parts within the quick report
    public GetBuildPartsSum() {
        var value = 0
        for (var i = 0; i < this.m_selectedBuildParts.length; i++) {
            value += this.m_selectedBuildParts[i].GetTotalSum(this.m_priceRadioPos, this.m_priceFlags)
        }

        return value
    }

    // Access : SelectionID
    public GetSelectionID() {
        return this.m_app.GetSelectionID()
    }

    // Access : SelectionID
    public SetSelectionID(newID: string) {
        this.m_app.setState({ selectionID: newID })
    }

    // Access : SelectedArticleItem
    public GetSelectedArticleItem() {
        return this.m_selectedArticleItem
    }

    // Access : SelectedArticleItem
    public SetSelectedArticleItem(artItem: ArticleItemGW) {
        this.m_selectedArticleItem = artItem
        this.m_selectedArticle = artItem.props.article
        this.m_app.state.selectionID = artItem.props.article.GetSelectionLabel()
    }

    // Access : selectedQRArticleItem
    public GetSelectedSelectionItem() {
        return this.m_selectedQRArticleItem
    }

    // Access : selectedQRArticleItem
    public SetSelectedSelectionItem(selectedItem: SelectionItemGW) {
        this.m_selectedQRArticleItem = selectedItem
        this.m_selectedArticle = selectedItem.props.article
        this.m_app.state.selectionID = selectedItem.props.article.GetSelectionLabel()
    }

    // Access : selectedBuildPartItem
    public GetSelectedBuildPartItem() {
        return this.m_selectedBuildPartItem
    }

    // Access : selectedBuildPartQRItem
    public GetSelectedQRBuildPartItem() {
        return this.m_selectedBuildPartQRItem
    }

    // Access : selectedProjectItem
    public GetSelectedProjectItem() {
        return this.m_selectedProjectItem
    }

    // REcieving mva for price calculations (edit constant above - by top of this file)
    public GetMva() {
        var mva: number = norwegianMVA
        return mva
    }

    // HandleItemClick is the general handler for all clicks within an article Item
    HandleItemClick(article: ArticleItemGW, clickPos: string) {
        var isSelectingANewItem = (this.m_app.IsArticleSelected(article.props.article.GetSelectionLabel()) == false)

        // Preserving some parameters for the second phase of the handling process
        this.m_itemToSelect = article
        this.m_itemToSelectClickPos = clickPos
        this.m_itemToSelectIsNew = isSelectingANewItem

        // Finishes the previous selection and processing its eventual pending input
        if (isSelectingANewItem == true) {
            // this.m_prevArticleOnSwitch = this.m_app.state.selectedArticle
            this.m_app.state.selectionID = article.props.article.GetSelectionLabel()
            this.EndOldSelection()
        }
        else {
            this.HandleItemClickPhaseTwo()
            return
        }

        setTimeout(this.HandleItemClickPhaseTwo.bind(this), 150)
    }

    // Phase two of HandleItemClick (a delayed phase is performed because some actions will kill their own thread)
    HandleItemClickPhaseTwo() {
        const clickPos = this.m_itemToSelectClickPos
        const article = this.m_itemToSelect
        const isSelectingANewItem = this.m_itemToSelectIsNew

        if (isSelectingANewItem == true) {
            const newID = article.props.article.GetSelectionLabel()
            
            if (this.m_itemToSelectIsNew == true) {
                this.ClearSelection()
                this.m_selectedArticleItem = article
                this.m_selectedArticle = article.props.article
                this.SetSelectionID(newID)
                this.ExpandGroupInTreeView(article.props.article.GetGroupID())
            }
            else {
                this.ExpandGroupInTreeView(article.props.article.GetGroupID())
                this.ClearSelection()
                this.m_selectedArticleItem = article
                this.m_selectedArticle = article.props.article
            }
        }

        if (clickPos == "input") {
            this.SetInputItemArt(article)

            var extData = article.GetArticle().GetIfHasExtData()

            if (extData == null) {
                if (this.m_extDataForSelectedItem != null) {
                    if (this.m_extDataForSelectedItem.GetSelectionLabel() == article.GetArticle().GetSelectionLabel()) {
                        extData = this.m_extDataForSelectedItem
                    }
                }
            }

            if (extData != null) {
                extData.SetActivePakkeIndex(extData.GetValgtPakkeIndex())
            }
        }

        if (clickPos == "minus") {
            this.m_inputItemArticleView = null
            var decrement = article.props.article.GetAmount() % 1

            if (decrement == 0) {
                decrement = 1
            }

            article.props.article.AddAmount(-decrement)

            this.RedrawTotalSum()

            if (this.m_app.state.ExtInfoUp == true) {
                this.m_extInfoView.ResetControlInsertion()
            }

            var extData = article.GetArticle().GetIfHasExtData()

            if (extData == null) {
                if (this.m_extDataForSelectedItem != null) {
                    if (this.m_extDataForSelectedItem.GetSelectionLabel() == article.GetArticle().GetSelectionLabel()) {
                        extData = this.m_extDataForSelectedItem
                    }
                }
            }

            if (extData != null) {
                extData.SetActivePakkeIndex(extData.GetValgtPakkeIndex())
            }
        }

        if (clickPos == "plus") {
            this.m_inputItemArticleView = null

            var increment = 1 - article.props.article.GetAmount() % 1
            
            article.props.article.AddAmount(increment)

            if (article.props.article.GetIfHasExtData() == null) {
                this.InsertSelectArticle(article.props.article)
            }

            this.RedrawTotalSum()

            if (this.m_app.state.ExtInfoUp == true) {
                this.m_extInfoView.ResetControlInsertion()
            }

            var extData = article.GetArticle().GetIfHasExtData()

            if (extData == null) {
                if (this.m_extDataForSelectedItem != null) {
                    if (this.m_extDataForSelectedItem.GetSelectionLabel() == article.GetArticle().GetSelectionLabel()) {
                        extData = this.m_extDataForSelectedItem
                    }
                }
            }

            if (extData != null) {
                extData.SetActivePakkeIndex(extData.GetValgtPakkeIndex())
            }
        }

        if (clickPos == "favorites") {
            this.UpdateFavorites()

            if (this.FavoritesFind(article.props.article.GetSelectionLabel())) {
                if (this.m_app.state.DisplayMode == this.m_app.DMode.Favorites && (this.WillAskDeleteFavoritesQuestion() == true)) {
                    this.SetFavoritesDeletionArticle(article.props.article)
                    this.m_app.setState({
                        DisplayMode: this.m_app.DMode.FavoritesDelete
                    })
                }
                else {
                    this.FavoritesRemove(article.props.article.GetSelectionLabel())
                }
            }
            else {
                this.FavoritesInsert(article.props.article)
            }
        }

        if (clickPos == "none") {
            if (isSelectingANewItem == false) {
                this.GetApp().ToggleExtInfo()
            }
            else {
                this.SetInputItemArt(article)
            }
        }

        if (isSelectingANewItem == true) {
            this.m_articleView.forceUpdate()
        }
        else {
            article.forceUpdate()
        }
    }

    // Handler for clicks on build part items (currently disabled, still so?)
    HandleBuildPartItemClick(bp: BuildPartItem, clickPos: string) {
        var isSelectingANewItem = (this.GetSelectionID() == (bp.props.buildPart.GetId()) == false)

        // Preserving some parameters for the second phase of the handling process
        this.m_itemToSelectBP = bp
        this.m_itemToSelectClickPos = clickPos
        this.m_itemToSelectIsNew = isSelectingANewItem

        // Finishes the previous selection and processing its eventual pending input
        if (isSelectingANewItem == true) {
            // this.m_prevBuildPartOnSwitch = this.m_app.state.selectedBuildPart
            this.m_app.state.selectionID = bp.props.buildPart.GetId()
            this.EndOldSelection()
        }
        else {
            this.HandleBPItemClickPhaseTwo()
            return
        }

        setTimeout(this.HandleBPItemClickPhaseTwo.bind(this), 150)
    }

    // Phase two of HandleItemClick (a delayed phase is performed because some actions will kill their own thread)
    HandleBPItemClickPhaseTwo() {
        const clickPos = this.m_itemToSelectClickPos
        const bp = this.m_itemToSelectBP
        const isSelectingANewItem = this.m_itemToSelectIsNew

        if (isSelectingANewItem == true) {
            const newID = bp.props.buildPart.GetId()

            if (this.m_itemToSelectIsNew == true) {
                this.ClearSelection()
                this.m_selectedBuildPartItem = bp
                this.SetSelectionID(newID)
                this.UpdateBuildPartView()
            }
            else {
                this.ClearSelection()
                this.m_selectedBuildPartItem = bp
            }
        }

        if (clickPos == "input") {
            this.SetInputItemBP(bp)
        }

        if (clickPos == "minus") {
            this.m_inputItemBuildPartView = null

            var decrement = bp.props.buildPart.GetAmount() % 1

            if (decrement == 0) {
                decrement = 1
            }

            bp.props.buildPart.AddAmount(-decrement)
            this.RedrawTotalSum()
            bp.forceUpdate()
        }

        if (clickPos == "plus") {
            this.m_inputItemBuildPartView = null
            bp.props.buildPart.AddAmount(1 - (bp.props.buildPart.GetAmount() % 1))

            if (bp.props.buildPart.IsExtended() == false) {
                this.AddSelectBuildPart(bp.props.buildPart)
            }

            this.RedrawTotalSum()

            bp.forceUpdate()
            this.UpdateQReportTab()
        }
        
        if (clickPos == "none") {
            if (isSelectingANewItem == false) {
                this.GetApp().ToggleExtInfo()
            }
            else {
                this.SetInputItemBP(bp)
            }
        }

        if (isSelectingANewItem == true) {
            this.UpdateBuildPartView()
        }
        else {
            bp.forceUpdate()
        }
    }

    // Handler for item clicks within the build part view
    HandleBuildPartQRItemClick(bp: BuildPartQRItem, clickPos: string) {
        var isSelectingANewItem = this.GetSelectionID() != bp.props.buildPart.GetId()

        // Preserving some parameters for the second phase of the handling process
        this.m_itemToSelectBPQR = bp
        this.m_itemToSelectClickPos = clickPos
        this.m_itemToSelectIsNew = isSelectingANewItem

        // Finishes the previous selection and processing its eventual pending input
        if (isSelectingANewItem == true) {
            // this.m_prevBuildPartOnSwitch = this.m_app.state.selectedBuildPart
            this.m_app.state.selectionID = bp.props.buildPart.GetId()
            this.EndOldSelection()
        }
        else {
            this.HandleBPQRItemClickPhaseTwo()
            return
        }
        
        setTimeout(this.HandleBPQRItemClickPhaseTwo.bind(this), 150)
    }

    // Phase two of HandleItemClick (a delayed phase is performed because some actions will kill their own thread)
    HandleBPQRItemClickPhaseTwo() {

        const clickPos = this.m_itemToSelectClickPos
        const bp = this.m_itemToSelectBPQR
        const isSelectingANewItem = this.m_itemToSelectIsNew

        if (isSelectingANewItem == true) {
            const newID = bp.props.buildPart.GetId()
            
            if (this.m_itemToSelectIsNew == true) {
                this.ClearSelection()
                this.m_selectedBuildPartQRItem = bp
                this.SetSelectionID(newID)
                this.m_selectionView.forceUpdate()
            }
            else {
                this.ClearSelection()
                this.m_selectedBuildPartQRItem = bp
            }
        }

        if (clickPos == "input") {
            this.SetInputItemBPQR(bp)
        }

        if (clickPos == "minus") {
            this.m_inputItemBuildPartQRView = null

            var decrement = bp.props.buildPart.GetAmount() % 1

            if (decrement == 0) {
                decrement = 1
            }

            bp.props.buildPart.AddAmount(-decrement)
            this.RedrawTotalSum()
            bp.forceUpdate()
        }

        if (clickPos == "plus") {
            this.m_inputItemBuildPartQRView = null
            bp.props.buildPart.AddAmount(1 - (bp.props.buildPart.GetAmount() % 1))
            this.RedrawTotalSum()
            bp.forceUpdate()
        }
        
        if (clickPos == "cross") {
            bp.props.buildPart.SetAmount(0)
            var newContents = new Array<BuildPartGW>()

            for (var i = 0; i < this.m_selectedBuildParts.length; i++) {
                if (this.m_selectedBuildParts[i].GetId() != bp.props.buildPart.GetId()) {
                    newContents.push(this.m_selectedBuildParts[i])
                }
            }

            this.m_selectedBuildParts = newContents
            this.m_app.forceUpdate()
        }

        if (clickPos == "none") {
            if (isSelectingANewItem == false) {
                this.GetApp().ToggleExtInfo()
            }
            else {
                this.SetInputItemBPQR(bp)
            }
        }

        if (isSelectingANewItem == true) {
            this.UpdateBuildPartView()
        }
        else {
            bp.forceUpdate()
        }
    }

    // HandleQRItemClick is the general handler for all clicks within a selection Item
    HandleQRItemClick(article: SelectionItemGW, clickPos: string) {
        var isSelectingANewItem = (this.m_app.IsArticleSelected(article.props.article.GetSelectionLabel()) == false)

        // Preserving some parameters for the second phase of the handling process
        this.m_QRitemToSelect = article
        this.m_itemToSelectClickPos = clickPos
        this.m_itemToSelectIsNew = isSelectingANewItem

        if (isSelectingANewItem == true) {
            // this.m_prevArticleOnSwitch = this.m_app.state.selectedArticle
            this.m_app.state.selectionID = article.props.article.GetSelectionLabel()
            this.EndOldSelection()
        }
        else {
            this.HandleQRItemClickPhaseTwo()
            return
        }

        setTimeout(this.HandleQRItemClickPhaseTwo.bind(this), 150)
    }
    
    // A precise update of the extended info view, if it is up
    UpdateExtInfoView() {
        if (this.m_extInfoView == null) {
            return
        }

        if (this.m_app.state.ExtInfoUp == false) {
            return
        }

        var extInfoValid = false

        if (this.GetApp().state.DisplayMode == this.GetApp().DMode.Favorites) {
            extInfoValid = true
        }

        if (this.GetApp().state.DisplayMode == this.GetApp().DMode.SearchResult) {
            extInfoValid = true
        }

        if (this.GetApp().state.DisplayMode == this.GetApp().DMode.NobbGroup) {
            extInfoValid = true
        }

        if (this.GetApp().state.DisplayMode == this.GetApp().DMode.QReport) {
            if (this.GetSelectedSelectionItem() != null) {
                extInfoValid = true
            }
        }

        if (extInfoValid == true) {
            this.m_extInfoView.forceUpdate()
        }
    }

    // Phase two of HandleQRItemClick (a delayed phase is performed because some actions will kill their own thread)
    HandleQRItemClickPhaseTwo() {
        const clickPos = this.m_itemToSelectClickPos
        const article = this.m_QRitemToSelect
        const isSelectingANewItem = this.m_itemToSelectIsNew

        if (isSelectingANewItem == true) {
            const newID = article.props.article.GetSelectionLabel()
            
            if (this.m_itemToSelectIsNew == true) {
                this.ClearSelection()
                this.m_selectedQRArticleItem = article
                this.m_selectedArticle = article.props.article
                this.SetSelectionID(newID)
                this.ExpandGroupInTreeView(article.props.article.GetGroupID())
            }
            else {
                this.ExpandGroupInTreeView(article.props.article.GetGroupID())
                this.ClearSelection()
                this.m_selectedQRArticleItem = article
                this.m_selectedArticle = article.props.article
            }
        }

        if (clickPos == "input") {
            this.SetInputItemSel(article)
        }

        if (clickPos == "cross") {
            article.GetArticle().SetAmount(0)
            article.GetArticle().SetExtData(null)
            this.RemoveSelectArticle(article.GetArticle().GetSelectionLabel())
            this.m_app.forceUpdate()
        }

        if (clickPos == "minus") {
            this.m_inputItemSelectionView = null
            var decrement = article.props.article.GetAmount() % 1

            if (decrement == 0) {
                decrement = 1
            }

            article.props.article.AddAmount(-decrement)
            this.RedrawTotalSum()
            article.forceUpdate()
            if (this.m_app.state.ExtInfoUp == true) {
                this.m_extInfoView.ResetControlInsertion()
            }
        }

        if (clickPos == "plus") {
            this.m_inputItemSelectionView = null
            article.props.article.AddAmount(1 - (article.props.article.GetAmount() % 1))
            this.RedrawTotalSum()
            article.forceUpdate()
            if (this.m_app.state.ExtInfoUp == true) {
                this.m_extInfoView.ResetControlInsertion()
            }
        }
        
        if (clickPos == "favorites") {
            if (this.FavoritesFind(article.props.article.GetSelectionLabel())) {
                this.FavoritesRemove(article.props.article.GetSelectionLabel())
            }
            else {
                this.FavoritesInsert(article.props.article)
            }
        }

        if (clickPos == "none") {
            if (isSelectingANewItem == false) {
                this.GetApp().ToggleExtInfo()
            }
            else {
                this.SetInputItemSel(article)
            }
        }

        if (isSelectingANewItem == true) {
            this.m_selectionView.forceUpdate()
        }
        else {
            article.forceUpdate()
        }
    }

    // This will trigger a number of delayed methods that will get their threads killed in the end, but will also do some good before that happens.
    EndOldSelection() {
        var previousImputItem = this.m_inputItemArticleView
        var previousQRImputItem = this.m_inputItemSelectionView
        var previousBuildPartItem = this.m_inputItemBuildPartView
        var previousQRBuildPartItem = this.m_inputItemBuildPartQRView
        setTimeout(this.FinishOngoingInput.bind(this), 50)

        if (previousImputItem != null) {
            setTimeout(previousImputItem.forceUpdate.bind(previousImputItem), 100)
        }

        if (previousQRImputItem != null) {
            setTimeout(previousQRImputItem.forceUpdate.bind(previousQRImputItem), 100)
        }

        if (previousBuildPartItem != null) {
            setTimeout(previousBuildPartItem.forceUpdate.bind(previousBuildPartItem), 100)
        }

        if (previousQRBuildPartItem != null) {
            setTimeout(previousQRBuildPartItem.forceUpdate.bind(previousQRBuildPartItem), 100)
        }
    }

    /* This will interrupt any ongoing inputs, like typing in an amount into an article, but it will first cause it to take effect
       in its present state (if it is a valid input).  */
    FinishOngoingInput() {
        if (this.m_inputItemArticleView != null) {
            const handle = this.m_inputItemArticleView
            this.m_inputItemArticleView = null
            handle.state.mouseState = "out"
            handle.m_choise.state.hovering = false
            handle.m_choise.state.minusPressed = false
            handle.m_choise.state.plusPressed = false
            handle.m_choise.processInput() // This will kill the thread
        }

        if (this.m_inputItemSelectionView != null) {
            const handle2 = this.m_inputItemSelectionView
            this.m_inputItemSelectionView = null
            handle2.state.mouseState = "out"
            handle2.m_choise.state.hovering = false
            handle2.m_choise.state.minusPressed = false
            handle2.m_choise.state.plusPressed = false
            handle2.m_choise.processInput() // This will kill the thread
        }

        if (this.m_inputItemBuildPartView != null) {
            const handle3 = this.m_inputItemBuildPartView
            this.m_inputItemBuildPartView = null
            handle3.state.mouseState = "out"
            handle3.m_choiseCtrl.state.hovering = false
            handle3.m_choiseCtrl.state.minusPressed = false
            handle3.m_choiseCtrl.state.plusPressed = false
            handle3.m_choiseCtrl.processInput() // This will kill the thread
        }

        if (this.m_inputItemBuildPartQRView != null) {
            const handle4 = this.m_inputItemBuildPartQRView
            this.m_inputItemBuildPartQRView = null
            handle4.state.mouseState = "out"
            handle4.m_choiseCtrl.state.hovering = false
            handle4.m_choiseCtrl.state.minusPressed = false
            handle4.m_choiseCtrl.state.plusPressed = false
            handle4.m_choiseCtrl.processInput() // This will kill the thread
        }
        
        if (this.m_app.state.ExtInfoUp == true) {
            if (this.GetExtInfoView()) {
                this.m_inputUnitExtInfo = -1
            }
        }
    }

    // Nullifying all types of item selections
    public ClearSelection() {
        this.m_selectedArticleItem = null
        this.m_selectedQRArticleItem = null
        this.m_selectedBuildPartItem = null
        this.m_selectedQRBuildPartItem = null
        this.m_selectedArticle = null
        this.m_searchInFocus = false
    }

    // Access to the latest article which was selected
    public GetPresentlySelectedArticle() {
        return this.m_selectedArticle
    }

    // Update call for the build part view if it is up and active and should be shown
    public UpdateBuildPartView() {
        if (this.m_buildPartView != null) {
            if (this.GetApp().state.DisplayMode != this.GetApp().DMode.byggdelLoad) {
                return
            }

            this.m_buildPartView.forceUpdate()
        }
    }

    /* ConvertArticleAmounts translates amounts by custom choises of supplier or/and package types, into
     * the primary default option which is always orderable through the pricebook. The conversion takes
     * into account the calculated amount of that given package type into the corresponding amount of
     * the default package for the given article. All selected articles will be affected by this routine. */
    public ConvertArticleAmounts() {
        for (var sa = 0; sa < this.m_selectedArticles.length; sa++) {
            const ext = this.m_selectedArticles[sa].GetIfHasExtData()
            
            // Scanning through all supplier and package settings for chosen amounts..
            if (ext != null) {
                var realAmount = 0

                ext.DirectSubChoise(this.m_selectedArticles[sa])
                const defaultLev = ext.GetValgtLeverantorIndex()
                const defaultPak = ext.GetValgtPakkeIndex()

                for (var le = 0; le < ext.GetLeverandorer().length; le++) {
                    for (var pa = 0; pa < ext.GetLeverandorer()[le].pakninger.length; pa++) {
                        var amt = ext.GetLeverandorer()[le].pakninger[pa].amount
                        if (amt > 0) {
                            realAmount += amt * ext.GetLeverandorer()[le].pakninger[pa].kalkulertInnhold
                            ext.GetLeverandorer()[le].pakninger[pa].amount = 0
                        }
                    }
                }

                // Nå siden totalen av virkelig mengde er kalkulert skal den omsettes i default enheter
                const divideValue = ext.GetLeverandorer()[defaultLev].pakninger[defaultPak].kalkulertInnhold
                var newAmount = realAmount / divideValue

                // Rounding up:
                var excess = newAmount % 1
                if (excess != 0) {
                    newAmount -= excess
                }

                newAmount++

                ext.GetLeverandorer()[defaultLev].pakninger[defaultPak].amount = newAmount
            }
        }
    }

    // Resets what value and into what numerical control the user last time did enter something, which is used
    // for selecting what package type should be displayed data about and what value should be translated into
    // corresponding amounts for the other package types.
    public ResetExtInfoViewInputTrackings() {

        if (this.GetApp().state.ExtInfoUp == false) {
            return
        }

        var validMode = false

        if (this.GetApp().state.DisplayMode == this.GetApp().DMode.Favorites) {
            validMode = true
        }

        if (this.GetApp().state.DisplayMode == this.GetApp().DMode.NobbGroup) {
            validMode = true
        }

        if (this.GetApp().state.DisplayMode == this.GetApp().DMode.SearchResult) {
            validMode = true
        }

        if (this.GetApp().state.DisplayMode == this.GetApp().DMode.QReport) {
            validMode = true
        }

        if (validMode == true) {
            const eiw = this.GetExtInfoView()

            if ((eiw == null) || (eiw == undefined)) {
                return
            }

            eiw.ResetControlInsertion()
        }
    }

    /* Searching for custom set amounts within artices and enlists occurances of those non default user choises */
    public ConvertArticleAmountOccuranceCollect(quickCheck: boolean) { // if quickCheck is true, the search will return once the first occurance is found (for use as boolean)
        var result = new Array<string>()

        for (var sa = 0; sa < this.m_selectedArticles.length; sa++) {
            const ext = this.m_selectedArticles[sa].GetIfHasExtData()

            // Scanning through all supplier and package settings for chosen amounts..
            if (ext != null) {
                ext.DirectSubChoise(this.m_selectedArticles[sa])
                const defaultLev = ext.GetValgtLeverantorIndex()
                const defaultPak = ext.GetValgtPakkeIndex()

                for (var le = 0; le < ext.GetLeverandorer().length; le++) {
                    for (var pa = 0; pa < ext.GetLeverandorer()[le].pakninger.length; pa++) {
                        if ((le == defaultLev) && (pa == defaultPak)) {
                            continue
                        }

                        var amt = ext.GetLeverandorer()[le].pakninger[pa].amount
                        if (amt > 0) {
                            var str = amt.toString() + " " + ext.GetLeverandorer()[le].pakninger[pa].pakningsEnhet + " "
                            result.push(str + this.m_selectedArticles[sa].GetDescription() + " => " + ext.GetLeverandorer()[defaultLev].pakninger[defaultPak].pakningsEnhet)

                            if (quickCheck == true) {
                                return result
                            }
                        }
                    }
                }
            }
        }

        return result
    }

    // A switch to show the old version of Ext info view where suppliers and package types could be chosen and browsed for more information, this
    // switch is made available for debug reasons only, and active use of the older ext info view might cause some undesired effects in the updated system.
    public GetShouldDisplayOldExtInfoView() {

        if (oldExtInfoViewDisplayed) {
            return true
        }

        return false
    }

    // Referencing the newest instance of a mounted Summary View as it mounts
    public ConnectSummaryView(sw: SummaryView) {
        this.m_summaryView = sw
    }

    // If true, the buttons for crating and viewing buildparts will be shown
    public IsBuildpartsOn() {
        return this.m_userBuildPartOn == true
    }

    // Access : m_userBuildPartOn
    public SetBuildpartsOn(isOn : boolean) {
        this.m_userBuildPartOn = isOn
    }

    // Access : Summary View
    public GetSummaryView() {
        return this.m_summaryView
    }

    // Collects a referrence to the last mounted and rendered search input component
    public ConnectSearchField(sf: SearchField) {
        this.m_searchField = sf
    }

    // Collects a referrence to the last mounted and rendered search settings view (right hand side)
    public ConnectSearchSettingsView(ss: SearchSettingsView) {
        this.m_searchSettingsView = ss
    }

    // Access search settings view
    GetSearchSettingsView() {
        return this.m_searchSettingsView
    }
    
    // Access to the flag containing number which controls search results, 
    // specifically what parts of an article the search words should be tried upon
    public GetFilterFlags() {
        const flags = this.m_filterFlags
        return flags
    }

    // Access to the flag containing number which controls search results, 
    // specifically what parts of an article the search words should be tried upon
    public SetFilterFlags(flags : number) {
        this.m_filterFlags = flags
    }

    // This check will return true if the user has begun the login process, then (at startup)
    // the user will ge told the program is starting, otherwise be offered to log in with a link.
    public HasCalledForLogin() {
        if (this.m_hasCalledForLogin == true) {
            return true
        }
        else {
            return false
        }
    }
    
    // Access to the controling flag of HasCalledForLogin()  (see right above)
    public SetLoginCalled(loginCalledStatus: boolean) {
        this.m_hasCalledForLogin = loginCalledStatus
    }

    // Collects a referrence to the last mounted and rendered price settings view (right hand side)
    public ConnectPriceSettingsView(PSV: PriceSettingsView) {
        this.m_priceSettingsView = PSV
    }

    // Collects a referrence to the last mounted and rendered help view (right hand side)
    public ConnectHelpView(HV: HelpView) {
        this.m_helpView = HV
    }
    
    // Access to the flag containing number which controls how to gather the unit price value for
    // articles or build parts, such as if MVA should be added and discounts etc.
    public GetPriceFlags() {
        return this.m_priceFlags
    }

    // Access to the flag containing number which controls how to gather the unit price value for
    // articles or build parts, such as if MVA should be added and discounts etc.
    public SetPriceFlags(flags : number) {
        this.m_priceFlags = flags
    }

    // Access to a number which tells how to gather the unit price value for articles or build parts,
    // such as if it's the basic price, factory or recommended price that should be used.
    public GetPriceRadio() {
        return this.m_priceRadioPos
    }

    // Access to a number which tells how to gather the unit price value for articles or build parts,
    // such as if it's the basic price, factory or recommended price that should be used.
    public SetPriceRadio(pos: number) {
        this.m_priceRadioPos = pos
    }

    // Sets what pricebook ID to have selected by default
    public SetDefaultPriceBook(pricelistId: string) {
        this.m_defaultPriceBook = pricelistId
    }

    // Access to the array of available price books
    public GetPriceBooks() {
        return this.m_priceBooks
    }

    // Access to index for the radio button at summary view, telling if or not the values of articles should be rounded up to the smallest orderable package
    public GetSummaryModeRadioIndex() {
        return this.m_summaryModeRadioIndex
    }

    // Access to index for the radio button at summary view, telling if or not the values of articles should be rounded up to the smallest orderable package
    public SetSummaryModeRadioIndex(index : number) {
        this.m_summaryModeRadioIndex = index
    }

    // Access to the article being removed from favorites
    public GetFavoritesDeletionArticle() {
        return this.m_favoritesDeletionArticle
    }

    // Access to the article being removed from favorites
    public SetFavoritesDeletionArticle(art: ArticleGW) {
        this.m_favoritesDeletionArticle = art
    }

    // Sets a string telling what task the loading view was created for last time
    public SetLoadingTask(task: string) {
        this.m_whatIsBeingLoaded = task
    }

    // Returns a string telling what task the loading view was created for last time
    public GetLoadingTask() {
        return this.m_whatIsBeingLoaded
    }

    // setting the articles to be displayed in the article view
    public SetDisplayedArticles(articles: Array<ArticleGW>) {
        this.SortArticles(articles)
        this.m_displayedArticles = articles
    }

    // Debug info collection
    public DebugIncrementProjectLoadCalls() {
        this.m_countOfProjectLoadCalls++
    }

    // Debug info collection
    public DebugIncrementBuildPartLoadCalls() {
        this.m_countOfBuildPartLoadCalls++
    }

    // Debug info collection
    public DebugIncrementSelectionSetSaveCalls() {
        this.m_countOfSelectionSetSaveCalls++
    }

    // the default sorting method for articles (alphabetically by its description and description 2)
    SortArticles(articles: Array<ArticleGW>) {
        articles.sort(function (a, b) {
            var x = a.GetDescription()
            var y = b.GetDescription();
            if (x < y) { return -1; }
            if (x > y) { return 1; }
            x = a.GetDescription2();
            y = b.GetDescription2();
            if (x < y) { return -1; }
            if (x > y) { return 1; }
            return 0;
        })
    }

    // Returns the constant for local time hour difference (norwegian time equals 2)
    public GetLocalTimeDifference() {
        return localTimeDifference
    }

    // Sorts the selected articles (after any change was made to it)
    public SortSelectedArticles() {
        
        var selection = this.m_selectedArticles

        if (!selection) {
            return
        }

        const core = this

        if (core) {
            selection.sort(function (a, b) {
                var x = ""
                var y = ""
                const pbX = core.GetPriceBookByID(a.GetPriceBook())
                const pbY = core.GetPriceBookByID(b.GetPriceBook())

                if (pbX) {
                    x = pbX.GetTitle().toLowerCase()
                }

                if (pbY) {
                    y = pbY.GetTitle().toLowerCase()
                }
                
                if (x < y) { return -1; }
                if (x > y) { return 1; }
                var e = a.GetIdentifier()
                var f = b.GetIdentifier();
                if (e < f) { return -1; }
                if (e > f) { return 1; }
                return 0;
            })
        }
    }
    
    // Sorts favorites (after any change was made to it)
    public SortFavorites() {
        var selection = this.m_favorites

        if (!selection) {
            return
        }

        selection.sort(function (a, b) {
            var x = a.GetIdentifier()
            var y = b.GetIdentifier();
            if (x < y) { return -1; }
            if (x > y) { return 1; }
            return 0;
        })
    }

    public WillAskDeleteFavoritesQuestion() {
        return this.m_willAskDeleteFavoritesQuestion
    }

    public ToggleAskingDeleteFavoritesQuestion() {
        this.m_willAskDeleteFavoritesQuestion = ! this.m_willAskDeleteFavoritesQuestion
    }

    // 
    public SetInputItemExtInfoUnit(index: number) {
        this.m_inputUnitExtInfo = index
        this.ClearInput(5)
        this.UpdateExtInfoView()
        this.UpdateSelectedItem()
    }

    public GetLastExtInfoInputIndex() {
        return this.m_lastWrittenInputIndex
    }

    public GetLastExtInfoInputValue() {
        return this.m_lastWrittenInputValue
    }

    public GetLastExtInfoInputArticleID() {
        return this.m_lastWrittenInputArticleID
    }

    public SetLastExtInfoInput(index: number, newValue: string | null, prevID:string | null) {
        this.m_lastWrittenInputIndex = index
        this.m_lastWrittenInputValue = newValue
        this.m_lastWrittenInputArticleID = prevID
    }

    public ClearLastExtInfoInput() {
        this.m_lastWrittenInputIndex = -1
        this.m_lastWrittenInputValue = null
        this.m_lastWrittenInputArticleID = null
    }

    public BeginNewSearch() {
        this.m_latestSearchCall++
        return this.m_latestSearchCall
    }

    public IsLatestSearch(callIndex: number) {
        return this.m_latestSearchCall == callIndex
    }

    public GetSearchCallIndex() {
        return this.m_latestSearchCall
    }
    
    // References to renderable parts of the page, articles data, central application data
    private m_QReportTitle: QReportTitleGW
    private m_totalDisplay: TotalDisplayGW // Shows the total prize of chosen wares in the topright corner
    private m_treeView: TreeViewGW // Reference to the tree view (left hand side)
    private m_articleView: ArticleViewGW // Reference to the article  view
    private m_selectionView: SelectionViewGW // Reference to the selection (quickReport) view
    private m_app: App // Reference to the entire page
    private m_selectedArticles: Array<ArticleGW> // the current selection of wares by user
    private m_displayedArticles: Array<ArticleGW> // The articles to be shown within the ArticlesView
    private m_renderedArticleGroup: Array<ArticleGW> // The group of articles that was last rendered
    private m_favorites: Array<ArticleGW> // The list of articles that has been set as favorites
    private m_inputItemArticleView: ArticleItemGW | null // Reference to the eventual aricle item that was currently being typed into
    private m_inputItemSelectionView: SelectionItemGW | null // Reference to the eventual selection item that was currently being typed into
    private m_inputItemBuildPartView: BuildPartItem | null //  Reference to the eventual build part item that was currently being typed into
    private m_inputItemBuildPartQRView: BuildPartQRItem | null // A reference to the build part item in selection view that is currently being typed into
    private m_lastSelectedNobbGroup: string // Keeps track of what nobb group was latest selected, good when making searches within the selected nobb group
    private m_previousItem: ArticleItemGW // Marks a previously selected/focused item to be deselected during unsensitive (delayed) threads
    private m_itemToSelect: ArticleItemGW // Preserved parameter for a delayed selection procedure (HandleItemClickPhaseTwo)
    private m_previousQRItem: SelectionItemGW // Marks a previously selected/focused item to be deselected during unsensitive (delayed) threads
    private m_QRitemToSelect: SelectionItemGW // Preserved parameter for a delayed selection procedure (HandleItemClickPhaseTwo)
    private m_itemToSelectClickPos: string // Preserved parameter for a delayed selection procedure (HandleItemClickPhaseTwo)
    private m_itemToSelectIsNew: boolean // Preserved parameter for a delayed selection procedure (HandleItemClickPhaseTwo)
    private m_previousItemBP: BuildPartItem // Marks a previously selected/focused build part item to be deselected during unsensitive (delayed) threads
    private m_itemToSelectBP: BuildPartItem // Preserved parameter for a delayed build part  selection procedure (HandleBPItemClickPhaseTwo)
    private m_previousItemBPQR: BuildPartQRItem // Marks a previously selected/focused build part item to be deselected during unsensitive (delayed) threads
    private m_itemToSelectBPQR: BuildPartQRItem // Preserved parameter for a delayed build part  selection procedure (HandleBPItemClickPhaseTwo)
    private m_prevBuildPartOnSwitch: BuildPartGW // In the case where a selection of a new buildPart is made, this handle keeps track of which was the previous one
    private m_functionalKeyPressed: string // Preserved parameter for a delayed selection procedure (HandleItemClickPhaseTwo)
    private m_ArticleItemFirst: ArticleItemGW // The first item will update this handle whenever it renders. It is the first item on the current page of results
    private m_ArticleItemLast: ArticleItemGW // The last item will update this handle whenever it renders. It is the last item on the current page of results
    private m_selectedArticleItem: ArticleItemGW | null // The selected item will update this handle whenever it renders.
    private m_selectedQRArticleItem: SelectionItemGW | null // The selected item will update this handle whenever it renders.
    private m_selectedBuildPartItem: BuildPartItem | null // The selected item will update this handle whenever it renders.
    private m_selectedQRBuildPartItem: BuildPartQRItem | null // The selected item will update this handle whenever it renders.
    private m_selectedArticle: ArticleGW | null // This handle refers to the last selected article when needed for Extended info handling
    private m_arrowKeyInputFlag: string | null // When using arrow down or up, the next item to recieve focus will do so on its render if it owns this article/buildPart ID and is selected
    private m_loginName: String | null // The name to be displayed as who is logged in (default is a temporary name, to be replaced when a login app has been properly applied.)
    private m_loginCompany: String | null // The company name of the person to be displayed as logged in (default is a temporary name, to be replaced when a login app has been properly applied.)
    private m_extDataForSelectedItem: ExtDataGW | null // This holds a cashed data extension for the article currently being selected
    private m_extInfoView: ExtInfoViewGW // Handle to the extended info view for article items
    private m_prevArticleOnSwitch: ArticleGW // In the case where a selection of a new article is made, this handle keeps track of which was the previous one
    private m_selectionRollIn: number // This value tells if the last or first article item on a search result page should be selected due to a page change using the up/down keys
    private m_loadBarHandle: LoadBarGW // A handle to the data loading percentage bar (for updates)
    private m_incomingResultLength: number // A postponed state value for updating the view due to changes found at article view render (cannot cause immediate refresh due to an ongoing render)
    private m_groupBeingLoaded: ArticleGroupGW | null // This is a handle to the group being loaded from the server during the first minutes of runtime
    private m_dataLoadPercentage: number // Keeps track of the current loading status for the entire data set
    private m_dataLoadGroup: string // Keeps track of what group is currently being loaded
    private m_loadBarIsMounted: boolean // a flag telling if the load bar for search field is currently mounted and can recieve update requests (without producing any warnings)
    private m_groupDataResponse: string // This is basic data from the server to build up the nobb groups
    private m_qreportInsertionQueue: Array<ArticleGW> // A queue of articles to be inserted into the array of selected articles once its Extended data has been recived from back end
    private m_qreportInsertionPulseOn: boolean // A flag telling if more articles are to be inserted into the selection array and the insertion pulse is currently active
    private m_qreportInsertionPreviousIncomplete: boolean // A flag telling if the previous item to be inserted is yet not completed (the insertion pulse will then wait for it)
    private m_articleBeingInsertedAmongSelectedArticles: ArticleGW | null // handle to the article that has extended data presently being loaded for it, before being added among the selected articles
    private m_prislisterContents: string // Contents for the prislister dropdown menu
    private m_extDataLoadCompletionHandler: () => void // 
    private m_selectionSetManager: SelectionSetManagerGW // reference to the single selection set manager instance being in use
    private m_userIsWarnedAboutSupplierPackageChangeEffects: boolean //
    private m_projectView: ProjectView // Handle to the loading view for saved projects 
    private m_projectSaveView: ProjectSaveView // Handle to the project saving view 
    private m_buildPartView: BuildPartView // Handle to the loading view for saved projects 
    private m_buildPartSaveView: BuildPartSaveView // Handle to the project saving view 
    private m_priceBooks: Array<PriceBook> // Holds the pricebooks accessed from the prislister dropdown menu 
    private m_selectedPriceBookID: number | null // This is the ID number of the selected pricebook 
    private m_alphabeticalDisplay: boolean // true if the nobb herarchy (tree to the left) will instead be shown as subgroups in alphabetical order 
    private m_initStage: number // 0: not initiated, 1 loading data activated
    private m_favoritesUpdateStage: number // Countdown to the next favorites save 
    private m_buildParts: Array<BuildPartGW> // All available build parts for the account 
    private m_projectSummaries: Array<BuildPartGW> // All available projects, accessed in the form of buildparts
    private m_selectedBuildParts: Array<BuildPartGW> // the current selection of build parts by the user
    private m_buildPartLoadingSets: Array<SelectionSetGW> | null // the current selection of build parts by the user
    private m_projectLoadingSets: Array<SelectionSetGW> | null // the selection of projects saved by the user
    private m_buildPartLoadingSet: SelectionSetGW // Used as a parameter storage in the process of loading build parts from the server
    private m_projectSummaryLoadingSet: SelectionSetGW // Used as a parameter storage in the process of loading project summary build parts from the server
    private m_selectedBuildPartQRItem: BuildPartQRItem | null // Keeps track of what buildPart item is currently selected in the selection view
    private m_selectedProjectItem: ProjectItem // Keeps track of what project item is currently selected in the project view
    private m_QRTab: QReportTabGW // Handle to the quick report tab at the upper part of thescreen, for updates due to changes
    private m_userBuildPartOn: boolean // if true, then the buttons requred for accessing stored build parts or creating new ones are available, if false, these are hidden
    private m_userRole: number // 0: default   3:admin  (clearance for accessing purchased functionalities)
    private m_summaryView: SummaryView // Handle to the project saving view 
    private m_inputUnitExtInfo: number // the index of the alternate unit which has recieved focus for typing a value (-1 if none)
    private m_searchInFocus: boolean // A flag telling if the search input is or should be in focus
    private m_indexForUniqueKeys: number // When filling arrays of graphical elements, a unique key is required for each element (either it's used or not) this increasing index is fetched for that purpose.
    private m_printableArticleSource : Array<ArticleGW> | null // A temporary collection of articles created for printing purpose
    private m_printableArticlesReady: boolean // flag telling if the printable articles all have ext data loaded and are ready to be displayed
    private m_printableArticlesLoadIndex: number // iterating position for loading ext info data for articles out of buildparts (which are missing extInfo data) prior to printing/displaying the article selection
    private m_searchField: SearchField | null // A handle to the search field for updates
    private m_searchSettingsView: SearchSettingsView | null  // A handle to the search settings view
    private m_filterFlags: number // A number used bitwise for flags that's controlling the search filtering

    private m_priceFlags: number // A number used bitwise for flags controling the price settings
    private m_priceRadioPos: number // a number telling what position the radio buttons has got selected for the choise of price type

    private m_groupBeingLoadedIsFromPricebookID: number // Keeps track of what pricebook a group being loaded origins from, for debug info and article generation
    
    private m_countOfLoadingBeats: number // Keeps track of how many times the loading beat has been called
    private m_countOfGroupLoadInits: number // Keeps track of how many groups has been called for loading
    private m_countOfGroupLoadCompletes: number // Keeps track of how many groups has been successfully loaded
    private m_countOfFinishedHierarchialMidGroups: number // counts the times a L2 hierarchal group is completed
    private m_countOfFinishedHierarchialTopGroups: number // counts the times a L1 hierarchal group is completed
    private m_countOfFinishedHierarchialLowGroups: number // counts the times a L1 hierarchal group is completed
    private m_countOfPriceBookInitiations: number // Keeps track of how many pricebooks was initiated for loading
    private m_countOfPriceBookGenerations: number // Keeps track of how many pricebooks has begun its generation after recieving its data
    private m_countOfRequestedExtDataForLoadedArticles : number // Counter

    private m_countOfProjectLoadCalls: number // Keeps track of how many times a project was requested
    private m_countOfBuildPartLoadCalls: number // Keeps track of how many times a buildPart was requested
    private m_countOfSelectionSetSaveCalls: number // Keeps track of how many times a selection of articles was requested

    private m_allPriceBooksAlphabeticalGroups: Array<ArticleGroupGW> | undefined
    private m_allPriceBooksHierarchialGroups: Array<ArticleGroupGW> | undefined
    private m_allPriceBooksAlphabeticalGroupsHierarchial: Array<ArticleGroupGW> | undefined
    private m_allPriceBooksNobblessGroups: Array<ArticleGroupGW> | undefined
    private m_allPriceBookHierarchyNeedRefresh: boolean 
    private m_allPriceBookHierarchyIsValid: boolean // Says if current global hierarchy was built after all pricebooks were initiated

    private m_allPriceBooksFullData: Array<ArticleGW> // A complete array of all articles that has been downloaded and made available from the memory

    private m_beatStage: number // A looping increasing stage that goes from 0 up to 100, only for animation purposes. 

    private m_hasCalledForLogin : boolean // A flag telling if the user has clicked the login link or not
    
    private m_idServerUrl: string
    private m_serverUrl: string
    private m_priceSettingsView: PriceSettingsView | null
    private m_newProjectView: NewProjectView | null
    private m_helpView: HelpView | null

    private m_defaultPriceBook: string | undefined
    
    private m_loadingStatus: { // Statuses that controls the data download
        beatStarted: boolean,
        prioGrp?: ArticleGroupGW,
        loadInProgress: boolean,
        priceBookGenerationInProgress: boolean,
        priceBookGenerationAtIndex: number
    } 

    private m_nobbUnspecifiedArticlesFromAllPriceBooks: Array<ArticleGW>

    private m_summaryModeRadioIndex: number
    private m_loadingView: LoadingView | null
    private m_logView: LogView | null

    private m_whatIsBeingLoaded: string // Keeps track of what task the loading view was created for last time

    private m_selectedArticleQueuedToReciveExtData: ArticleGW | null // Background cashing of ext data for selected articles - handle to the selected article
    private m_articleIsQueuedToReciveExtData: boolean // Background cashing of ext data for selected articles - flag telling if a load of ext data is in progress or not
    
    private m_favoritesDeleteView: FavoritesDeleteView | null
    private m_favoritesDeletionArticle: ArticleGW | null

    private m_willAskDeleteFavoritesQuestion: boolean
    private m_locallyStoredGroupData: string
    private m_locallyStoredGroupDataIsUsed: boolean
    private m_loadingGroupUrl: string // Kept url for if local storage contains no data about the group being loaded
    private m_loadingGroupDate: string // Kept date string from the earlier locally stored group being loaded

    private m_defaultExtDataForArticlesMissingIt: ExtDataGW // Used instead of nobb info wherever loading of ext info was failed for an article.
    private m_handlerForAccountReload: (() => void) | null // handler for action to be taken right after an account reload

    private m_lastWrittenInputIndex: number
    private m_lastWrittenInputValue: string | null
    private m_lastWrittenInputArticleID: string | null

    private m_latestSearchCall: number // Identifies the latest started search call, in order to interrupt all earlier calls
    private m_searchCallChecks: number // Time counter until next search will begin after the user stopped typing more letters, 
                                       // and before an aftercheck is made to ensure the latest search is the content in the search field
    private m_activeSearches: Array<SearchCall> // Handlers to the searches that has started

    private m_recentSearchInput: string // kept sring for comparing towards new search inputs to notice differences and then perform a new search

    ClearPastSearches() {
        if (this.m_activeSearches.length > 0) {
            var freshArray = new Array<SearchCall>()

            for (var i = 0; i < this.m_activeSearches.length; i++) {
                if (this.m_activeSearches[i].IsActive() == true){
                    freshArray.push(this.m_activeSearches[i])
                }
            }

            this.m_activeSearches = freshArray
        }
    }
}

/* 
 Backend access punkter: 
 http://apibyggdata.azurewebsites.net/api/price: Lister tilgjengelige prisfiler for bruker, med ID og beskrivelse. 
 http://apibyggdata.azurewebsites.net/api/price/{id}: Lister alle tilgjengelige grupper, leverandører og nobbgrupper i en prisfil. 
 http://apibyggdata.azurewebsites.net/api/price/{id}/group/{gid}: Lister alle varer under angitt gruppe i en prisfil. 
 http://apibyggdata.azurewebsites.net/api/price/{id}/supplier/{sid}: Lister alle varer for angitt leverandør i en prisfil. 
 http://apibyggdata.azurewebsites.net/api/price/{id}/nobbgroup/{gid} : Lister alle varer under angitt nobb-gruppe i en prisfil. 
 http://apibyggdata.azurewebsites.net/api/price/nobb/{nobbnr}: Henter utvidet vareinfo for vare med angitt nobbnr. 
 
 For informasjon om alle feltene som er tilgjengelig i utvidet vareinfo, se NOBB-dokumentasjon på 
 http://dok.byggtjeneste.no/importeksport/#VareEksport%208.0_xsd.html 
 
 // var bufferLev = new Array<JSX.Element>()  (useful)
*/



// WEBPACK FOOTER //
// ./client/view/BDKalkCore.tsx